import "../../site/js/set-dist-public-path";
import { editLineupDirection } from './sidebar/editLineupDirection';
import { Fragment, Suspense, lazy, useState, useEffect, useRef, useCallback, useContext, useMemo, useReducer } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { GlobalProvider, GlobalContext } from "./header/GlobalContext.js";
import FetchAthletes from "./FetchAthletes.js";
const { createBoardAthleteRequestLanes } = require("./boardAthleteRequestLanes.js");
import FetchBoardBootstrap from "./FetchBoardBootstrap.js";
import { FetchTopPlaysStaticCache } from "./FetchTopPlaysStatic.js";
import reportBoardIssue from "./ReportBoardIssue.js";
import createBoardPerformanceTracker from "./BoardPerformance.js";
import {
  applyChangedSelectionLinesToBoard,
  isConfirmedUnavailableSelection,
} from "./selectionSyncReconciliation.js";
import FetchStats, { clearStatsCache, getCurrentShowOddsValue, getCurrentUserTierValue } from "./FetchStats.js";
import FetchStatsSelection from "./FetchStatsSelection.js";
import ClearSportCache from "./ClearSportCache.js";
import FetchFeedVersion from "./FetchFeedVersion.js";
import RefreshFeedIfStale from "./RefreshFeedIfStale.js";
import AthleteCard from "./AthleteCard.js";
import StatSelection from "./header/StatSelection.js";
import GamesSlider, { clearCachedGamesForSport } from "./header/GamesSlider.js";
import { getSportFamily } from "./sportFamilies.js";
import restFetchOptions, { getCurrentRestNonce } from "./restFetchOptions.js";
import {
  TEAMS_STAT_KEY,
  TEAM_LINEUP_FEATURE_ENABLED,
  TEAM_LINEUP_SPORTS,
  getTeamLineupFetchStats,
  isTeamsLineupSport,
  withTeamsStatOption,
} from "./teamLineupAvailability.js";
import { formatBoardStartTime, parseBoardStartDate } from "./timeDisplay.js";
import SportSelection from "./header/SportSelection.js";
import SearchInput from "./header/SearchInput.js";
import SearchAthletes from "./header/SearchAthletes.js";
import AthleteBoardSkeleton, {
  CollapsedSidebarShell,
  MobileStickyMenuShell,
  PromoOffersShell,
} from "./AthleteBoardSkeleton.js";
import { isFallbackHeadshotImage } from "./athleteHeadshotPlaceholder.js";
import { formatBoardStatLabel } from "./boardStatLabels.js";
import { triggerSelectionHaptic } from "./selectionHaptics.js";
import { validatePropCombinability } from "./sidebar/propCombinabilityValidation.js";
import { getLiveSameEventLineupPolicy } from "./sidebar/selectionStartState.js";
import {
  filterUnavailableSelections,
  getSelectionAvailabilityKey,
  getUnavailableSelectionSet,
  readSelectedAthletesFromStorage,
  setEntryAvailabilityState,
  writeSelectedAthletesToStorage,
} from "./entryAvailabilityState.js";
import {
  ArrowRightIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlayCircleIcon,
  ThemeModeIcon,
} from "../icons/heroIcons";
import {
  getLowLiquidityLineupLimitViolation,
  getLowLiquiditySelectionChangeViolation,
  getCardSelectionState,
  getSelectionStateFromToken,
  matchesSelectionFamily,
  matchesSelectionVariant,
  normalizeProjectedStatKey,
} from "./selectionState.js";
import toastr from "toastr";
import "toastr/toastr.scss";
import { trackAthleteSelected } from "./athleteAnalytics.js";
import {
  hasRecentDiscountedStatPromoUse,
  TAP_IN_PROMO_UNAVAILABLE_EVENT,
} from "./promoState.js";
import {
  WIN_100_PICK_COUNT,
  WIN_100_SELECTED_STORAGE_KEY,
  filterWin100CompatibleSelections,
  getWin100LineupViolation,
  isWin100ModeActive,
} from "./win100Mode.js";
const { isLiveGame, getEventAvailabilityKey, filterRejectedEvents, reconcileLiveSports } = require("./header/gameAvailability.js");
const {
  buildTopPlaysMetadataRecovery,
  filterAthletesForRequestedSport,
  isBoardRefreshCurrent,
  normalizeSportKey,
  settleBoardRequestWithin,
  shouldDeferBoardResumeRefresh,
} = require("./boardRequestGuards.js");
const {
  buildAthleteSelectionPreview,
  filterAthletesForRequestedStat,
  hasCompleteAthleteSelectionPreview,
} = require("./athleteSelectionPreview.js");
const {
  atomicBoardTransitionMatches,
  createAtomicBoardTransition,
} = require("./atomicBoardTransition.js");
const { createBoardDataSource, createRecentVerifiedBoardCache } = require("./BoardDataSource.js");
const { createBoardVisualCache, createDeferredBoardPersistence } = require("./boardVisualCache.js");
const {
  BOARD_NAVIGATION_PHASES,
  boardNavigationReducer,
  createInitialBoardNavigationState,
  selectBoardNavigationView,
} = require("./boardNavigationState.js");
const {
  boardBundleIsOutdated,
  getBoardBundlePathFromManifest,
} = require("./boardAssetVersion.js");
const { mixTopPlaysAthletes } = require("./topPlaysOrdering.js");
const RAPID_SELECTION_TOGGLE_WINDOW_MS = 800;
const getSelectionTapKey = ({ athleteId, statKey, promo, prop } = {}) => (
  [athleteId, statKey, promo, prop].map((value) => String(value ?? "")).join("|")
);
const shouldIgnoreRapidSelectionToggle = (lastTap, selection, now = Date.now()) => {
  const lastTimestamp = Number(lastTap?.timestamp || 0);
  const elapsed = Number(now) - lastTimestamp;

  return Boolean(
    lastTimestamp > 0 &&
    getSelectionTapKey(lastTap) === getSelectionTapKey(selection) &&
    elapsed >= 0 &&
    elapsed < RAPID_SELECTION_TOGGLE_WINDOW_MS
  );
};
// import sendAthleteSelectionToAPI from "./sendAthleteSelectionToAPI.js";
const PromoOffers = lazy(() => import(/* webpackChunkName: "sportV2-promo-offers" */ "./header/PromoOffers.js"));
const Sidebar = lazy(() => import(/* webpackChunkName: "sportV2-sidebar" */ "./sidebar/Sidebar.js"));
const HardModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/HardModal.js"));
const EasyModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/EasyModal.js"));
const PromoModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/PromoModal.js"));
const HowToPlayModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/HowToPlayModal.js"));
const H2HMatchModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/H2hMatchModal.js"));
const IconsExplainedModal = lazy(() => import(/* webpackChunkName: "sportV2-modals" */ "./modals/IconsExplainedModal.js"));
const SearchResultsList = lazy(() => import(/* webpackChunkName: "sportV2-search-results" */ "./SearchResultsList.js"));
const GeoLocate = lazy(() => import(/* webpackChunkName: "sportV2-geolocate" */ "./GeoLocate.js"));
const SuperPacks = lazy(() => import(/* webpackChunkName: "sportV2-super-packs" */ "./SuperPacks.js"));
let teamLineupModulePromise = null;
const loadTeamLineupModule = () => {
  if (!teamLineupModulePromise) {
    teamLineupModulePromise = import(
      /* webpackChunkName: "sportV2-team-lineups" */ "./TeamLineupBoard.js"
    ).catch((error) => {
      teamLineupModulePromise = null;
      throw error;
    });
  }

  return teamLineupModulePromise;
};
const TeamLineupBoard = lazy(() => loadTeamLineupModule());
const buildTeamLineupGroupsOnDemand = async (athletes, sport) => {
  const { buildTeamLineupGroups } = await loadTeamLineupModule();
  return buildTeamLineupGroups(athletes, sport);
};

const PromoOffersFallback = () => (
  <div className="athlete-board-skeleton athlete-board-skeleton--promo-fallback">
    <PromoOffersShell />
  </div>
);

const CollapsedSidebarFallback = () => (
  <div className="athlete-board-skeleton athlete-board-skeleton--sidebar-fallback">
    <CollapsedSidebarShell />
  </div>
);

const BoardInfoModalLauncher = ({ type }) => {
  const modalId = type ? `${type}-modal` : "";

  useEffect(() => {
    if (!modalId || typeof window === "undefined") {
      return undefined;
    }

    let attempts = 0;
    let cancelled = false;
    let retryId = null;
    const showWhenMounted = () => {
      if (cancelled) {
        return;
      }

      const modal = window.jQuery?.(`#${modalId}`);
      if (modal?.length) {
        modal.modal("show");
        return;
      }

      attempts += 1;
      if (attempts < 40) {
        retryId = window.setTimeout(showWhenMounted, 50);
      }
    };

    const timeoutId = window.setTimeout(showWhenMounted, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (retryId) {
        window.clearTimeout(retryId);
      }
    };
  }, [modalId]);

  if (type === "easy") return <EasyModal />;
  if (type === "hard") return <HardModal />;
  if (type === "promo") return <PromoModal />;
  if (type === "how-to-play") return <HowToPlayModal />;
  if (type === "icons-explained") return <IconsExplainedModal />;
  return null;
};

const DeferredSuperPacks = (props) => {
  const [shouldMount, setShouldMount] = useState(false);
  const mountSentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = mountSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver !== "function") {
      setShouldMount(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldMount(true);
        observer.disconnect();
      }
    }, { rootMargin: "300px 0px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`super-packs-mount-sentinel${shouldMount ? " super-packs-mount-sentinel--mounted" : ""}`}
      ref={mountSentinelRef}
    >
      {shouldMount ? (
        <Suspense fallback={null}>
          <SuperPacks {...props} />
        </Suspense>
      ) : null}
    </div>
  );
};

const EventModeBackIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const withEventColorAlpha = (value, alpha) => {
  const color = String(value || "").trim();
  const match = color.match(/^#([0-9a-f]{6})$/i);
  if (!match) {
    return "transparent";
  }

  const channels = match[1].match(/.{2}/g).map((channel) => parseInt(channel, 16));
  return `rgba(${channels.join(", ")}, ${alpha})`;
};

const urlParams = new URLSearchParams(window.location.search);
const h2hParam = urlParams.get('h2h');
const win100ModeActive = isWin100ModeActive(window.location.search);
const selectedStorageKey = win100ModeActive
  ? WIN_100_SELECTED_STORAGE_KEY
  : undefined;
const boardCacheBypassParam = urlParams.has("no-cache") || urlParams.get("clear") === "stats";
const loggedIn = document.body.classList.contains('logged-in');
let boardRegistrationModulePromise = null;
const loadBoardRegistration = () => {
  if (loggedIn) {
    return Promise.resolve();
  }

  if (!boardRegistrationModulePromise) {
    boardRegistrationModulePromise = import(
      /* webpackChunkName: "sportV2-register-on-demand" */ "../Register.js"
    ).catch((error) => {
      boardRegistrationModulePromise = null;
      throw error;
    });
  }

  return boardRegistrationModulePromise;
};
const timeoutNotifiedRefGlobal = { current: false }; // singleton to avoid duplicate timeout alerts
const REGISTRATION_FLOW_KEY = "ww_registration_started_at";
const REGISTRATION_FLOW_TTL_MS = 45 * 60 * 1000; // 45 minutes
const BOARD_BACKGROUND_AT_KEY = "ww_board_backgrounded_at";
const BOARD_BACKGROUND_HANDLED_KEY = "ww_board_background_handled_at";
const BOARD_RETURN_REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
const BOARD_RESUME_REFRESH_EVENT = "ww:board-resume-refresh";
const BOARD_BOOTSTRAP_SETTLED_EVENT = "ww:board-bootstrap-settled";
const BOARD_RESUME_REFRESH_RETRY_MS = 250;
const BOARD_ASSET_MANIFEST_URL = "/wp-content/themes/understrap-child/dist/react/manifest.json";
const BOARD_ASSET_REFRESH_PARAM = "ww_asset_refresh";
const MEDIAN_APP_RESUMED_EVENT = "ww:median-app-resumed";
const STALE_FEED_MAX_AGE_MS = 2 * 60 * 1000;
const STALE_FEED_REFRESH_COOLDOWN_MS = 90 * 1000;
const TEAM_LINEUP_POOL_CACHE_KEY = "ww_team_lineup_pool_cache_v2";
const TEAM_LINEUP_QUOTE_CACHE_KEY = "ww_team_lineup_quote_cache_v2";
const TEAM_LINEUP_POOL_CACHE_TTL_MS = 60 * 60 * 1000;
const initialBoardAthleteLimit = Number(window.__WW_INITIAL_BOARD_ATHLETE_LIMIT);
const INITIAL_BOARD_ATHLETE_LIMIT = Number.isFinite(initialBoardAthleteLimit) && initialBoardAthleteLimit > 0
  ? Math.floor(initialBoardAthleteLimit)
  : 15;
const BOARD_ATHLETE_HYDRATION_PAGE_SIZE = 50;
const BOARD_ATHLETE_HYDRATION_MAX_OFFSET = 500;
const EVENT_BOARD_INITIAL_ATHLETE_LIMIT = 16;
const EVENT_BOARD_HYDRATION_CHUNK_SIZE = 50;
const EVENT_BOARD_HYDRATION_MAX_OFFSET = 200;
const BOARD_ATHLETE_CACHE_TTL_MS = 4 * 60 * 60 * 1000;
const BOARD_ATHLETE_CACHE_MAX_ENTRIES = 160;
// v3/v5 intentionally invalidate legacy browser rows that could retain a
// market-swapped athlete across stat tabs even after the authoritative list
// had changed.
const PERSISTED_BOARD_CACHE_KEY = "ww_board_athlete_cache_v3";
const PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY = "ww_board_bootstrap_cache_v5";
const PERSISTED_BOARD_METADATA_CACHE_KEY = "ww_board_bootstrap_metadata_v1";
const RECENT_BOARD_LEAGUES_KEY = "ww_recent_board_leagues_v1";
const RECENT_BOARD_LEAGUES_MAX = 7;
const PERSISTED_BOARD_CACHE_TTL_MS = 15 * 60 * 1000;
const PERSISTED_BOARD_STALE_PREVIEW_TTL_MS = 4 * 60 * 60 * 1000;
const PERSISTED_BOARD_BOOTSTRAP_METADATA_TTL_MS = 24 * 60 * 60 * 1000;
const PERSISTED_BOARD_CACHE_MAX_ENTRIES = 48;
const BOARD_BOOTSTRAP_RECOVERY_TIMEOUT_MS = 10000;
const BOARD_BOOTSTRAP_RECOVERY_RETRY_DELAYS_MS = [0, 2000, 5000];
const BOARD_PREWARM_FOREGROUND_WAIT_MS = 1200;
const BOARD_ATHLETE_HYDRATION_DELAY_MS = 1200;
// Only near-viewport pagination gets the shorter settling window. Event-wide
// stat hydration keeps its existing delay and foreground requests retain priority.
const BOARD_VIEWPORT_HYDRATION_DELAY_MS = 300;
const BOARD_VISIBLE_LOADING_RECOVERY_MS = 15000;
const MAX_AUTO_SWITCH_EMPTY_STATS_PER_SPORT = 3;
const BOARD_REFRESH_HIGHLIGHT_MS = 1800;
const POPULAR_STAT_KEY = "projected_popular";
const TOP_PLAYS_SPORT = "topplays";
const isTopPlaysSport = (sportValue) => String(sportValue || "").toLowerCase() === TOP_PLAYS_SPORT;
const readRecentBoardLeagues = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  try {
    const leagues = JSON.parse(window.localStorage.getItem(RECENT_BOARD_LEAGUES_KEY) || "[]");
    return Array.isArray(leagues)
      ? [...new Set(leagues.map(normalizeSportKey).filter(Boolean))].slice(0, RECENT_BOARD_LEAGUES_MAX)
      : [];
  } catch (e) {
    return [];
  }
};
const TOP_PLAYS_POPULAR_ROW_LIMIT = 24;
const TOP_PLAYS_DRAG_MIN_VELOCITY = 0.035;
const TOP_PLAYS_DRAG_MOMENTUM_FRICTION = 0.92;
const TOP_PLAYS_EMPTY_RETRY_DELAYS_MS = [300, 900];
const INDIVIDUAL_EVENT_SELECTION_SPORTS = new Set([
  "bowling",
  "boxing",
  "celeb",
  "celebgolf",
  "darts",
  "f1",
  "fpa",
  "hotdog",
  "mma",
  "nascar",
  "olympics",
  "pga",
  "tennis",
  "tt",
  "ufc",
]);
const EMPTY_ATHLETE_STATE_GRACE_MS = 3000;
const MOBILE_STICKY_CHAT_GAP_PX = 12;
const TAWK_MOBILE_FRAME_GUTTER_PX = 16;
const TAWK_EXPANDED_MIN_HEIGHT_PX = 240;
const TAWK_OPEN_TARGET_TIMEOUT_MS = 2500;
const MOBILE_LINEUP_AUTO_REVIEW_COUNT = 6;
const MOBILE_STICKY_MAX_PICK_COUNT = 6;
const MOBILE_STICKY_MAX_VISIBLE_CIRCLES = 6;
const MOBILE_STICKY_PAYOUT_TIERS = {
  2: 3,
  3: 5,
  4: 10,
  5: 15,
  6: 25,
};
const STICKY_FOOTER_LIGHT_BACKGROUND = "rgba(255, 255, 255, 0.94)";
const STICKY_FOOTER_DARK_BACKGROUND = "rgba(13, 17, 23, 0.94)";
const STICKY_FOOTER_POTENTIAL_AVATAR_STYLE = {
  border: "2px dashed rgb(66 66 66)",
  boxShadow: "none",
  boxSizing: "border-box",
  color: "rgb(66 66 66)",
  fontSize: "0.68rem",
  fontWeight: 700,
};
let boardBackgroundedAtMemory = 0;
let boardBackgroundHandledAtMemory = 0;
const MOBILE_LINEUP_SHEET_QUERY = "(max-width: 767.98px)";
const INITIAL_HEADSHOT_DESKTOP_QUERY = "(min-width: 768px)";
const INITIAL_MOBILE_HEADSHOT_COUNT = 2;
const INITIAL_DESKTOP_HEADSHOT_COUNT = 5;
const TAWK_WIDGET_SELECTOR = [
  'iframe[src*="tawk"]',
  'iframe[title*="chat"]',
  'iframe[title*="Chat"]',
  'iframe[title*="widget"]',
  'iframe[title*="Widget"]',
  '[id*="tawk"]',
  '[id*="Tawk"]',
  '[class*="tawk"]',
  '[class*="Tawk"]',
].join(", ");
const TAWK_WINDOW_Z_INDEX = 2000000010;
const TAWK_LAUNCHER_Z_INDEX = 2000000000;
const TAWK_CHAT_OPEN_BODY_CLASS = "ww-tawk-chat-open";

const getIsDesktopViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(INITIAL_HEADSHOT_DESKTOP_QUERY).matches;
};

const getVisibleElementHeight = (element) => {
  if (!(element instanceof HTMLElement)) {
    return 0;
  }

  const computedStyle = window.getComputedStyle(element);
  if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
    return 0;
  }

  return Math.ceil(element.getBoundingClientRect().height);
};

const clearTawkLayoutStyles = (target) => {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  target.style.removeProperty("bottom");
  target.style.removeProperty("top");
  target.style.removeProperty("height");
  target.style.removeProperty("min-height");
  target.style.removeProperty("max-height");
};

const clearTawkTargetStyles = (target) => {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  clearTawkLayoutStyles(target);
  target.style.removeProperty("z-index");
};

const getTawkOffsetTargets = () => {
  if (typeof document === "undefined") {
    return [];
  }

  const targets = new Set();

  document.querySelectorAll(TAWK_WIDGET_SELECTOR).forEach((node) => {
    if (
      !(node instanceof HTMLElement) ||
      node === document.body ||
      node === document.documentElement
    ) {
      return;
    }

    targets.add(node);

    let current = node.parentElement;
    while (current instanceof HTMLElement && current !== document.body) {
      if (window.getComputedStyle(current).position === "fixed") {
        targets.add(current);
      }
      current = current.parentElement;
    }
  });

  return Array.from(targets);
};

const getTawkViewportHeight = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  const visualViewportHeight = window.visualViewport?.height;

  if (Number.isFinite(visualViewportHeight) && visualViewportHeight > 0) {
    return Math.ceil(visualViewportHeight);
  }

  return Math.ceil(
    window.innerHeight ||
    document.documentElement?.clientHeight ||
    0
  );
};

const getTawkConstrainedFrameHeight = (bottomOffset) => {
  const viewportHeight = getTawkViewportHeight();

  if (!viewportHeight) {
    return `calc(100dvh - ${Math.max(bottomOffset + TAWK_MOBILE_FRAME_GUTTER_PX, TAWK_MOBILE_FRAME_GUTTER_PX)}px)`;
  }

  return `${Math.max(TAWK_EXPANDED_MIN_HEIGHT_PX, viewportHeight - bottomOffset - TAWK_MOBILE_FRAME_GUTTER_PX)}px`;
};

const shouldConstrainTawkFrame = (target) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const rect = target.getBoundingClientRect();
  const viewportWidth =
    window.visualViewport?.width ||
    window.innerWidth ||
    document.documentElement?.clientWidth ||
    0;
  const minExpandedWidth = viewportWidth ? Math.min(280, viewportWidth * 0.6) : 280;
  const computedStyle = window.getComputedStyle(target);

  return (
    (target.tagName === "IFRAME" || computedStyle.position === "fixed") &&
    rect.width >= minExpandedWidth &&
    rect.height >= TAWK_EXPANDED_MIN_HEIGHT_PX
  );
};

const useBodyPortalTarget = () => {
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    setPortalTarget(document.body);
    return undefined;
  }, []);

  return portalTarget;
};

// Recreate the body-level sticky nodes once after a native resume so WebKit
// calculates their fixed position against the restored visual viewport.
const useStickyChromeMountKey = () => {
  const [mountKey, setMountKey] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let animationFrame = 0;

    const remountStickyChrome = () => {
      if (animationFrame) {
        return;
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        setMountKey((currentKey) => currentKey + 1);
      });
    };

    window.addEventListener(BOARD_RESUME_REFRESH_EVENT, remountStickyChrome);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      window.removeEventListener(BOARD_RESUME_REFRESH_EVENT, remountStickyChrome);
    };
  }, []);

  return mountKey;
};

const normalizeStickyPath = (path = "") => {
  if (!path) {
    return "/";
  }

  const normalizedPath = path.replace(/\/+$/, "");
  return normalizedPath || "/";
};

const StickyAccountIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const StickyInviteIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z"
    />
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z"
      clipRule="evenodd"
    />
  </svg>
);

const StickyLineupsIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M2.625 6.75a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0A.75.75 0 0 1 8.25 6h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75ZM2.625 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0ZM7.5 12a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12A.75.75 0 0 1 7.5 12Zm-4.875 5.25a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75Z"
      clipRule="evenodd"
    />
  </svg>
);

const StickyPromoIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      d="M9.375 3a1.875 1.875 0 0 0 0 3.75h1.875v4.5H3.375A1.875 1.875 0 0 1 1.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0 1 12 2.753a3.375 3.375 0 0 1 5.432 3.997h3.943c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 1 0-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3ZM11.25 12.75H3v6.75a2.25 2.25 0 0 0 2.25 2.25h6v-9ZM12.75 12.75v9h6.75a2.25 2.25 0 0 0 2.25-2.25v-6.75h-9Z"
    />
  </svg>
);

const StickySupportIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"
      clipRule="evenodd"
    />
  </svg>
);

const StickyArrowIcon = () => (
  <svg className="sticky-footer__arrow" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4.167 10h11.666M10.833 4.167 15.833 10l-5 5.833"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getAthleteInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase() || "")
    .join("");

const getMobileStickyPayoutMultiplier = (pickCount) => MOBILE_STICKY_PAYOUT_TIERS[pickCount] || "";

const getStickyFooterPotentialPayouts = (selectedCount, visibleAvatarCount) => {
  const availableSlots = Math.max(MOBILE_STICKY_MAX_VISIBLE_CIRCLES - visibleAvatarCount, 0);
  if (availableSlots <= 0) {
    return [];
  }

  const payouts = [];

  for (
    let pickCount = selectedCount + 1;
    pickCount <= MOBILE_STICKY_MAX_PICK_COUNT;
    pickCount += 1
  ) {
    const multiplier = getMobileStickyPayoutMultiplier(pickCount);

    if (multiplier) {
      payouts.push({
        label: `${multiplier}x`,
        pickCount,
      });
    }
  }

  if (payouts.length <= availableSlots) {
    return payouts;
  }

  const visiblePayouts = payouts.slice(0, Math.max(availableSlots - 1, 0));
  const hiddenPayoutCount = payouts.length - visiblePayouts.length;

  return [
    ...visiblePayouts,
    {
      label: `25x`,
      pickCount: `overflow-${hiddenPayoutCount}`,
    },
  ];
};

const StickyFooterAvatar = ({
  athlete,
  overflow,
  payoutPotential,
  payoutPotentialBackground = STICKY_FOOTER_LIGHT_BACKGROUND,
}) => {
  const style = {
    ...(payoutPotential
      ? {
          ...STICKY_FOOTER_POTENTIAL_AVATAR_STYLE,
          background: payoutPotentialBackground,
          "--sticky-footer-avatar-label-length": payoutPotential.label.length,
        }
      : {}),
  };
  const classes = [
    "sticky-footer__avatar",
    payoutPotential ? "sticky-footer__avatar--potential" : "",
    overflow ? "sticky-footer__avatar--overflow" : "",
    !overflow && !payoutPotential && !athlete?.image ? "sticky-footer__avatar--fallback" : "",
  ].filter(Boolean).join(" ");

  if (payoutPotential) {
    return (
      <span className={classes} style={style}>
        <span className="sticky-footer__avatar-label">{payoutPotential.label}</span>
      </span>
    );
  }

  if (overflow) {
    return (
      <span className={classes} style={style}>
        +{overflow}
      </span>
    );
  }

  if (athlete?.image) {
    return (
      <span className={classes} style={style}>
        <img src={athlete.image} alt={athlete.name || "Athlete"} loading="lazy" />
      </span>
    );
  }

  return (
    <span className={classes} style={style}>
      {getAthleteInitials(athlete?.name)}
    </span>
  );
};

const MobileStickyItem = ({
  active = false,
  children,
  href,
  id,
  label,
  loginRequired = false,
  onClick,
  renderAs = "a",
}) => {
  const className = `sticky-menu__item${active ? " is-active" : ""}`;

  if (renderAs === "div") {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <div
        className={className}
        data-ww-login-trigger={loginRequired ? "true" : undefined}
        id={id}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <span className="sticky-menu__icon-wrap">{children}</span>
        <span className="sticky-menu__label">{label}</span>
      </div>
    );
  }

  return (
    <a
      aria-current={active ? "page" : undefined}
      className={className}
      data-ww-login-trigger={loginRequired ? "true" : undefined}
      href={href}
      id={id}
      onClick={onClick}
    >
      <span className="sticky-menu__icon-wrap">{children}</span>
      <span className="sticky-menu__label">{label}</span>
    </a>
  );
};

const formatSkeletonStatLabel = (statsMap, selectedStat) => {
  const rawLabel =
    statsMap && typeof statsMap === "object" && selectedStat ? statsMap[selectedStat] : "";
  return formatBoardStatLabel(selectedStat, rawLabel, { popularLabel: "Popular" });
};

const normalizePromoStatLabel = (value = "") => (
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
);

const buildActivePromoSummary = (athleteList = []) => {
  const seen = new Set();
  const labels = [];

  (Array.isArray(athleteList) ? athleteList : []).forEach((athlete) => {
    const selectionState = getCardSelectionState(
      athlete?.stat_key,
      athlete?.promo,
      athlete?.easy,
      athlete?.hard
    );

    if (selectionState.promoToken !== "promo") {
      return;
    }

    const athleteName = String(athlete?.name || "").trim();
    if (!athleteName) {
      return;
    }

    const statLabel = normalizePromoStatLabel(athlete?.stat_display || selectionState.statKey);
    const statValue = athlete?.stat ?? athlete?.stat_value ?? "";
    const key = [
      athlete?.athlete_id || athlete?.term_id || athleteName,
      selectionState.statKey,
      statValue,
    ].join("|");

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    labels.push(`${athleteName} : ${statLabel}${statValue !== "" ? ` (${statValue})` : ""}`);
  });

  return {
    count: labels.length,
    text: labels.join(" • "),
  };
};

const getActivePromoAthlete = (athleteList = []) => (
  (Array.isArray(athleteList) ? athleteList : []).find((athlete) => {
    const selectionState = getCardSelectionState(
      athlete?.stat_key,
      athlete?.promo,
      athlete?.easy,
      athlete?.hard
    );

    return selectionState.promoToken === "promo";
  }) || null
);

const normalizeActivePromoSummary = (summary = {}) => {
  const text = String(summary?.text || "").trim();
  const parsedCount = Number(summary?.count);
  const count = Number.isFinite(parsedCount) && parsedCount > 0
    ? parsedCount
    : text.split("•").filter((promoText) => promoText.trim()).length;

  return {
    count,
    text,
  };
};

const getAthleteVariantKey = (athlete) => {
  if (!athlete || typeof athlete !== "object") {
    return "";
  }

  const selectionState = getCardSelectionState(
    athlete.stat_key,
    athlete.promo,
    athlete.easy,
    athlete.hard
  );
  const athleteId =
    athlete.athlete_id ??
    `${String(athlete.league || "").toLowerCase()}|${String(athlete.game_id || "")}|${String(athlete.name || "").toLowerCase()}`;
  const gameId = String(athlete.game_id || athlete.event_id || "");

  return [String(athleteId), gameId, selectionState.statKey || "", selectionState.promoToken].join("|");
};

const getTopPlaysLeagueKey = (athlete) => (
  String(athlete?._top_play_source_league || athlete?.league || "popular")
    .trim()
    .toLowerCase()
);

const normalizeEventComponent = (value = "") => (
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
);

const eventFilterSlug = (value = "") => normalizeEventComponent(value).replace(/\s+/g, "_").replace(/^_+|_+$/g, "");

const EVENT_EXPLICIT_TIME_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const EVENT_LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?)?$/;

const formatUtcEventStart = (date) => date.toISOString().slice(0, 19).replace("T", " ");

const normalizeEventStart = (athlete) => {
  const raw = String(athlete?.start_time_iso || athlete?.start_time_obj || athlete?.start_time || "").trim();
  if (!raw) {
    return "";
  }

  const localMatch = raw.match(EVENT_LOCAL_DATE_TIME_PATTERN);
  if (localMatch && !EVENT_EXPLICIT_TIME_ZONE_PATTERN.test(raw)) {
    return formatUtcEventStart(new Date(Date.UTC(
      Number(localMatch[1]),
      Number(localMatch[2]) - 1,
      Number(localMatch[3]),
      Number(localMatch[4] || 0),
      Number(localMatch[5] || 0),
      Number(localMatch[6] || 0)
    )));
  }

  const startDate = new Date(EVENT_EXPLICIT_TIME_ZONE_PATTERN.test(raw) ? raw : raw.replace(" ", "T"));
  if (!startDate) {
    return raw;
  }

  return Number.isNaN(startDate.getTime()) ? raw : formatUtcEventStart(startDate);
};

const getAthleteEventFilterId = (leagueKey, athlete) => {
  const sportKey = eventFilterSlug(leagueKey);
  const gameId = String(athlete?.game_id || athlete?.gameId || athlete?.event_id || athlete?.eventId || "").trim();
  if (!INDIVIDUAL_EVENT_SELECTION_SPORTS.has(sportKey)) {
    return gameId;
  }

  const name = normalizeEventComponent(athlete?.post_title || athlete?.name || athlete?.athlete_name || athlete?.display_name || "");
  const opponent = normalizeEventComponent(athlete?.opponent_name || athlete?.opponent_short || athlete?.opponent || "");
  if (!name && !opponent) {
    return gameId;
  }

  const participants = [name, opponent].filter(Boolean).sort();
  const parts = [];
  const startTime = normalizeEventStart(athlete);
  if (startTime) {
    parts.push(eventFilterSlug(startTime));
  } else if (gameId) {
    parts.push(`game_${eventFilterSlug(gameId)}`);
  }

  participants.forEach((participant) => {
    parts.push(eventFilterSlug(participant));
  });

  return `${sportKey}_event_${parts.filter(Boolean).join("_")}`;
};

const getTopPlaysLeagueRows = (athleteList = []) => {
  const athletes = mixTopPlaysAthletes(athleteList, TOP_PLAYS_POPULAR_ROW_LIMIT);

  return athletes.length > 0 ? [{
    league: "popular",
    label: "Popular",
    athletes,
  }] : [];
};

const getTopPlaysAthleteStatKey = (athlete) => normalizeProjectedStatKey(athlete?.stat_key || "");

const getTopPlaysFilteredAthletes = (athleteList = [], selectedStat = POPULAR_STAT_KEY) => {
  const normalizedStat = normalizeProjectedStatKey(selectedStat);

  if (!normalizedStat || normalizedStat === POPULAR_STAT_KEY || normalizedStat === TEAMS_STAT_KEY) {
    return Array.isArray(athleteList) ? athleteList : [];
  }

  return (Array.isArray(athleteList) ? athleteList : []).filter(
    (athlete) => getTopPlaysAthleteStatKey(athlete) === normalizedStat
  );
};

const getTopPlaysEventCards = (athleteList = []) => {
  const eventMap = new Map();

  (Array.isArray(athleteList) ? athleteList : []).forEach((athlete) => {
    const leagueKey = getTopPlaysLeagueKey(athlete);
    const gameId = String(athlete?.game_id || "").trim();
    const eventFilterId = String(athlete?.event_filter_id || getAthleteEventFilterId(leagueKey, athlete) || gameId).trim();
    if (!leagueKey || !eventFilterId) {
      return;
    }

    const eventKey = `${leagueKey}|${eventFilterId}`;
    if (!eventMap.has(eventKey)) {
      eventMap.set(eventKey, {
        id: eventFilterId,
        game_id: gameId,
        _topPlaysLeague: leagueKey,
        sport_family: getSportFamily(leagueKey),
        home: "",
        away: "",
        home_logo: "",
        away_logo: "",
        home_color: "",
        home_color_two: "",
        away_color: "",
        away_color_two: "",
        home_score: athlete?.home_score ?? null,
        away_score: athlete?.away_score ?? null,
        start_time: String(athlete?.start_time || ""),
        start_time_iso: String(athlete?.start_time_iso || athlete?.start_time_obj || ""),
        available_stats: {},
      });
    }

    const eventCard = eventMap.get(eventKey);
    const eventStatKey = normalizeProjectedStatKey(athlete?.stat_key || "");
    if (eventStatKey && eventStatKey !== POPULAR_STAT_KEY) {
      eventCard.available_stats[eventStatKey] = athlete?.stat_display || eventStatKey;
    }
    const usesIndividualEventId = INDIVIDUAL_EVENT_SELECTION_SPORTS.has(eventFilterSlug(leagueKey));
    const teamName = String(
      usesIndividualEventId
        ? (athlete?.post_title || athlete?.name || athlete?.athlete_name || athlete?.team_short || athlete?.team || "")
        : (athlete?.team_short || athlete?.team || "")
    ).trim();
    const opponentName = String(athlete?.opponent_name || athlete?.opponent_short || athlete?.opponent || "").trim();
    const location = String(athlete?.location || "").toLowerCase();
    const teamLogo = String(athlete?.team_logo || "").trim();
    const opponentLogo = String(athlete?.opponent_logo || "").trim();
    const teamColor = String(athlete?.team_color || "").trim();
    const teamColorTwo = String(athlete?.team_color_two || "").trim();

    if (!eventCard.start_time && athlete?.start_time) {
      eventCard.start_time = String(athlete.start_time);
    }

    if (!eventCard.start_time_iso && (athlete?.start_time_iso || athlete?.start_time_obj)) {
      eventCard.start_time_iso = String(athlete.start_time_iso || athlete.start_time_obj);
    }

    if (eventCard.home_score === null && athlete?.home_score !== null && athlete?.home_score !== undefined) {
      eventCard.home_score = athlete.home_score;
    }
    if (eventCard.away_score === null && athlete?.away_score !== null && athlete?.away_score !== undefined) {
      eventCard.away_score = athlete.away_score;
    }

    if (location === "home") {
      if (!eventCard.home && teamName) {
        eventCard.home = teamName;
      }
      if (!eventCard.away && opponentName) {
        eventCard.away = opponentName;
      }
      if (!eventCard.home_logo && teamLogo) {
        eventCard.home_logo = teamLogo;
      }
      eventCard.home_color = eventCard.home_color || teamColor;
      eventCard.home_color_two = eventCard.home_color_two || teamColorTwo;
      if (!eventCard.away_logo && opponentLogo) {
        eventCard.away_logo = opponentLogo;
      }
      return;
    }

    if (location === "away") {
      if (!eventCard.home && opponentName) {
        eventCard.home = opponentName;
      }
      if (!eventCard.away && teamName) {
        eventCard.away = teamName;
      }
      if (!eventCard.home_logo && opponentLogo) {
        eventCard.home_logo = opponentLogo;
      }
      if (!eventCard.away_logo && teamLogo) {
        eventCard.away_logo = teamLogo;
      }
      eventCard.away_color = eventCard.away_color || teamColor;
      eventCard.away_color_two = eventCard.away_color_two || teamColorTwo;
      return;
    }

    if (!eventCard.home && teamName) {
      eventCard.home = teamName;
      eventCard.home_logo = eventCard.home_logo || teamLogo;
      eventCard.home_color = eventCard.home_color || teamColor;
      eventCard.home_color_two = eventCard.home_color_two || teamColorTwo;
    }
    if (!eventCard.away && opponentName) {
      eventCard.away = opponentName;
      eventCard.away_logo = eventCard.away_logo || opponentLogo;
    }
  });

  return Array.from(eventMap.values())
    .map((eventCard) => ({
      ...eventCard,
      home: eventCard.home || "TBD",
      away: eventCard.away || "TBD",
    }))
    .sort((left, right) => {
      const leftStart = String(left?.start_time_iso || left?.start_time || "");
      const rightStart = String(right?.start_time_iso || right?.start_time || "");
      if (leftStart !== rightStart) {
        return leftStart.localeCompare(rightStart);
      }

      const leftLeague = String(left?._topPlaysLeague || "");
      const rightLeague = String(right?._topPlaysLeague || "");
      if (leftLeague !== rightLeague) {
        return leftLeague.localeCompare(rightLeague);
      }

      return String(left?.id || "").localeCompare(String(right?.id || ""));
    });
};

const mergeTopPlaysEventCards = (athleteCards = [], liveCards = []) => {
  const eventMap = new Map();
  [...(Array.isArray(athleteCards) ? athleteCards : []), ...(Array.isArray(liveCards) ? liveCards : [])]
    .forEach((eventCard) => {
      const league = String(eventCard?._topPlaysLeague || "").toLowerCase();
      const eventId = String(eventCard?.id || eventCard?.game_id || "").trim();
      if (!league || !eventId) {
        return;
      }

      const key = `${league}|${eventId}`;
      const existing = eventMap.get(key) || {};
      eventMap.set(key, {
        ...existing,
        ...eventCard,
        available_stats: {
          ...(existing.available_stats || {}),
          ...(eventCard.available_stats || {}),
        },
      });
    });

  return Array.from(eventMap.values()).sort((left, right) => {
    const leftStart = String(left?.start_time_iso || left?.start_time || "");
    const rightStart = String(right?.start_time_iso || right?.start_time || "");
    if (leftStart !== rightStart) {
      return leftStart.localeCompare(rightStart);
    }
    return String(left?.id || "").localeCompare(String(right?.id || ""));
  });
};

const getEventStatKeys = (statsMap) =>
  Object.keys(statsMap && typeof statsMap === "object" ? statsMap : {})
    .filter((statKey) => statKey && statKey !== POPULAR_STAT_KEY);

const getEventStatOptions = (game, leagueStats, eventAthletes) => {
  const availableStats = game?.available_stats && typeof game.available_stats === "object"
    ? game.available_stats
    : {};
  const athleteStatKeys = new Set(
    (Array.isArray(eventAthletes) ? eventAthletes : [])
      .map((athlete) => normalizeProjectedStatKey(athlete?.stat_key || ""))
      .filter((statKey) => statKey && statKey !== POPULAR_STAT_KEY)
  );
  const nextStats = { [POPULAR_STAT_KEY]: "All" };

  Object.keys(leagueStats && typeof leagueStats === "object" ? leagueStats : {}).forEach((statKey) => {
    if (statKey === POPULAR_STAT_KEY) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(availableStats, statKey) || athleteStatKeys.has(normalizeProjectedStatKey(statKey))) {
      nextStats[statKey] = availableStats[statKey] || leagueStats[statKey];
    }
  });

  Object.entries(availableStats).forEach(([statKey, statLabel]) => {
    if (statKey && statKey !== POPULAR_STAT_KEY && !Object.prototype.hasOwnProperty.call(nextStats, statKey)) {
      nextStats[statKey] = statLabel;
    }
  });

  return nextStats;
};

const getEventRequestStats = (game, leagueStats) => {
  const availableStats = game?.available_stats && typeof game.available_stats === "object"
    ? game.available_stats
    : {};

  return Object.keys(availableStats).length > 0
    ? getEventStatOptions(game, leagueStats, [])
    : leagueStats;
};

const getEventStatSortValue = (athlete, statOrder) => {
  const statKey = normalizeProjectedStatKey(athlete?.stat_key || "");
  return statOrder.has(statKey) ? statOrder.get(statKey) : Number.MAX_SAFE_INTEGER;
};

const sortEventAthleteStats = (athletes, statKeys) => {
  const statOrder = new Map(statKeys.map((statKey, index) => [normalizeProjectedStatKey(statKey), index]));

  return [...athletes].sort((left, right) => {
    const leftStart = String(left?.start_time_iso || left?.start_time_obj || "");
    const rightStart = String(right?.start_time_iso || right?.start_time_obj || "");
    if (leftStart !== rightStart) {
      return leftStart.localeCompare(rightStart);
    }

    const leftLineup = Number(left?.lineup ?? Number.MAX_SAFE_INTEGER);
    const rightLineup = Number(right?.lineup ?? Number.MAX_SAFE_INTEGER);
    if (Number.isFinite(leftLineup) && Number.isFinite(rightLineup) && leftLineup !== rightLineup) {
      return leftLineup - rightLineup;
    }

    const leftName = String(left?.name || "");
    const rightName = String(right?.name || "");
    if (leftName !== rightName) {
      return leftName.localeCompare(rightName);
    }

    return getEventStatSortValue(left, statOrder) - getEventStatSortValue(right, statOrder);
  });
};

const normalizeLineValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const stringValue = String(value).trim();
  if (stringValue === "") {
    return null;
  }

  const numericValue = Number(stringValue);
  if (Number.isFinite(numericValue)) {
    return {
      type: "number",
      value: numericValue,
    };
  }

  return {
    type: "string",
    value: stringValue,
  };
};

const lineValuesMatch = (previousValue, nextValue) => {
  const previousLine = normalizeLineValue(previousValue);
  const nextLine = normalizeLineValue(nextValue);

  if (!previousLine || !nextLine || previousLine.type !== nextLine.type) {
    return false;
  }

  if (previousLine.type === "number") {
    return Math.abs(previousLine.value - nextLine.value) < 0.0001;
  }

  return previousLine.value === nextLine.value;
};

const selectionMatchesSyncRequest = (selection, requestedSelection) => (
  Boolean(requestedSelection) &&
  String(selection?.selected || "") === String(requestedSelection?.selected || "") &&
  lineValuesMatch(selection?.stat, requestedSelection?.stat)
);

const getRemovedLineNotice = (removedSelections = []) => {
  const names = Array.from(new Set(
    removedSelections
      .map((selection) => String(selection?.name || "").trim())
      .filter(Boolean)
  ));

  if (names.length === 0) {
    return "A selected line is no longer available and was removed from your lineup.";
  }

  if (names.length === 1) {
    return `${names[0]}'s line is no longer available and was removed from your lineup.`;
  }

  const athleteList = names.length === 2
    ? names.join(" and ")
    : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;

  return `Lines for ${athleteList} are no longer available and were removed from your lineup.`;
};

const logBoardSelectionRemoval = (removedSelections = [], removedItems = [], message = "", attemptId = "") => {
  try {
    const formData = new FormData();
    formData.append("action", "ww_log_entry_submit_debug");
    formData.append("event", "client_board_selection_removed");
    formData.append("details", JSON.stringify({
      reason: "selection_sync_removed",
	  attemptId,
      message,
      selectedCount: removedSelections.length,
      path: window.location.pathname,
      syncRemovalReasons: removedItems
        .map((item) => String(item?.removal_reason || "unknown"))
        .filter(Boolean),
      validationIssues: removedItems.map((item) => {
        const selection = removedSelections.find(
          (candidate) => getAthleteVariantKey(candidate) === getAthleteVariantKey(item)
        );
        return {
          name: item?.name || selection?.name || "",
          league: item?.league || selection?.league || "",
          stat: item?.stat_key || selection?.stat_key || "",
          classification: item?.classification || "",
          reason: item?.removal_reason || "unknown",
          source: item?.validation_source || "",
          refreshSource: item?.refresh_source || "",
          refreshState: item?.refresh_state || "",
          bundleId: item?.validation_bundle_id || "",
          snapshotId: item?.validation_snapshot_id || "",
          snapshotAge: item?.validation_age_seconds ?? null,
          snapshotObservedAt: item?.validation_observed_at || 0,
          boardSource: selection?.src || "",
          boardObservedAt: selection?.last_updated || "",
          boardBundleId: selection?.board_bundle_id || "",
          boardSnapshotId: selection?.board_snapshot_id || "",
          boardSnapshotObservedAt: selection?.board_observed_at || 0,
          boardLine: selection?.stat ?? "",
          currentLine: item?.current_stat ?? "",
        };
      }),
      selections: removedSelections.map((selection) => ({
        id: selection?.athlete_id || "",
        name: selection?.name || "",
        league: selection?.league || "",
        game: selection?.game_id || "",
        stat: selection?.stat_key || "",
        side: selection?.selected || "",
        line: selection?.stat ?? "",
        promo: selection?.promo ? 1 : 0,
      })),
    }));

    const endpoint = window.ajaxurl || "/wp-admin/admin-ajax.php";
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, formData);
      return;
    }

    fetch(endpoint, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      body: formData,
    }).catch(() => {});
  } catch (error) {
    // Diagnostics must never interfere with board recovery.
  }
};

const TAP_IN_LEAGUE_ALIASES = {
  "3pt": "nba",
  allstar: "nba",
  allstarmlb: "mlb",
  cbb1h: "cbb",
  cbb2h: "cbb",
  march: "cbb",
  mma: "ufc",
  nba2h: "nba",
  nbasl: "nba",
  nflp: "nfl",
  nflszn: "nfl",
  ohockey: "nhl",
  hrderby: "mlb",
  tbt: "nba",
  unr: "wnba",
  wnba1h: "wnba",
  worldcup: "worldcup",
  worldcup1h: "worldcup",
  worldcup2h: "worldcup",
};

const normalizeTapInLeague = (league = "") => {
  const normalizedLeague = String(league || "")
    .trim()
    .toLowerCase();
  const compactLeague = normalizedLeague.replace(/[^a-z0-9]+/g, "");

  if (TAP_IN_LEAGUE_ALIASES[compactLeague]) {
    return TAP_IN_LEAGUE_ALIASES[compactLeague];
  }

  return TAP_IN_LEAGUE_ALIASES[normalizedLeague] || normalizedLeague;
};

const normalizeTapInStatKey = (statKey = "") => {
  const rawStatKey = String(statKey || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!rawStatKey) {
    return "";
  }

  if (rawStatKey.startsWith("projected_")) {
    return normalizeProjectedStatKey(rawStatKey);
  }

  if (rawStatKey.startsWith("promo_")) {
    return normalizeProjectedStatKey(`projected_${rawStatKey.replace(/^promo_/, "")}`);
  }

  if (rawStatKey.startsWith("goblin_") || rawStatKey.startsWith("demon_")) {
    return normalizeProjectedStatKey(rawStatKey);
  }

  return normalizeProjectedStatKey(`projected_${rawStatKey}`);
};

const normalizeTapInName = (name = "") => {
  const rawName = String(name || "").trim();

  const asciiName = typeof rawName.normalize === "function"
    ? rawName.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : rawName;

  return asciiName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getTapInStatLabel = (detail = {}, statKey = "") => {
  const rawLabel = String(detail.statName || "").replace(/_/g, " ").trim();
  if (rawLabel) {
    return rawLabel.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return statKey
    .replace(/^projected_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const findTapInAthlete = (athletes, detail, statKey) => {
  const targetName = normalizeTapInName(detail?.name);
  const targetStatValue = detail?.statValue;
  const candidateAthletes = (Array.isArray(athletes) ? athletes : []).filter((athlete) =>
    normalizeTapInName(athlete?.name) === targetName &&
    normalizeProjectedStatKey(athlete?.stat_key) === statKey &&
    Boolean(athlete?.promo)
  );

  if (candidateAthletes.length === 0) {
    return null;
  }

  if (targetStatValue !== null && targetStatValue !== undefined && String(targetStatValue).trim() !== "") {
    return candidateAthletes.find((athlete) =>
      lineValuesMatch(athlete?.stat, targetStatValue)
    ) || null;
  }

  return candidateAthletes[0];
};

const findTapInAthleteAcrossStats = async (
  fetchBoardAthletes,
  targetSport,
  targetStat,
  detail,
  requestToken,
  statsMap = {}
) => {
  const statCandidates = [
    targetStat,
    ...Object.keys(statsMap && typeof statsMap === "object" ? statsMap : {}),
  ].map((statKey) => normalizeProjectedStatKey(statKey)).filter(Boolean);
  const attemptedStats = new Set();

  for (const statCandidate of statCandidates) {
    if (attemptedStats.has(statCandidate)) {
      continue;
    }

    attemptedStats.add(statCandidate);
    const athleteResult = await fetchBoardAthletes(
      targetSport,
      statCandidate,
      false,
      requestToken,
      { limit: 0 }
    );

    if (athleteResult.aborted) {
      return { aborted: true, athlete: null, list: [] };
    }

    const tapInAthlete = findTapInAthlete(athleteResult.list, detail, targetStat);
    if (tapInAthlete) {
      return {
        aborted: false,
        athlete: tapInAthlete,
        list: athleteResult.list,
      };
    }
  }

  return { aborted: false, athlete: null, list: [] };
};

const getTapInSportCandidates = (league = "", verifiedStats = {}) => {
  const normalized = normalizeTapInLeague(league);
  const compactLeague = String(league || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  const rawLeague = String(league || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  const candidates = [normalized, compactLeague, rawLeague]
    .map((sportKey) => String(sportKey || "").trim().toLowerCase())
    .filter(Boolean);

  if (normalized === "soccer") {
    candidates.push("worldcup", "worldcup1h", "worldcup2h");
  }
  if (normalized === "worldcup" || normalized === "worldcup1h" || normalized === "worldcup2h") {
    candidates.push("soccer");
  }

  const availableSports = Object.keys(verifiedStats && typeof verifiedStats === "object" ? verifiedStats : {});
  availableSports.forEach((sportKey) => {
    const normalizedSport = String(sportKey || "").trim().toLowerCase();
    if (normalizedSport && (normalizedSport === compactLeague || normalizedSport === rawLeague)) {
      candidates.push(normalizedSport);
    }
  });

  return Array.from(new Set(candidates));
};

const stripBoardRefreshMeta = (athlete) => {
  if (!athlete || typeof athlete !== "object") {
    return athlete;
  }

  const {
    _boardRefreshState,
    _boardRefreshToken,
    _boardPreviousStat,
    ...rest
  } = athlete;

  return rest;
};

const clearBoardRefreshMeta = (athletes) => {
  if (!Array.isArray(athletes) || athletes.length === 0) {
    return athletes;
  }

  let changed = false;
  const nextAthletes = athletes.map((athlete) => {
    if (!athlete?._boardRefreshState && !athlete?._boardRefreshToken && !athlete?._boardPreviousStat) {
      return athlete;
    }

    changed = true;
    return stripBoardRefreshMeta(athlete);
  });

  return changed ? nextAthletes : athletes;
};

const stripSelectionSyncMeta = (athlete) => {
  if (!athlete || typeof athlete !== "object") {
    return athlete;
  }

  const {
    _selectionSyncStatus,
    _selectionCurrentStat,
    _selectionCurrentStatDisplay,
    _selectionSyncClassification,
    _selectionSyncReason,
    _selectionSyncRefreshState,
    _selectionSyncRefreshStartedAt,
    _selectionSyncRefreshTimeout,
    ...rest
  } = athlete;

  return rest;
};

const feedMetaIsStale = (meta) => {
  const updatedAtSeconds = Number(meta?.updated_at || meta?.mtime || 0);
  if (!Number.isFinite(updatedAtSeconds) || updatedAtSeconds <= 0) {
    return true;
  }

  return Date.now() - (updatedAtSeconds * 1000) >= STALE_FEED_MAX_AGE_MS;
};

const getHiddenMoneyValue = (id) => {
  if (typeof document === "undefined") {
    return 0;
  }

  const rawValue = document.getElementById(id)?.value;
  const value = Number.parseFloat(String(rawValue ?? "").replace(/,/g, ""));

  return Number.isFinite(value) ? value : 0;
};

const currentUserHasRefreshBalance = () => {
  return getHiddenMoneyValue("balance") > 0 || getHiddenMoneyValue("promo-balance") > 0;
};

const markRegistrationFlowStarted = () => {
  try {
    sessionStorage.setItem(REGISTRATION_FLOW_KEY, String(Date.now()));
  } catch (e) {
    // Storage write failures should not impact board UX.
  }
};

const clearRegistrationFlowStarted = () => {
  try {
    sessionStorage.removeItem(REGISTRATION_FLOW_KEY);
  } catch (e) {
    // no-op
  }
};

const isRegistrationFlowActive = () => {
  if (loggedIn) {
    return false;
  }

  try {
    const raw = sessionStorage.getItem(REGISTRATION_FLOW_KEY);
    if (!raw) return false;

    const startedAt = Number(raw);
    if (!Number.isFinite(startedAt)) {
      sessionStorage.removeItem(REGISTRATION_FLOW_KEY);
      return false;
    }

    if (Date.now() - startedAt > REGISTRATION_FLOW_TTL_MS) {
      sessionStorage.removeItem(REGISTRATION_FLOW_KEY);
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

const markBoardBackgrounded = () => {
  const now = Date.now();
  boardBackgroundedAtMemory = now;
  boardBackgroundHandledAtMemory = 0;

  try {
    sessionStorage.setItem(BOARD_BACKGROUND_AT_KEY, String(now));
    sessionStorage.removeItem(BOARD_BACKGROUND_HANDLED_KEY);
  } catch (e) {
    // Storage write failures should not impact board UX.
  }
};

const getBoardBackgroundedAt = () => {
  try {
    const raw = sessionStorage.getItem(BOARD_BACKGROUND_AT_KEY);
    if (raw) {
      const ts = Number(raw);
      if (Number.isFinite(ts) && ts > 0) {
        return ts;
      }
    }
  } catch (e) {
    // Fall back to in-memory timestamp below.
  }

  return boardBackgroundedAtMemory;
};

const consumeBoardReturnRefreshSignal = () => {
  const backgroundedAt = getBoardBackgroundedAt();
  if (!backgroundedAt) {
    return false;
  }

  if (Date.now() - backgroundedAt < BOARD_RETURN_REFRESH_THRESHOLD_MS) {
    return false;
  }

  let handledAt = boardBackgroundHandledAtMemory;

  try {
    const rawHandledAt = sessionStorage.getItem(BOARD_BACKGROUND_HANDLED_KEY);
    if (rawHandledAt) {
      const parsedHandledAt = Number(rawHandledAt);
      if (Number.isFinite(parsedHandledAt)) {
        handledAt = parsedHandledAt;
      }
    }
  } catch (e) {
    // Fall back to in-memory handled timestamp below.
  }

  if (handledAt === backgroundedAt) {
    return false;
  }

  boardBackgroundHandledAtMemory = backgroundedAt;

  try {
    sessionStorage.setItem(BOARD_BACKGROUND_HANDLED_KEY, String(backgroundedAt));
  } catch (e) {
    // no-op
  }

  return true;
};
// const promoBalance = document.getElementById("promo-balance")?.value;
// const paidBalance = document.getElementById("balance")?.value;
// const shrk = document.getElementById("shrk")?.value;
// const promoPlay = promoBalance > paidBalance;

const App = () => {
  const { isH2H, isAdmin } = useContext(GlobalContext);
  const stickyPortalTarget = useBodyPortalTarget();
  const stickyChromeMountKey = useStickyChromeMountKey();
  const [athletes, setAthletes] = useState(false);
  const [emptyAthleteStateReadyKey, setEmptyAthleteStateReadyKey] = useState("");
  const [emptyBoardRefreshKey, setEmptyBoardRefreshKey] = useState("");
  const [boardActivePromoSummary, setBoardActivePromoSummary] = useState(() => normalizeActivePromoSummary());
  const visibleActivePromoSummary = useMemo(() => buildActivePromoSummary(athletes), [athletes]);
  const activePromoAthlete = useMemo(() => getActivePromoAthlete(athletes), [athletes]);
  const activePromoSummary = useMemo(() => (
    boardActivePromoSummary.count > 0 || boardActivePromoSummary.text
      ? boardActivePromoSummary
      : visibleActivePromoSummary
  ), [boardActivePromoSummary, visibleActivePromoSummary]);
  const syncActivePromoSummaryFromBootstrap = useCallback((payload) => {
    if (!payload || typeof payload !== "object" || !Object.prototype.hasOwnProperty.call(payload, "activePromoSummary")) {
      return;
    }

    setBoardActivePromoSummary(normalizeActivePromoSummary(payload.activePromoSummary));
  }, []);
  const [loading, setLoading] = useState(true);
  const [athletesLoading, setAthletesLoading] = useState(true);
	const [athletesLoadFailed, setAthletesLoadFailed] = useState(false);
  const boardRefreshWarningRef = useRef(null);
  const dismissBoardRefreshWarning = useCallback(() => {
    if (boardRefreshWarningRef.current && typeof toastr?.clear === "function") {
      toastr.clear(boardRefreshWarningRef.current);
    }
    boardRefreshWarningRef.current = null;
  }, []);
  const showBoardRefreshWarning = useCallback(() => {
    dismissBoardRefreshWarning();
    boardRefreshWarningRef.current = toastr["warning"](
      "This stat could not be refreshed. Tap it to try again.",
      "Board Refresh"
    );
  }, [dismissBoardRefreshWarning]);
  const [boardLoadingMoreAthletes, setBoardLoadingMoreAthletes] = useState(false);
  const [boardHydrationHasMore, setBoardHydrationHasMore] = useState(false);
  useEffect(() => {
    if (!athletesLoading && Array.isArray(athletes) && athletes.length > 0) {
      setAthletesLoadFailed(false);
      dismissBoardRefreshWarning();
    }
  }, [athletes, athletesLoading, dismissBoardRefreshWarning]);
  const [isDesktopViewport, setIsDesktopViewport] = useState(getIsDesktopViewport);
  const initialHeadshotEagerCount = isDesktopViewport
    ? INITIAL_DESKTOP_HEADSHOT_COUNT
    : INITIAL_MOBILE_HEADSHOT_COUNT;
  const [selected, setSelected] = useState([]);
  const [stats, setStats] = useState([]);
  const [stat, setStat] = useState("");
  const [sportStatsLoading, setSportStatsLoading] = useState(false);
  const currentPath = normalizeStickyPath(window.location.pathname);
  const balance = document.getElementById("balance").value;
  const [sport, setSportState] = useState("");
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedGame, setSelectedGame] = useState(null);
  const eventModeSnapshotRef = useRef(null);
  const eventUnavailableHandlerRef = useRef(null);
  const activeSportRef = useRef("");
  const setSport = (nextSport) => {
    const nextSportKey = normalizeSportKey(nextSport);
    if (activeSportRef.current && nextSportKey !== activeSportRef.current) {
      // Event IDs are league-specific. Carrying an MLB/WNBA event into a
      // Tennis request can return an empty result and strand mobile users
      // behind the athlete-card skeleton until they toggle leagues again.
      setSelectedGameId("");
      setSelectedGame(null);
      if (selectedGameId) {
        eventModeSnapshotRef.current = null;
      }
    }
    activeSportRef.current = nextSportKey;
    setSportState(nextSport);
  };
  const [sports, setSports] = useState([]);
  const [leagueInventoryBySport, setLeagueInventoryBySport] = useState({});
  const handleLeagueInventoryChange = useCallback((league, hasInventory) => {
    const leagueKey = normalizeSportKey(league);
    if (!leagueKey) {
      return;
    }

    setLeagueInventoryBySport((current) => (
      current[leagueKey] === hasInventory
        ? current
        : { ...current, [leagueKey]: hasInventory }
    ));
  }, []);
  const displayedSports = sports.filter(
    (sportKey) => leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
  );
  const displayedSportsKey = displayedSports
    .map((sportKey) => normalizeSportKey(sportKey))
    .filter((sportKey) => sportKey && sportKey !== TOP_PLAYS_SPORT)
    .join("|");
  const [liveSports, setLiveSports] = useState([]);
  const liveRailStatesRef = useRef({});
  const handleLiveAvailabilityChange = useCallback((league, isLive) => {
    liveRailStatesRef.current[league] = { isLive, observedAt: Date.now() };
    setLiveSports((current) => reconcileLiveSports(current, liveRailStatesRef.current));
  }, []);
  const [liveSportsRefreshTick, setLiveSportsRefreshTick] = useState(0);
  useEffect(() => {
    const requestedSports = displayedSportsKey.split("|").filter(Boolean);
    if (requestedSports.length === 0) {
      setLiveSports([]);
      return undefined;
    }

    const controller = new AbortController();
    const requestTimeout = window.setTimeout(() => controller.abort(), 10000);
    const refreshTimeout = window.setTimeout(
      () => setLiveSportsRefreshTick((tick) => tick + 1),
      60 * 1000
    );
    const params = new URLSearchParams({
      sports: requestedSports.join(","),
      shrk: String(document.getElementById("shrk")?.value || "0"),
    });

    fetch(`/wp-json/wagedwar/v1/live-leagues?${params.toString()}`, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((json) => {
        if (!controller.signal.aborted) {
          setLiveSports(reconcileLiveSports(json?.data, liveRailStatesRef.current));
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setLiveSports([]);
        }
      })
      .finally(() => window.clearTimeout(requestTimeout));

    return () => {
      controller.abort();
      window.clearTimeout(requestTimeout);
      window.clearTimeout(refreshTimeout);
    };
  }, [displayedSportsKey, liveSportsRefreshTick]);
  const [recentBoardLeagues, setRecentBoardLeagues] = useState(readRecentBoardLeagues);
  const [darkMode, setDarkMode] = useState(true);
  const [maxAthletes, setMaxAthletes] = useState(
    win100ModeActive ? WIN_100_PICK_COUNT : 6
  );
  const [showLineupReviewSheet, setShowLineupReviewSheet] = useState(false);
  const [sidebarLoadRequested, setSidebarLoadRequested] = useState(false);
  const [activeInfoModal, setActiveInfoModal] = useState("");
  const [verifiedStats, setVerifiedStats] = useState({});
  const [popularPrewarmComplete, setPopularPrewarmComplete] = useState(true);
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResetSignal, setSearchResetSignal] = useState(0);
  const [teamLineupAthletes, setTeamLineupAthletes] = useState([]);
  const [teamLineupGroups, setTeamLineupGroups] = useState([]);
  const [teamLineupQuotedGroups, setTeamLineupQuotedGroups] = useState([]);
  const [teamLineupQuotesReady, setTeamLineupQuotesReady] = useState(false);
  const [teamLineupLoading, setTeamLineupLoading] = useState(false);
  const [teamLineupAvailableBySport, setTeamLineupAvailableBySport] = useState({});
  const [teamsHubOpen, setTeamsHubOpen] = useState(false);
  const [teamsHubGroups, setTeamsHubGroups] = useState([]);
  const [teamsHubQuotedGroups, setTeamsHubQuotedGroups] = useState([]);
  const [teamsHubQuotesReady, setTeamsHubQuotesReady] = useState(false);
  const [teamsHubLoading, setTeamsHubLoading] = useState(false);
  const [pendingTeamReplaceKey, setPendingTeamReplaceKey] = useState("");
  const [pendingTeamApplyKey, setPendingTeamApplyKey] = useState("");
  const h2hCount = document.getElementById("h2h-count")?.value;
  const [cacheCleared, setCacheCleared] = useState(false); // Track if cache has been cleared
  const cacheClearedRef = useRef(false); // Use ref to persist across renders
  const clearedSportsRef = useRef(new Set()); // Track cache clears per sport
  const athletesRef = useRef(false);
  const seenBoardAthletesRef = useRef(new Map());
  const displayedAthletesBySelectionRef = useRef(new Map());
  const selectedRef = useRef([]);
  const lastSelectionTapRef = useRef(null);
  const selectedLineSyncRequestRef = useRef(0);
  const selectedLineSyncActiveSignatureRef = useRef("");
  const selectedLineSyncCompletedSignatureRef = useRef("");
  const selectedLineSyncQueuedRef = useRef(false);
  const selectedLineSyncRefreshTicketsRef = useRef({});
  const selectedLineSyncRefreshTimerRef = useRef(null);
  const selectedLineSyncRefreshDeadlineRef = useRef(0);
  const selectedLineSyncDeferredRetryRef = useRef(0);
  const selectedLineSyncRetrySignatureRef = useRef("");
  const topPlaysEmptyFallbackAttemptRef = useRef("");
  const topPlaysScrollerRefs = useRef(new Map());
  const topPlaysDragRef = useRef({
    animationFrame: null,
    inertiaFrame: null,
    isDown: false,
    lastClientX: 0,
    lastMoveAt: 0,
    moved: false,
    pointerId: null,
    scroller: null,
    settlingScroller: null,
    scrollLeft: 0,
    startX: 0,
    suppressClick: false,
    targetScrollLeft: 0,
    velocityX: 0,
  });
  const timeoutNotifiedRef = useRef(timeoutNotifiedRefGlobal.current);
  const boardPerformanceTrackerRef = useRef(null);
  const boardSelectionPerformanceTrackerRef = useRef(null);
  const boardSelectionPerformanceKeyRef = useRef("");
  const boardPerformanceBaselineReadyRef = useRef(false);
  const lastBoardPerformanceSelectionRef = useRef("");
  const [boardNavigationState, dispatchBoardNavigation] = useReducer(
    boardNavigationReducer,
    undefined,
    createInitialBoardNavigationState
  );
  const atomicBoardTransition = boardNavigationState.transition;
  const atomicBoardTransitionRef = useRef(null);
  const isTopPlaysSelected = isTopPlaysSport(sport);
  const isTeamsSelected = stat === TEAMS_STAT_KEY;
  const normalizedSport = String(sport || "").toLowerCase();
  const teamLineupAvailabilityKnown = Object.prototype.hasOwnProperty.call(teamLineupAvailableBySport, normalizedSport);
  const teamLineupAvailableForSport = !teamLineupAvailabilityKnown || teamLineupAvailableBySport[normalizedSport] !== false;
  const currentUserTier = Number(getCurrentUserTierValue());
  const teamsHiddenForTier = currentUserTier === 2 || currentUserTier === 3;
  const teamsBaseEnabled =
    maxAthletes >= 4 &&
    !isH2H &&
    !teamsHiddenForTier &&
    isTeamsLineupSport(sport);
  const teamsStatEnabled =
    teamsBaseEnabled &&
    teamLineupAvailabilityKnown &&
    teamLineupAvailableForSport;
  const teamsHubEnabled =
    TEAM_LINEUP_FEATURE_ENABLED &&
    maxAthletes >= 4 &&
    !isH2H &&
    !teamsHiddenForTier;
  const isTeamsViewSelected = (teamsStatEnabled && isTeamsSelected) || (teamsHubEnabled && teamsHubOpen);

  useEffect(() => {
    const selectedLeague = normalizeSportKey(sport);
    if (!selectedLeague) {
      return;
    }

    setRecentBoardLeagues((currentLeagues) => {
      const nextLeagues = [
        selectedLeague,
        ...currentLeagues.filter((league) => league !== selectedLeague),
      ].slice(0, RECENT_BOARD_LEAGUES_MAX);

      if (nextLeagues.join("|") === currentLeagues.join("|")) {
        return currentLeagues;
      }

      try {
        window.localStorage?.setItem(RECENT_BOARD_LEAGUES_KEY, JSON.stringify(nextLeagues));
      } catch (e) {
        // Storage restrictions should not affect league navigation.
      }

      return nextLeagues;
    });
  }, [sport]);

  useEffect(() => {
    boardPerformanceTrackerRef.current = createBoardPerformanceTracker();

    return () => {
      boardPerformanceTrackerRef.current?.dispose();
      boardSelectionPerformanceTrackerRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    const tracker = boardPerformanceTrackerRef.current;
    if (!tracker || tracker.isComplete()) {
      return undefined;
    }

    tracker.update({
      athletesCount: Array.isArray(athletes) ? athletes.length : 0,
      sport,
      stat,
    });

    if (loading || athletesLoading || !Array.isArray(athletes)) {
      return undefined;
    }

    if (athletes.length === 0) {
      const emptyTimer = window.setTimeout(() => {
        lastBoardPerformanceSelectionRef.current = [
          normalizeSportKey(sport),
          normalizeProjectedStatKey(stat),
          String(selectedGameId || ""),
        ].join("|");
        boardPerformanceBaselineReadyRef.current = true;
        tracker.finish("empty", {
          athletesCount: 0,
          reason: sport && stat ? "no-athletes-after-recovery" : "board-selection-unresolved",
          sport,
          stat,
        });
      }, 500);

      return () => window.clearTimeout(emptyTimer);
    }

    // Passive effects run after React commits this board. A zero-delay task
    // gives the browser a paint opportunity without depending on animation
    // frames, which mobile browsers may suspend for backgrounded webviews.
    const finishTimer = window.setTimeout(() => {
      lastBoardPerformanceSelectionRef.current = [
        normalizeSportKey(sport),
        normalizeProjectedStatKey(stat),
        String(selectedGameId || ""),
      ].join("|");
      boardPerformanceBaselineReadyRef.current = true;
      const paintedCards = document.querySelectorAll(
        ".athlete-card:not(.athlete-board-skeleton__card)"
      ).length;
      tracker.finish("success", {
        athletesCount: athletes.length,
        reason: paintedCards > 0 ? "athlete-cards-painted" : "athlete-data-rendered",
        sport,
        stat,
      });
    }, 0);

    return () => {
      window.clearTimeout(finishTimer);
    };
  }, [athletes, athletesLoading, loading, selectedGameId, sport, stat]);

  useEffect(() => {
    const initialTracker = boardPerformanceTrackerRef.current;
    if (!initialTracker?.isComplete()) {
      return undefined;
    }

    const selectionKey = [
      normalizeSportKey(sport),
      normalizeProjectedStatKey(stat),
      String(selectedGameId || ""),
    ].join("|");
    const boardReady = !loading && !athletesLoading && Array.isArray(athletes);
    let tracker = boardSelectionPerformanceTrackerRef.current;
    if (boardNavigationState.phase === BOARD_NAVIGATION_PHASES.CHECKING) {
      return undefined;
    }
    const isTrackableSelection =
      Boolean(sport) &&
      Boolean(normalizeProjectedStatKey(stat)) &&
      !searchMode &&
      !isTeamsViewSelected &&
      !isTopPlaysSelected;

    if (!boardPerformanceBaselineReadyRef.current) {
      lastBoardPerformanceSelectionRef.current = selectionKey;
      boardPerformanceBaselineReadyRef.current = true;
      return undefined;
    }

    if (tracker?.isComplete()) {
      lastBoardPerformanceSelectionRef.current = boardSelectionPerformanceKeyRef.current;
      boardSelectionPerformanceTrackerRef.current = null;
      boardSelectionPerformanceKeyRef.current = "";
      tracker = null;
    }

    // Searches, Teams, and Top Plays replace the standard stat-card board and
    // cannot satisfy this tracker. Discard any pending measurement instead of
    // logging a false 20-second athlete-selection timeout.
    if (!isTrackableSelection) {
      tracker?.dispose();
      boardSelectionPerformanceTrackerRef.current = null;
      boardSelectionPerformanceKeyRef.current = "";
      lastBoardPerformanceSelectionRef.current = selectionKey;
      return undefined;
    }

    // A second selection supersedes the first one. Start its timer at the
    // actual click instead of allowing the abandoned selection's timer and
    // context to leak into the new board.
    if (tracker && boardSelectionPerformanceKeyRef.current !== selectionKey) {
      tracker.dispose();
      boardSelectionPerformanceTrackerRef.current = null;
      boardSelectionPerformanceKeyRef.current = "";
      tracker = null;
    }

    const selectionChanged = selectionKey !== lastBoardPerformanceSelectionRef.current;
    if (!tracker && selectionChanged && (loading || athletesLoading || boardReady)) {
      tracker = createBoardPerformanceTracker({
        startedAt: Date.now(),
        loadType: "selection",
        context: {
          athletesCount: 0,
          sport,
          stat,
        },
      });
      boardSelectionPerformanceTrackerRef.current = tracker;
      boardSelectionPerformanceKeyRef.current = selectionKey;
    }

    if (!tracker || tracker.isComplete()) {
      return undefined;
    }

    tracker.update({
      athletesCount: Array.isArray(athletes) ? athletes.length : 0,
      sport,
      stat,
    });
    boardSelectionPerformanceKeyRef.current = selectionKey;

    if (!boardReady) {
      return undefined;
    }

    if (athletes.length === 0) {
      const emptyTimer = window.setTimeout(() => {
        lastBoardPerformanceSelectionRef.current = selectionKey;
        tracker.finish("empty", {
          athletesCount: 0,
          reason: sport && stat ? "selection-returned-no-athletes" : "selection-unresolved",
          sport,
          stat,
        });
        if (boardSelectionPerformanceTrackerRef.current === tracker) {
          boardSelectionPerformanceTrackerRef.current = null;
          boardSelectionPerformanceKeyRef.current = "";
        }
      }, 500);

      return () => window.clearTimeout(emptyTimer);
    }

    let firstFrame = 0;
    let secondFrame = 0;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        lastBoardPerformanceSelectionRef.current = selectionKey;
        const paintedCards = document.querySelectorAll(
          ".athlete-card-grid .athlete-card:not(.athlete-board-skeleton__card)"
        ).length;
        tracker.finish("success", {
          athletesCount: athletes.length,
          reason: paintedCards > 0 ? "selection-cards-painted" : "selection-data-rendered",
          sport,
          stat,
        });
        if (boardSelectionPerformanceTrackerRef.current === tracker) {
          boardSelectionPerformanceTrackerRef.current = null;
          boardSelectionPerformanceKeyRef.current = "";
        }
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [
    athletes,
    athletesLoading,
    isTeamsViewSelected,
    isTopPlaysSelected,
    loading,
    searchMode,
    selectedGameId,
    sport,
    stat,
    boardNavigationState.phase,
  ]);

  const finishBoardSelectionPerformance = (outcome, context = {}) => {
    const tracker = boardSelectionPerformanceTrackerRef.current;
    if (!tracker || tracker.isComplete()) {
      return false;
    }

    const finished = tracker.finish(outcome, context);
    if (finished && boardSelectionPerformanceTrackerRef.current === tracker) {
      boardSelectionPerformanceTrackerRef.current = null;
      boardSelectionPerformanceKeyRef.current = "";
    }
    return finished;
  };

  const beginBoardSelectionPerformance = (selectedSport, selectedStat) => {
    finishBoardSelectionPerformance("abandoned", {
      reason: "selection-superseded",
    });
    const selectionKey = [
      normalizeSportKey(selectedSport),
      normalizeProjectedStatKey(selectedStat),
      "",
    ].join("|");
    const tracker = createBoardPerformanceTracker({
      startedAt: Date.now(),
      loadType: "selection",
      context: {
        athletesCount: 0,
        sport: selectedSport,
        stat: selectedStat,
        reason: "atomic-selection-checking",
      },
    });
    boardSelectionPerformanceTrackerRef.current = tracker;
    boardSelectionPerformanceKeyRef.current = selectionKey;
    return tracker;
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(INITIAL_HEADSHOT_DESKTOP_QUERY);
    const handleViewportChange = () => {
      setIsDesktopViewport(mediaQuery.matches);
    };

    handleViewportChange();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  const normalizeSelectedAthlete = (athlete) => {
    if (!athlete || typeof athlete !== "object") return athlete;
    const sanitizedAthlete = stripSelectionSyncMeta(stripBoardRefreshMeta(athlete));
    const selectionState = getCardSelectionState(
      sanitizedAthlete.stat_key,
      sanitizedAthlete.promo,
      sanitizedAthlete.easy,
      sanitizedAthlete.hard
    );
    return {
      ...sanitizedAthlete,
      stat_key: selectionState.statKey,
      easy: selectionState.easy,
      hard: selectionState.hard,
      promo: selectionState.promo,
    };
  };

  const getLocalBoardSelectionStatus = useCallback((entry, boardEntries = athletesRef.current) => {
    if (!entry || typeof entry !== "object") {
      return null;
    }

    const liveBoardEntries = Array.isArray(boardEntries) ? boardEntries : [];
    const cachedBoardEntries = Array.from(seenBoardAthletesRef.current.values());
    const candidateEntries = liveBoardEntries.length > 0 ? liveBoardEntries : cachedBoardEntries;

    if (candidateEntries.length === 0) {
      return null;
    }

    const exactVariant = candidateEntries.find(
      (candidate) => getAthleteVariantKey(candidate) === getAthleteVariantKey(entry)
    );

    if (exactVariant) {
      if (lineValuesMatch(exactVariant?.stat, entry?.stat)) {
        return { status: "ok" };
      }

      return {
        status: "changed",
        current_stat: exactVariant?.stat ?? null,
        current_stat_display: exactVariant?.stat_display ?? entry?.stat_display ?? "",
      };
    }

    if (liveBoardEntries.length > 0) {
      return null;
    }

    const familyMatch = candidateEntries.find((candidate) =>
      matchesSelectionFamily(candidate, entry.athlete_id, entry.stat_key)
    );

    if (!familyMatch) {
      return null;
    }

    return {
      status: "changed",
      current_stat: familyMatch?.stat ?? null,
      current_stat_display: familyMatch?.stat_display ?? entry?.stat_display ?? "",
    };
  }, []);

  const handleSwappedSelection = useCallback((swapData) => {
    if (!swapData || !swapData.athleteId) {
      return;
    }

    setSelected((prevSelected) => {
      const currentIndex = prevSelected.findIndex((entry) =>
        matchesSelectionVariant(
          entry,
          swapData.athleteId,
          swapData.previousStatKey,
          swapData.previousPromoToken,
          swapData.previousEasy,
          swapData.previousHard
        )
      );
      const fallbackIndex = currentIndex > -1
        ? currentIndex
        : prevSelected.findIndex((entry) =>
            matchesSelectionFamily(entry, swapData.athleteId, swapData.previousStatKey)
          );

      if (fallbackIndex === -1) {
        return prevSelected;
      }

      const currentSelection = prevSelected[fallbackIndex];
      if (
        currentSelection.selected === "less" &&
        (swapData.nextEasy || swapData.nextHard || swapData.nextPromoToken === "promo")
      ) {
        return prevSelected.filter((_, index) => index !== fallbackIndex);
      }

      const nextSelection = normalizeSelectedAthlete({
        ...currentSelection,
        stat_key: swapData.nextStatKey,
        stat: swapData.nextStatValue,
        easy: swapData.nextEasy,
        hard: swapData.nextHard,
        promo: swapData.nextPromoToken === "promo",
      });

      return prevSelected.reduce((nextSelected, entry, index) => {
        if (index === fallbackIndex) {
          nextSelected.push(nextSelection);
          return nextSelected;
        }

        if (matchesSelectionFamily(entry, swapData.athleteId, swapData.nextStatKey)) {
          return nextSelected;
        }

        nextSelected.push(entry);
        return nextSelected;
      }, []);
    });
  }, []);

  const isMobileLineupViewport = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(MOBILE_LINEUP_SHEET_QUERY).matches;
  };

  const openMobileLineupReviewAtPickCount = (previousCount, nextCount) => {
    const reviewPickCount = win100ModeActive
      ? WIN_100_PICK_COUNT
      : MOBILE_LINEUP_AUTO_REVIEW_COUNT;
    if (
      previousCount < reviewPickCount &&
      nextCount === reviewPickCount &&
      isMobileLineupViewport()
    ) {
      setShowLineupReviewSheet(true);
    }
  };

  const getShrkValue = () => document.getElementById('shrk')?.value;
  const [topPlaysLiveEventCards, setTopPlaysLiveEventCards] = useState([]);
  const [topPlaysLiveRefreshTick, setTopPlaysLiveRefreshTick] = useState(0);
  const [rejectedEvents, setRejectedEvents] = useState({});
  const [pendingEventKey, setPendingEventKey] = useState("");
  const pendingEventRef = useRef("");
  const beginAtomicBoardTransition = (requestToken, selectedSport, selectedStat = "", gameId = false) => {
    const transition = createAtomicBoardTransition({
      requestToken,
      sport: selectedSport,
      stat: selectedStat,
      sourceSport: sport,
    });

    atomicBoardTransitionRef.current = transition;
    dispatchBoardNavigation({ type: "begin", transition: {
      ...transition,
      gameId: String(gameId || ""),
      previewRows: boardVisualCacheRef.current.get(getBoardVisualKey(selectedSport, selectedStat, gameId)),
    } });
    beginBoardSelectionPerformance(transition.sport, transition.stat);
  };

  const finishAtomicBoardTransition = (requestToken, outcome = "ready", snapshot = null, error = "") => {
    if (!atomicBoardTransitionMatches(atomicBoardTransitionRef.current, requestToken)) {
      return false;
    }
    atomicBoardTransitionRef.current = null;
    dispatchBoardNavigation({
      type: outcome === "ready" ? "ready" : "error",
      requestToken,
      snapshot,
      error,
    });
    return true;
  };
  useEffect(() => {
    if (!isTopPlaysSelected) {
      setTopPlaysLiveEventCards([]);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => setTopPlaysLiveRefreshTick((tick) => tick + 1),
      60 * 1000
    );
    const requestTimeout = window.setTimeout(() => {
      // A hung refresh must not leave yesterday's live CTA on screen forever.
      controller.abort();
      setTopPlaysLiveEventCards([]);
    }, 10 * 1000);

    // Use the same slim, read-only endpoint as league event rails. Keep this
    // uncached: live hero eligibility must still be checked on every refresh.
    fetch(`/wp-json/wagedwar/v1/available-games/${TOP_PLAYS_SPORT}`, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load Top Picks live events");
        return response.json();
      })
      .then((json) => {
        if (!controller.signal.aborted && json?.success && Array.isArray(json.data)) {
          setTopPlaysLiveEventCards(json.data);
        } else if (!controller.signal.aborted) {
          setTopPlaysLiveEventCards([]);
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setTopPlaysLiveEventCards([]);
          console.error("Error loading Top Picks live events:", error);
        }
      })
      .finally(() => window.clearTimeout(requestTimeout));

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
      window.clearTimeout(requestTimeout);
    };
  }, [isTopPlaysSelected, topPlaysLiveRefreshTick]);
  const topPlaysVisibleAthletes = useMemo(
    () => (isTopPlaysSelected ? getTopPlaysFilteredAthletes(athletes, POPULAR_STAT_KEY) : []),
    [isTopPlaysSelected, athletes]
  );
  const topPlaysEventCards = useMemo(
    () => (
      isTopPlaysSelected
        ? filterRejectedEvents(mergeTopPlaysEventCards(
            // Cached athlete cards cannot independently authorize a live hero.
            getTopPlaysEventCards(topPlaysVisibleAthletes).filter((game) => !isLiveGame(game)),
            topPlaysLiveEventCards
          ), rejectedEvents)
        : []
    ),
    [isTopPlaysSelected, topPlaysLiveEventCards, topPlaysVisibleAthletes, rejectedEvents]
  );
  const topPlaysLiveHeroEvents = useMemo(
    () => topPlaysEventCards.filter((game) => (
      isLiveGame(game) && Object.keys(game?.available_stats || {}).length > 0
    )),
    [topPlaysEventCards]
  );
  const shouldShowTopPlaysInitialSkeleton = () => {
    const requestedSport = urlParams.get("sport");
    if (isTopPlaysSport(requestedSport) || isTopPlaysSelected) {
      return true;
    }

    if (requestedSport) {
      return false;
    }

    return String(getShrkValue() ?? "0") !== "1" && Number(getCurrentUserTierValue()) <= 1;
  };
  const filterAthleteList = (list) => filterUnavailableSelections(list);
  const teamsStatSignature = Object.keys(stats || {}).join("|");
  const displayStats = useMemo(
    () => withTeamsStatOption(stats, teamsStatEnabled),
    [stats, teamsStatEnabled]
  );
  const visibleStatOptions = displayStats;
  const isEventMode = Boolean(selectedGameId && selectedGame);
  const eventStatOptions = useMemo(
    () => (isEventMode ? getEventStatOptions(selectedGame, stats, athletes) : {}),
    [isEventMode, selectedGame, stats, athletes]
  );
  const activeStatOptions = isEventMode ? eventStatOptions : visibleStatOptions;
  const visibleSelectedStat = isEventMode
    ? (stat || POPULAR_STAT_KEY)
    : stat;
  const boardNavigationView = selectBoardNavigationView(
    boardNavigationState,
    sport,
    visibleSelectedStat
  );
  const pendingBoardSport = boardNavigationView.pendingSport;
  const pendingBoardStat = boardNavigationView.pendingStat;
  const transitioningFromTopPlaysToLeague = boardNavigationView.topPlaysHeroExiting;
  const topPlaysHeroExiting = boardNavigationView.topPlaysHeroExiting;
  const enteredTopPlaysFromLeague = boardNavigationView.topPlaysHeroEntering;
  const navigationSelectedSport = boardNavigationView.selectedSport;
  const navigationSelectedStat = boardNavigationView.selectedStat;
  // Search and Top Plays intentionally mix leagues and stat families. Every
  // standard board must match both active tabs, though, so stale state/cache
  // commits cannot leave a prior league or market visible after switching.
  const visibleAthletes = useMemo(
    () => (
      searchMode || isTopPlaysSelected
        ? athletes
        : filterAthletesForRequestedStat(
            filterAthletesForRequestedSport(athletes, sport, TOP_PLAYS_SPORT),
            stat,
            POPULAR_STAT_KEY
          )
    ),
    [athletes, isTopPlaysSelected, searchMode, sport, stat]
  );
  // Pending cards use exactly the same component tree and keys as the verified
  // destination. Keep complete display data separate from committed board state.
  const hasCachedBoardPreview = Boolean(
    atomicBoardTransition && !isTopPlaysSport(pendingBoardSport) &&
    atomicBoardTransition.previewRows?.length
  );
  const cardAthletes = hasCachedBoardPreview ? atomicBoardTransition.previewRows : visibleAthletes;
  const cardSport = hasCachedBoardPreview ? pendingBoardSport : sport;
  const cardStat = hasCachedBoardPreview ? pendingBoardStat : stat;
  const cardEventMode = hasCachedBoardPreview ? Boolean(atomicBoardTransition.gameId) : isEventMode;
  const emptyAthleteSelectionKey = [
    normalizeSportKey(sport),
    normalizeProjectedStatKey(stat),
    String(selectedGameId || ""),
  ].join("|");
  const hasEmptyAthleteState =
    !loading &&
    !athletesLoading &&
    !isTeamsViewSelected &&
    (!visibleAthletes || (Array.isArray(visibleAthletes) && visibleAthletes.length === 0));
  const emptyAthleteStatePending =
    sportStatsLoading || emptyBoardRefreshKey === emptyAthleteSelectionKey;
  const hasResolvedEmptyAthleteState = hasEmptyAthleteState && !emptyAthleteStatePending;
  const emptyAthleteStateReady = emptyAthleteStateReadyKey === emptyAthleteSelectionKey;
  const shouldDeferEmptyAthleteState =
    hasEmptyAthleteState &&
    !searchMode &&
    (emptyAthleteStatePending || !emptyAthleteStateReady);
  const teamLineupDebugEnabled = isAdmin && (
    urlParams.get("team_debug") === "1" ||
    urlParams.get("debug") === "teams" ||
    urlParams.get("show_odds") === "1"
  );
  const hasOptimisticAthletePreview =
    athletesLoading &&
    Array.isArray(visibleAthletes) &&
    visibleAthletes.some((athlete) => athlete?._boardOptimisticPreview);

  useEffect(() => {
    if (!hasResolvedEmptyAthleteState || searchMode) {
      setEmptyAthleteStateReadyKey("");
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setEmptyAthleteStateReadyKey(emptyAthleteSelectionKey);
    }, EMPTY_ATHLETE_STATE_GRACE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [emptyAthleteSelectionKey, hasResolvedEmptyAthleteState, searchMode]);

  // Per-sport version tracking to trigger conditional refreshes
  const feedVersionRef = useRef({});
  const verifiedBoardVersionRef = useRef({});
  const feedCheckInFlightRef = useRef(false);
  const staleFeedRefreshAttemptRef = useRef({});
  const staleFeedRefreshInFlightRef = useRef({});
  const sportValidationTokenRef = useRef(0);
  const athleteRequestLanesRef = useRef(null);
  if (!athleteRequestLanesRef.current) {
    athleteRequestLanesRef.current = createBoardAthleteRequestLanes();
  }
  const teamLineupRequestControllerRef = useRef(null);
  const teamLineupValidationControllerRef = useRef(null);
  const teamLineupValidationTokenRef = useRef(0);
  const teamLineupNoticeRef = useRef(null);
  const teamsHubRequestControllerRef = useRef(null);
  const boardAthleteCacheRef = useRef(new Map());
  const recentVerifiedBoardCacheRef = useRef(null);
  if (!recentVerifiedBoardCacheRef.current) {
    recentVerifiedBoardCacheRef.current = createRecentVerifiedBoardCache();
  }
  const boardPrewarmRunRef = useRef(0);
  const boardVisualCacheRef = useRef(null);
  if (!boardVisualCacheRef.current) boardVisualCacheRef.current = createBoardVisualCache();
  const deferredBoardPersistenceRef = useRef(null);
  if (!deferredBoardPersistenceRef.current) {
    deferredBoardPersistenceRef.current = createDeferredBoardPersistence({
      maxEntries: PERSISTED_BOARD_CACHE_MAX_ENTRIES,
      write: (entries) => {
        const now = Date.now();
        const state = prunePersistedCacheEntries(readPersistedCacheState(PERSISTED_BOARD_CACHE_KEY), now);
        entries.forEach(([key, entry]) => { state.entries[key] = entry; });
        writePersistedCacheState(PERSISTED_BOARD_CACHE_KEY, prunePersistedCacheEntries(state, now));
      },
    });
  }
  const popularPrewarmContextRef = useRef("");
  const boardPrewarmInFlightRef = useRef(new Map());
  const boardPrewarmControllersRef = useRef(new Map());
  const boardPrewarmQueueRef = useRef(Promise.resolve());
  const boardRequestTokenRef = useRef(0);
  const boardHydrationStateRef = useRef(null);
  const boardHydrationTimerRef = useRef(null);
  const boardHydrationSentinelRef = useRef(null);
  const initialBoardLoadInFlightRef = useRef(false);
  const resumeRefreshInFlightRef = useRef(false);
  const resumeRefreshPendingRef = useRef(false);
  const resumeRefreshRetryTimerRef = useRef(null);

  const cancelInFlightAthleteRequest = () => {
    athleteRequestLanesRef.current.cancelAll();
  };

  const resetBoardHydration = () => {
    athleteRequestLanesRef.current.cancelHydration();
    if (boardHydrationTimerRef.current) {
      window.clearTimeout(boardHydrationTimerRef.current);
      boardHydrationTimerRef.current = null;
    }
    boardHydrationStateRef.current = null;
    setBoardHydrationHasMore(false);
  };

  const startBoardRequest = () => {
    finishBoardSelectionPerformance("abandoned", {
      reason: "selection-superseded-by-board-request",
    });
    pendingEventRef.current = "";
    setPendingEventKey("");
    atomicBoardTransitionRef.current = null;
    dispatchBoardNavigation({ type: "reset" });
    // Foreground work always wins. Abort speculative requests immediately so
    // they cannot occupy PHP-FPM workers while the user's selection loads.
    cancelBoardPrewarm();
    resetBoardHydration();
    boardRequestTokenRef.current += 1;
    setBoardLoadingMoreAthletes(false);
    cancelInFlightAthleteRequest();
    return boardRequestTokenRef.current;
  };

  const invalidateBoardRequest = () => {
    resetBoardHydration();
    boardRequestTokenRef.current += 1;
    setBoardLoadingMoreAthletes(false);
    cancelInFlightAthleteRequest();
  };

  const isBoardRequestCurrent = (requestToken) => requestToken === boardRequestTokenRef.current;

  const getRequestedAthleteLimit = (options = {}) =>
    Number.isFinite(Number(options.limit)) && Number(options.limit) > 0
      ? Math.floor(Number(options.limit))
      : 0;

  const getRequestedAthleteOffset = (options = {}) =>
    Number.isFinite(Number(options.offset)) && Number(options.offset) > 0
      ? Math.floor(Number(options.offset))
      : 0;

  const cancelTeamLineupRequest = () => {
    const controller = teamLineupRequestControllerRef.current;
    teamLineupRequestControllerRef.current = null;
    if (controller) {
      controller.abort();
    }
  };

  const cancelTeamLineupValidation = () => {
    teamLineupValidationTokenRef.current += 1;
    const controller = teamLineupValidationControllerRef.current;
    teamLineupValidationControllerRef.current = null;
    if (controller) {
      controller.abort();
    }
    if (teamLineupNoticeRef.current) {
      toastr.clear(teamLineupNoticeRef.current);
      teamLineupNoticeRef.current = null;
    }
  };

  const cancelTeamsHubRequest = () => {
    const controller = teamsHubRequestControllerRef.current;
    teamsHubRequestControllerRef.current = null;
    if (controller) {
      controller.abort();
    }
  };

  const getTeamLineupAthleteStartMs = (athlete) => {
    const startValue = athlete?.start_time_iso || athlete?.start_time_obj || athlete?.start_time || "";
    const parsed = parseBoardStartDate(startValue);
    return parsed ? parsed.getTime() : null;
  };

  const filterFutureTeamLineupPool = (pool = [], now = Date.now()) => (
    (Array.isArray(pool) ? pool : []).filter((athlete) => {
      const startMs = getTeamLineupAthleteStartMs(athlete);
      return startMs === null || startMs > now;
    })
  );

  const readTeamLineupPoolCache = () => {
    if (typeof window === "undefined" || !window.localStorage) {
      return { entries: {} };
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(TEAM_LINEUP_POOL_CACHE_KEY) || "{}");
      return parsed && typeof parsed === "object" && parsed.entries ? parsed : { entries: {} };
    } catch (error) {
      return { entries: {} };
    }
  };

  const writeTeamLineupPoolCache = (state) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(TEAM_LINEUP_POOL_CACHE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage may be unavailable in private mode; ignore and continue live fetches.
    }
  };

  const getTeamLineupPoolCacheKey = (sportKey, statKeys = [], gameId = false) => [
    sportKey,
    gameId || "all",
    [...statKeys].sort().join(","),
  ].join("|");

  const readTeamLineupQuoteCache = () => {
    if (typeof window === "undefined" || !window.localStorage) {
      return { entries: {} };
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(TEAM_LINEUP_QUOTE_CACHE_KEY) || "{}");
      return parsed && typeof parsed === "object" && parsed.entries ? parsed : { entries: {} };
    } catch (error) {
      return { entries: {} };
    }
  };

  const writeTeamLineupQuoteCache = (state) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(TEAM_LINEUP_QUOTE_CACHE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage may be unavailable in private mode; ignore and continue live quotes.
    }
  };

  const getTeamLineupOptionStartMs = (option) => {
    const parsed = parseBoardStartDate(option?.startTime || "");
    return parsed ? parsed.getTime() : null;
  };

  const getTeamLineupQuoteLineupKey = (lineup = []) => (
    (Array.isArray(lineup) ? lineup : []).map((athlete) => [
      getAthleteVariantKey(athlete),
      String(athlete?.selected || ""),
      String(athlete?.stat || ""),
    ].join(":")).join("|")
  );

  const getTeamLineupQuoteCacheKey = (option) => [
    option?.key || "",
    option?.startTime || "",
    option?.candidateLineups?.length || 0,
  ].join("|");

  const getCachedTeamLineupQuote = (option) => {
    const cacheKey = getTeamLineupQuoteCacheKey(option);
    if (!cacheKey || !option?.available) {
      return null;
    }

    const state = readTeamLineupQuoteCache();
    const entry = state.entries?.[cacheKey];
    const now = Date.now();
    const startMs = getTeamLineupOptionStartMs(option);

    if (
      !entry ||
      now - Number(entry.cachedAt || 0) > TEAM_LINEUP_POOL_CACHE_TTL_MS ||
      (startMs !== null && startMs <= now)
    ) {
      if (entry) {
        delete state.entries[cacheKey];
        writeTeamLineupQuoteCache(state);
      }
      return null;
    }

    const preview = (option.candidateLineups || []).find((lineup) => (
      getTeamLineupQuoteLineupKey(lineup) === entry.lineupKey
    ));

    if (!preview || !entry.multiplierLabel) {
      delete state.entries[cacheKey];
      writeTeamLineupQuoteCache(state);
      return null;
    }

    return {
      multiplierLabel: entry.multiplierLabel,
      preview,
      quote: entry.quote || null,
    };
  };

  const setCachedTeamLineupQuote = (option, lineup, multiplierLabel, quote) => {
    if (!option?.available || !multiplierLabel) {
      return;
    }

    const startMs = getTeamLineupOptionStartMs(option);
    if (startMs !== null && startMs <= Date.now()) {
      return;
    }

    const cacheKey = getTeamLineupQuoteCacheKey(option);
    const state = readTeamLineupQuoteCache();
    state.entries[cacheKey] = {
      cachedAt: Date.now(),
      lineupKey: getTeamLineupQuoteLineupKey(lineup),
      multiplierLabel,
      quote: quote ? {
        payout: quote.payout,
        multiplier: quote.multiplier,
      } : null,
    };
    writeTeamLineupQuoteCache(state);
  };

  const getCachedTeamLineupPool = (cacheKey) => {
    const state = readTeamLineupPoolCache();
    const entry = state.entries?.[cacheKey];
    const now = Date.now();

    if (!entry || now - Number(entry.cachedAt || 0) > TEAM_LINEUP_POOL_CACHE_TTL_MS) {
      if (entry) {
        delete state.entries[cacheKey];
        writeTeamLineupPoolCache(state);
      }
      return null;
    }

    const filteredPool = filterFutureTeamLineupPool(entry.pool, now);
    if (filteredPool.length === 0) {
      delete state.entries[cacheKey];
      writeTeamLineupPoolCache(state);
      return null;
    }

    if (filteredPool.length !== (Array.isArray(entry.pool) ? entry.pool.length : 0)) {
      state.entries[cacheKey] = { ...entry, pool: filteredPool };
      writeTeamLineupPoolCache(state);
    }

    return filteredPool;
  };

  const setCachedTeamLineupPool = (cacheKey, pool) => {
    const filteredPool = filterFutureTeamLineupPool(pool);
    if (filteredPool.length === 0) {
      const state = readTeamLineupPoolCache();
      if (state.entries?.[cacheKey]) {
        delete state.entries[cacheKey];
        writeTeamLineupPoolCache(state);
      }
      return;
    }

    const state = readTeamLineupPoolCache();
    state.entries[cacheKey] = {
      cachedAt: Date.now(),
      pool: filteredPool,
    };
    writeTeamLineupPoolCache(state);
  };

  const deleteCachedTeamLineupPool = (cacheKey) => {
    const state = readTeamLineupPoolCache();
    if (!state.entries?.[cacheKey]) {
      return;
    }

    delete state.entries[cacheKey];
    writeTeamLineupPoolCache(state);
  };

  const setTeamLineupSportAvailability = (sportKey, available) => {
    const normalized = String(sportKey || "").toLowerCase();
    if (!normalized || !isTeamsLineupSport(normalized)) {
      return;
    }

    setTeamLineupAvailableBySport((current) => {
      if (current[normalized] === Boolean(available)) {
        return current;
      }

      return {
        ...current,
        [normalized]: Boolean(available),
      };
    });
  };

  const updateTeamLineupAvailabilityFromResponse = (response, selectedSport = "") => {
    if (!response || !response.cached) {
      return;
    }

    const pools = response.pools && typeof response.pools === "object" ? response.pools : {};
    Object.entries(pools).forEach(([poolSport, poolData]) => {
      const available = Boolean(poolData?.availability?.available);
      setTeamLineupSportAvailability(poolSport, available);
    });

    const sportKey = String(selectedSport || "").toLowerCase();
    if (sportKey && isTeamsLineupSport(sportKey) && !Object.prototype.hasOwnProperty.call(pools, sportKey)) {
      setTeamLineupSportAvailability(sportKey, false);
    }
  };

  const fetchTeamLineupServerCache = async (
    selectedSport = "",
    gameId = false,
    signal = undefined,
    options = {}
  ) => {
    const params = new URLSearchParams();
    params.set("viewer", "shared");
    if (options.availabilityOnly) {
      params.set("availability_only", "1");
    }
    if (selectedSport) {
      params.set("sport", selectedSport);
    }
    if (gameId) {
      params.set("game_id", gameId);
    }

    const query = params.toString();
    const response = await fetch(
      `/wp-json/wagedwar/v1/team-lineups-cache${query ? `?${query}` : ""}`,
      { signal, credentials: "same-origin" }
    );

    if (!response.ok) {
      return null;
    }

    return response.json();
  };

  const waitForTeamLineupCache = (delayMs, signal = undefined) => new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      resolve(!signal?.aborted);
    }, delayMs);

    if (signal) {
      signal.addEventListener("abort", () => {
        window.clearTimeout(timeoutId);
        resolve(false);
      }, { once: true });
    }
  });

  const fetchTeamLineupServerCacheWithRebuild = async (
    selectedSport = "",
    gameId = false,
    signal = undefined,
    options = {}
  ) => {
    const timeoutMs = Number.isFinite(Number(options.timeoutMs)) ? Number(options.timeoutMs) : 90000;
    const pollMs = Number.isFinite(Number(options.pollMs)) ? Number(options.pollMs) : 2500;
    const startedAt = Date.now();
    let lastResponse = null;

    while (!signal?.aborted && Date.now() - startedAt <= timeoutMs) {
      lastResponse = await fetchTeamLineupServerCache(selectedSport, gameId, signal, options);
      if (signal?.aborted) {
        return lastResponse;
      }

      if (lastResponse?.cached) {
        return lastResponse;
      }

      const shouldContinue = await waitForTeamLineupCache(pollMs, signal);
      if (!shouldContinue) {
        return lastResponse;
      }
    }

    return lastResponse;
  };

  const fetchTeamLineupAvailability = async (selectedSport, signal = undefined) => {
    const sportKey = String(selectedSport || "").toLowerCase();
    if (!isTeamsLineupSport(sportKey)) {
      return null;
    }

    const response = await fetchTeamLineupServerCacheWithRebuild(sportKey, false, signal, {
      availabilityOnly: true,
      timeoutMs: 15000,
    });
    if (signal?.aborted || !response?.cached) {
      return null;
    }

    updateTeamLineupAvailabilityFromResponse(response, sportKey);
    return response?.pools?.[sportKey]?.availability?.available === true;
  };

  const formatTeamLineupMultiplier = (quote) => {
    const rawMultiplier = Number(quote?.multiplier);
    if (Number.isFinite(rawMultiplier) && rawMultiplier > 0) {
      return `${String(Number(rawMultiplier.toFixed(2))).replace(/\.0$/, "")}x`;
    }

    const payout = Number(quote?.payout);
    if (Number.isFinite(payout) && payout > 0) {
      return `${String(Number((payout / 5).toFixed(2))).replace(/\.0$/, "")}x`;
    }

    return "";
  };

  const getTeamLineupGroupCacheKey = (group) => `${group?.sport || ""}|${group?.key || ""}`;

  const sortTeamLineupGroups = (groups = []) => (
    [...(Array.isArray(groups) ? groups : [])].sort((left, right) => {
      const leftTs = Date.parse(left.startTime);
      const rightTs = Date.parse(right.startTime);
      if (Number.isFinite(leftTs) && Number.isFinite(rightTs) && leftTs !== rightTs) {
        return leftTs - rightTs;
      }

      const leftSport = String(left.sport || "");
      const rightSport = String(right.sport || "");
      if (leftSport !== rightSport) {
        return leftSport.localeCompare(rightSport);
      }

      return String(left.key).localeCompare(String(right.key));
    })
  );

  const mergeTeamLineupGroup = (groups = [], nextGroup) => {
    const nextKey = getTeamLineupGroupCacheKey(nextGroup);
    const withoutNext = (Array.isArray(groups) ? groups : []).filter((group) => (
      getTeamLineupGroupCacheKey(group) !== nextKey
    ));

    if (!nextGroup) {
      return sortTeamLineupGroups(withoutNext);
    }

    return sortTeamLineupGroups([...withoutNext, nextGroup]);
  };

  const overlayTeamLineupGroups = (baseGroups = [], overlayGroups = []) => {
    let nextGroups = Array.isArray(baseGroups) ? baseGroups : [];
    (Array.isArray(overlayGroups) ? overlayGroups : []).forEach((group) => {
      nextGroups = mergeTeamLineupGroup(nextGroups, group);
    });

    return sortTeamLineupGroups(nextGroups);
  };

  const getVisibleTeamLineupGroups = (baseGroups = [], quotedGroups = [], quotesReady = false) => {
    return overlayTeamLineupGroups(baseGroups, quotedGroups);
  };

  const teamLineupGroupsHaveAvailableOptions = (groups = []) => (
    (Array.isArray(groups) ? groups : []).some((group) => (
      (Array.isArray(group?.options) ? group.options : []).some((option) => option?.available)
    ))
  );

  const yieldTeamLineupRender = () => new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });

  const quoteTeamLineupOption = async (option, signal) => {
    if (!option?.available || !Array.isArray(option.candidateLineups)) {
      return option;
    }

    const cachedQuote = getCachedTeamLineupQuote(option);
    if (cachedQuote) {
      return {
        ...option,
        multiplierLabel: cachedQuote.multiplierLabel,
        preview: cachedQuote.preview,
        debug: {
          ...option.debug,
          selected: cachedQuote.preview.map((athlete) => ({
            name: athlete?.name,
            team: athlete?.team_short || athlete?.team,
            opponent: athlete?.opponent_short || athlete?.opponent,
            stat_key: athlete?.stat_key,
            stat: athlete?.stat,
            selected: athlete?.selected,
          })),
          quote: cachedQuote.quote,
          quoteCache: "hit",
        },
      };
    }

    const { default: PayoutCalculator } = await import(
      /* webpackChunkName: "sportV2-lineup-interactions" */ "./sidebar/PayoutCalculator.js"
    );
    const quoteFailures = [];
    for (const lineup of option.candidateLineups) {
      if (signal?.aborted) {
        return option;
      }

      let quote = null;
      try {
        quote = await PayoutCalculator(lineup, 5, null, null);
      } catch (error) {
        quoteFailures.push({
          reason: "quote_error",
          message: error?.message || String(error),
        });
        continue;
      }
      if (signal?.aborted) {
        return option;
      }

      const multiplierLabel = formatTeamLineupMultiplier(quote);
      if (
        quote &&
        Number(quote.payout) > 0 &&
        multiplierLabel &&
        !quote?.lineup_risk?.shouldBlock &&
        !quote?.correlation?.shouldBlock
      ) {
        setCachedTeamLineupQuote(option, lineup, multiplierLabel, quote);

        return {
          ...option,
          multiplierLabel,
          preview: lineup,
          debug: {
            ...option.debug,
            selected: lineup.map((athlete) => ({
              name: athlete?.name,
              team: athlete?.team_short || athlete?.team,
              opponent: athlete?.opponent_short || athlete?.opponent,
              stat_key: athlete?.stat_key,
              stat: athlete?.stat,
              selected: athlete?.selected,
            })),
            quote,
          },
        };
      }

      quoteFailures.push({
        reason: "quote_unavailable",
        quote,
      });
    }

    return {
      ...option,
      available: false,
      unavailableReason: "Currently Unavailable",
      debug: {
        ...option.debug,
        rejected: [...(option.debug?.rejected || []), ...quoteFailures],
      },
    };
  };

  const quoteTeamLineupGroup = async (group, signal) => {
    if (!group || signal?.aborted) {
      return group;
    }

    const options = await Promise.all(group.options.map((option) => quoteTeamLineupOption(option, signal)));
    return {
      ...group,
      options,
    };
  };

  const quoteTeamLineupGroups = async (groups = [], signal, onGroupQuoted = null) => {
    const quotedGroups = [];
    for (const group of groups) {
      if (signal?.aborted) {
        return groups;
      }

      const quotedGroup = await quoteTeamLineupGroup(group, signal);
      if (signal?.aborted) {
        return groups;
      }

      quotedGroups.push(quotedGroup);
      if (typeof onGroupQuoted === "function") {
        onGroupQuoted(quotedGroup);
      }
    }

    return sortTeamLineupGroups(quotedGroups);
  };

  const fetchTeamLineupAthletePoolData = async (
    selectedSport = sport,
    statsMap = stats,
    gameId = selectedGameId || false,
    signal = undefined,
    onPartialPool = null
  ) => {
    const sportKey = String(selectedSport || "").toLowerCase();
    if (!isTeamsLineupSport(sportKey) || maxAthletes < 4 || isH2H || teamsHiddenForTier) {
      return [];
    }

    const statKeys = getTeamLineupFetchStats(sportKey, statsMap);
    if (statKeys.length === 0) {
      return [];
    }

    const cacheKey = getTeamLineupPoolCacheKey(sportKey, statKeys, gameId || false);
    const cachedPool = getCachedTeamLineupPool(cacheKey);
    if (cachedPool) {
      const cachedGroups = await buildTeamLineupGroupsOnDemand(cachedPool, sportKey);
      if (cachedGroups.length > 0) {
        setTeamLineupSportAvailability(sportKey, true);
        return cachedPool;
      }

      // A pool may still contain future rows while no longer having enough
      // compatible offers to build a lineup. Discard that stale fragment and
      // continue to the authoritative server cache instead of stranding the
      // selected league on an empty Teams view for the client-cache TTL.
      deleteCachedTeamLineupPool(cacheKey);
    }

    const response = await fetchTeamLineupServerCacheWithRebuild(sportKey, gameId || false, signal);
    if (signal?.aborted || !response?.cached) {
      return [];
    }

    updateTeamLineupAvailabilityFromResponse(response, sportKey);
    const pool = response?.pools?.[sportKey]?.pool || [];
    const poolGroups = await buildTeamLineupGroupsOnDemand(pool, sportKey);
    const serverAvailable = response?.pools?.[sportKey]?.availability?.available !== false;
    setTeamLineupSportAvailability(
      sportKey,
      serverAvailable && poolGroups.length > 0
    );
    if (typeof onPartialPool === "function" && pool.length > 0) {
      onPartialPool(filterFutureTeamLineupPool(pool));
    }
    setCachedTeamLineupPool(cacheKey, pool);
    return filterFutureTeamLineupPool(pool);
  };

  const fetchTeamLineupAthletePool = async (selectedSport = sport, statsMap = stats, gameId = selectedGameId || false) => {
    const sportKey = String(selectedSport || "").toLowerCase();
    if (!isTeamsLineupSport(sportKey) || maxAthletes < 4 || isH2H || teamsHiddenForTier) {
      cancelTeamLineupRequest();
      setTeamLineupAthletes([]);
      setTeamLineupQuotedGroups([]);
      setTeamLineupQuotesReady(false);
      setTeamLineupLoading(false);
      return [];
    }

    const statKeys = getTeamLineupFetchStats(sportKey, statsMap);
    if (statKeys.length === 0) {
      cancelTeamLineupRequest();
      setTeamLineupAthletes([]);
      setTeamLineupQuotedGroups([]);
      setTeamLineupQuotesReady(false);
      setTeamLineupLoading(false);
      return [];
    }

    cancelTeamLineupRequest();
    const controller = new AbortController();
    teamLineupRequestControllerRef.current = controller;
    setTeamLineupLoading(true);

    try {
      const pool = await fetchTeamLineupAthletePoolData(sportKey, statsMap, gameId, controller.signal, (partialPool) => {
        if (controller.signal.aborted) {
          return;
        }

        setTeamLineupAthletes(partialPool);
        if (partialPool.length > 0) {
          setTeamLineupLoading(false);
        }
      });
      setTeamLineupAthletes(pool);
      return pool;
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error loading team lineups:", error);
      }
      setTeamLineupAthletes([]);
      setTeamLineupQuotedGroups([]);
      setTeamLineupQuotesReady(false);
      return [];
    } finally {
      if (teamLineupRequestControllerRef.current === controller) {
        teamLineupRequestControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setTeamLineupLoading(false);
      }
    }
  };

  const fetchTeamsHubLineups = async () => {
    if (!teamsHubEnabled) {
      setTeamsHubGroups([]);
      setTeamsHubQuotedGroups([]);
      setTeamsHubQuotesReady(false);
      setTeamsHubLoading(false);
      return [];
    }

    cancelTeamsHubRequest();
    const controller = new AbortController();
    teamsHubRequestControllerRef.current = controller;
    setTeamsHubLoading(true);
    setTeamsHubGroups([]);
    setTeamsHubQuotedGroups([]);
    setTeamsHubQuotesReady(false);

    try {
      const nextGroups = [];
      const response = await fetchTeamLineupServerCacheWithRebuild("", false, controller.signal);
      if (controller.signal.aborted || !response?.cached) {
        return [];
      }

      updateTeamLineupAvailabilityFromResponse(response);
      const pools = response.pools && typeof response.pools === "object" ? response.pools : {};
      for (const [candidateSport, poolData] of Object.entries(pools)) {
        if (controller.signal.aborted) {
          return [];
        }

        const pool = filterFutureTeamLineupPool(poolData?.pool || []);
        const hasServerAvailability = poolData?.availability?.available !== false;
        if (!isTeamsLineupSport(candidateSport) || !hasServerAvailability || pool.length === 0) {
          continue;
        }

        const sportGroups = await buildTeamLineupGroupsOnDemand(pool, candidateSport);
        setTeamLineupSportAvailability(candidateSport, sportGroups.length > 0);

        for (const group of sportGroups) {
          if (controller.signal.aborted) {
            return [];
          }

          nextGroups.push(group);
          setTeamsHubGroups(sortTeamLineupGroups(nextGroups));
          await yieldTeamLineupRender();
        }
      }

      const sortedGroups = sortTeamLineupGroups(nextGroups);
      setTeamsHubGroups(sortedGroups);
      return sortedGroups;
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error loading Teams view:", error);
      }
      setTeamsHubGroups([]);
      setTeamsHubQuotedGroups([]);
      setTeamsHubQuotesReady(false);
      return [];
    } finally {
      if (teamsHubRequestControllerRef.current === controller) {
        teamsHubRequestControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setTeamsHubLoading(false);
      }
    }
  };

  const readPersistedCacheState = (storageKey) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return { version: 1, entries: {} };
    }

    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || parsed.version !== 1 || !parsed.entries || typeof parsed.entries !== "object") {
        return { version: 1, entries: {} };
      }

      return parsed;
    } catch (e) {
      return { version: 1, entries: {} };
    }
  };

  const writePersistedCacheState = (storageKey, state) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) {
      const entries = Object.entries(state.entries || {})
        .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
        .slice(0, Math.max(8, Math.floor(PERSISTED_BOARD_CACHE_MAX_ENTRIES / 2)));

      try {
        window.localStorage.setItem(storageKey, JSON.stringify({ version: 1, entries: Object.fromEntries(entries) }));
      } catch (retryError) {
        // Storage quota/private-mode failures should not block board rendering.
      }
    }
  };

  const clearPersistedBoardCache = () => {
    deferredBoardPersistenceRef.current.clear();
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.removeItem(PERSISTED_BOARD_CACHE_KEY);
      window.localStorage.removeItem(PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY);
    } catch (e) {
      // no-op
    }
  };

  const clearSelectedStoragePressure = () => {
    clearPersistedBoardCache();

    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.removeItem(TEAM_LINEUP_POOL_CACHE_KEY);
      window.localStorage.removeItem(TEAM_LINEUP_QUOTE_CACHE_KEY);
    } catch (e) {
      // no-op
    }
  };

  const filterFreshCachedBoardRows = (rows, now = Date.now()) => {
    const list = Array.isArray(rows) ? rows : [];

    return list.filter((row) => {
      const startRaw = row?.start_time_iso || row?.start_time_obj || row?.start_time || "";
      const parsedStart = parseBoardStartDate(startRaw);
      const startMs = parsedStart ? parsedStart.getTime() : NaN;
      return Boolean(row?.is_live) || !Number.isFinite(startMs) || startMs > now;
    });
  };

  const cachedBoardRowsAreFresh = (rows, now = Date.now()) => {
    return filterFreshCachedBoardRows(rows, now).length > 0;
  };

  const getOptimisticAthletePreview = (selectedSport, selectedStat, statsMap = {}) => {
    const currentRows = Array.isArray(athletesRef.current) ? athletesRef.current : [];
    const seenRows = Array.from(seenBoardAthletesRef.current.values());
    const now = Date.now();
    const sportKey = normalizeSportKey(selectedSport);
    const shrk = String(getShrkValue() ?? "");
    const userTier = String(getCurrentUserTierValue());
    const showOdds = String(getCurrentShowOddsValue());
    const persistedState = readPersistedCacheState(PERSISTED_BOARD_CACHE_KEY);
    const persistedRows = Object.entries(persistedState.entries || {})
      .filter(([entryKey, entry]) => {
        const keyParts = String(entryKey).split("|");
        const cachedAt = Number(entry?.cachedAt || 0);
        return keyParts.length >= 9
          && keyParts[0] === sportKey
          && keyParts[5] === shrk
          && keyParts[6] === userTier
          && keyParts[7] === showOdds
          && cachedAt > 0
          && now - cachedAt <= PERSISTED_BOARD_STALE_PREVIEW_TTL_MS;
      })
      .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
      .flatMap(([, entry]) => (
        Array.isArray(entry?.list) ? entry.list : (Array.isArray(entry?.rawList) ? entry.rawList : [])
      ));
    const candidates = filterFreshCachedBoardRows([...currentRows, ...seenRows, ...persistedRows], now);

    return buildAthleteSelectionPreview(
      candidates,
      selectedSport,
      selectedStat,
      statsMap,
      INITIAL_BOARD_ATHLETE_LIMIT
    );
  };

  const prunePersistedCacheEntries = (state, now = Date.now()) => {
    const entries = Object.entries(state.entries || {})
      .filter(([, entry]) => {
        const cachedAt = Number(entry?.cachedAt || 0);
        return cachedAt > 0
          && now - cachedAt <= PERSISTED_BOARD_STALE_PREVIEW_TTL_MS
          && cachedBoardRowsAreFresh(entry?.list || entry?.rawList, now);
      })
      .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
      .slice(0, PERSISTED_BOARD_CACHE_MAX_ENTRIES);

    return { version: 1, entries: Object.fromEntries(entries) };
  };

  const getBoardAthleteCacheKey = (
    selectedSport,
    selectedStat,
    gameId = false,
    requestedLimit = 0,
    requestedOffset = 0
  ) => {
    const sportKey = String(selectedSport || "").toLowerCase();
    const statKey = selectedStat ? normalizeProjectedStatKey(selectedStat) : "";
    const feedVersion = feedVersionRef.current?.[sportKey]?.version || "0";
    const shrk = String(getShrkValue() ?? "");
    const showOdds = getCurrentShowOddsValue();

    return [
      sportKey,
      statKey,
      String(gameId || ""),
      String(requestedLimit || 0),
      String(requestedOffset || 0),
      shrk,
      showOdds,
      feedVersion,
    ].join("|");
  };

  const getPersistedBoardAthleteCacheKey = (
    selectedSport,
    selectedStat,
    gameId = false,
    requestedLimit = 0,
    requestedOffset = 0
  ) => {
    const sportKey = String(selectedSport || "").toLowerCase();
    const statKey = selectedStat ? normalizeProjectedStatKey(selectedStat) : "";
    const shrk = String(getShrkValue() ?? "");
    const showOdds = getCurrentShowOddsValue();
    const userTier = getCurrentUserTierValue();
    const feedVersion = feedVersionRef.current?.[sportKey]?.version || "0";

    return [
      sportKey,
      statKey,
      String(gameId || ""),
      String(requestedLimit || 0),
      String(requestedOffset || 0),
      shrk,
      userTier,
      showOdds,
      feedVersion,
    ].join("|");
  };

  const getRecentVerifiedBoardKey = (selectedSport, selectedStat, gameId, limit, offset = 0) => {
    // A server-verified response is useful before the first metadata poll too.
    // The key still includes the observed version (or 0); later polls invalidate it.
    if (boardCacheBypassParam) return "";
    return JSON.stringify([getBoardAthleteCacheKey(selectedSport, selectedStat, gameId, limit, offset),
      getCurrentUserTierValue(), getCurrentRestNonce(), document.body.classList.contains("logged-in")]);
  };

  const getBoardVisualKey = (selectedSport, selectedStat, gameId = false) => boardCacheBypassParam ? "" : JSON.stringify([
    normalizeSportKey(selectedSport), normalizeProjectedStatKey(selectedStat), String(gameId || ""),
    getCurrentUserTierValue(), getCurrentRestNonce(), String(getShrkValue() ?? ""),
    getCurrentShowOddsValue(), document.body.classList.contains("logged-in"),
  ]);

  const cloneCachedAthleteResult = (
    entry,
    requestedLimit = 0,
    requestedOffset = 0,
    selectedSport = "",
    selectedStat = ""
  ) => {
    const now = Date.now();
    const rawList = filterFreshCachedBoardRows(Array.isArray(entry?.rawList) ? entry.rawList : [], now);
    const list = filterAthletesForRequestedStat(
      filterAthletesForRequestedSport(
        filterAthleteList(
          filterFreshCachedBoardRows(Array.isArray(entry?.list) ? entry.list : rawList, now)
        ),
        selectedSport,
        TOP_PLAYS_SPORT
      ),
      selectedStat,
      POPULAR_STAT_KEY
    );
    const sliceRows = (rows) => {
      if (requestedLimit <= 0) {
        return requestedOffset > 0 ? rows.slice(requestedOffset) : rows;
      }

      return rows.slice(requestedOffset, requestedOffset + requestedLimit);
    };

    return {
      aborted: false,
      cached: true,
      rawList: sliceRows(rawList),
      list: sliceRows(list),
    };
  };

  const pruneBoardAthleteCache = (now = Date.now()) => {
    const cache = boardAthleteCacheRef.current;
    cache.forEach((entry, key) => {
      if (!entry || now - entry.cachedAt > BOARD_ATHLETE_CACHE_TTL_MS) {
        cache.delete(key);
      }
    });

    while (cache.size > BOARD_ATHLETE_CACHE_MAX_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      if (!oldestKey) {
        break;
      }
      cache.delete(oldestKey);
    }
  };

  const getCachedBoardAthleteResult = (
    selectedSport,
    selectedStat,
    gameId = false,
    requestedLimit = 0,
    requestedOffset = 0
  ) => {
    if (boardCacheBypassParam) {
      return null;
    }

    const now = Date.now();
    pruneBoardAthleteCache(now);

    const cache = boardAthleteCacheRef.current;
    const cacheKey = getBoardAthleteCacheKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset);
    const cached = cache.get(cacheKey);
    if (cached && now - cached.cachedAt <= BOARD_ATHLETE_CACHE_TTL_MS) {
      return cloneCachedAthleteResult(cached, requestedLimit, 0, selectedSport, selectedStat);
    }

    if (requestedLimit > 0) {
      const fullCacheKey = getBoardAthleteCacheKey(selectedSport, selectedStat, gameId, 0, 0);
      const fullCached = cache.get(fullCacheKey);
      if (fullCached && now - fullCached.cachedAt <= BOARD_ATHLETE_CACHE_TTL_MS) {
        return cloneCachedAthleteResult(fullCached, requestedLimit, requestedOffset, selectedSport, selectedStat);
      }
    }

    const persistedState = prunePersistedCacheEntries(readPersistedCacheState(PERSISTED_BOARD_CACHE_KEY), now);
    const persistedKey = getPersistedBoardAthleteCacheKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset);
    let persisted = persistedState.entries[persistedKey];

    if (!persisted && requestedLimit > 0) {
      const fullPersistedKey = getPersistedBoardAthleteCacheKey(selectedSport, selectedStat, gameId, 0, 0);
      persisted = persistedState.entries[fullPersistedKey];
    }

    if (persisted && now - persisted.cachedAt <= PERSISTED_BOARD_CACHE_TTL_MS) {
      const rawList = filterFreshCachedBoardRows(Array.isArray(persisted.rawList) ? persisted.rawList : [], now);
      const list = filterAthleteList(filterFreshCachedBoardRows(Array.isArray(persisted.list) ? persisted.list : rawList, now));
      if (list.length > 0 && cachedBoardRowsAreFresh(list, now)) {
        const memoryEntry = {
          cachedAt: now,
          rawList,
          list,
        };
        cache.set(cacheKey, memoryEntry);
        pruneBoardAthleteCache(now);
        writePersistedCacheState(PERSISTED_BOARD_CACHE_KEY, persistedState);
        return cloneCachedAthleteResult(
          memoryEntry,
          requestedLimit,
          persisted === persistedState.entries[persistedKey] ? 0 : requestedOffset,
          selectedSport,
          selectedStat
        );
      }
    }

    writePersistedCacheState(PERSISTED_BOARD_CACHE_KEY, persistedState);
    return null;
  };

  const setCachedBoardAthleteResult = (
    selectedSport,
    selectedStat,
    gameId = false,
    requestedLimit = 0,
    requestedOffset = 0,
    athleteResult = {}
  ) => {
    if (!Array.isArray(athleteResult.list)) {
      return;
    }

    if (athleteResult.list.length === 0) {
      return;
    }

    // Disabled previews can bridge a slow refresh, but they must never become
    // an authoritative in-memory or persisted board response.
    if (athleteResult.list.some((athlete) => athlete?._boardOptimisticPreview)) {
      return;
    }

    const now = Date.now();
    const cacheKey = getBoardAthleteCacheKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset);
    if (!requestedOffset && !athleteResult.cached && !athleteResult.failed && !athleteResult.aborted) {
      boardVisualCacheRef.current.set(getBoardVisualKey(selectedSport, selectedStat, gameId), athleteResult.list);
    }
    boardAthleteCacheRef.current.set(cacheKey, {
      cachedAt: now,
      rawList: Array.isArray(athleteResult.rawList) ? athleteResult.rawList : athleteResult.list,
      list: athleteResult.list,
    });

    const persistedKey = getPersistedBoardAthleteCacheKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset);
    deferredBoardPersistenceRef.current.enqueue(persistedKey, {
      cachedAt: now,
      rawList: Array.isArray(athleteResult.rawList) ? athleteResult.rawList : athleteResult.list,
      list: athleteResult.list,
    });

    pruneBoardAthleteCache(now);
  };

  const canPrewarmBoardAthletes = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return false;
    }

    if (document.visibilityState === "hidden") {
      return false;
    }

    // The native wrapper has a hard network deadline and keeps its WebView
    // alive between sessions. Speculative PHP requests can compete with its
    // foreground bootstrap and make the wrapper incorrectly declare offline.
    if (typeof navigator !== "undefined" && /\bmedian\b/i.test(navigator.userAgent || "")) {
      return false;
    }

    const connection = typeof navigator !== "undefined" ? navigator.connection : null;
    const effectiveType = String(connection?.effectiveType || "").toLowerCase();
    return !connection?.saveData && effectiveType !== "slow-2g" && effectiveType !== "2g";
  };

  const getBoardPopularPrewarmTarget = (sportKey, statsMapBySport) => {
    const normalizedSport = String(sportKey || "").toLowerCase();
    const statsLookup = statsMapBySport && typeof statsMapBySport === "object" ? statsMapBySport : {};
    const sportStats = statsLookup[normalizedSport];
    if (!normalizedSport || isTopPlaysSport(normalizedSport) || !sportStats) {
      return null;
    }

    const popularStat = Object.keys(sportStats).find(
      (statKey) => normalizeProjectedStatKey(statKey) === POPULAR_STAT_KEY
    );
    if (!popularStat) {
      return null;
    }

    return {
      sport: normalizedSport,
      stat: popularStat,
      limit: getInitialAthleteLimit(popularStat),
    };
  };

  const prewarmBoardAthleteTarget = (target, runId = boardPrewarmRunRef.current) => {
    if (!target || !canPrewarmBoardAthletes()) {
      return Promise.resolve(null);
    }

    const cachedResult = recentVerifiedBoardCacheRef.current.get(getRecentVerifiedBoardKey(target.sport, target.stat, false, target.limit));
    if (cachedResult) {
      return Promise.resolve(cachedResult);
    }

    const cacheKey = getBoardAthleteCacheKey(target.sport, target.stat, false, target.limit, 0);
    const inFlight = boardPrewarmInFlightRef.current.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request = boardPrewarmQueueRef.current
      .catch(() => null)
      .then(async () => {
        if (runId !== boardPrewarmRunRef.current || !canPrewarmBoardAthletes()) {
          return null;
        }

        const queuedCachedResult = recentVerifiedBoardCacheRef.current.get(
          getRecentVerifiedBoardKey(target.sport, target.stat, false, target.limit)
        );
        if (queuedCachedResult) {
          return queuedCachedResult;
        }

        const controller = new AbortController();
        const verificationStartedAt = Date.now();
        const verificationKey = getRecentVerifiedBoardKey(target.sport, target.stat, false, target.limit);
        boardPrewarmControllersRef.current.set(cacheKey, controller);
        let response;
        try {
          response = await FetchAthletes(target.sport, target.stat, false, {
            limit: target.limit,
            signal: controller.signal,
            forceFresh: true,
          });
        } finally {
          if (boardPrewarmControllersRef.current.get(cacheKey) === controller) {
            boardPrewarmControllersRef.current.delete(cacheKey);
          }
        }

        if (runId !== boardPrewarmRunRef.current || response?.aborted || response?.timeout || response?.error) {
          return null;
        }
        if (verificationKey !== getRecentVerifiedBoardKey(target.sport, target.stat, false, target.limit)) {
          return null;
        }

        const rawList = Array.isArray(response) ? response : (response?.data || []);
        const filteredList = filterAthletesForRequestedStat(filterAthletesForRequestedSport(
          filterAthleteList(filterFreshCachedBoardRows(rawList)), target.sport, TOP_PLAYS_SPORT
        ), target.stat, POPULAR_STAT_KEY);
        if (filteredList.length === 0) {
          // A speculative miss must not mutate the league list. Doing so
          // reschedules the multi-league prewarm effect and can cascade through
          // every empty league. Foreground selection validation remains the
          // authority for removing a truly unavailable league.
          return { aborted: false, rawList: [], list: [] };
        }

        const athleteResult = {
          aborted: false,
          rawList,
          list: filteredList,
        };
        setCachedBoardAthleteResult(target.sport, target.stat, false, target.limit, 0, athleteResult);
        if (verificationKey === getRecentVerifiedBoardKey(target.sport, target.stat, false, target.limit)) {
          recentVerifiedBoardCacheRef.current.set(verificationKey, athleteResult, verificationStartedAt, normalizeSportKey(target.sport));
        }
        return athleteResult;
      })
      .catch(() => {
        // Prewarming is opportunistic. A miss should never affect the visible board.
        return null;
      })
      .finally(() => {
        if (boardPrewarmInFlightRef.current.get(cacheKey) === request) {
          boardPrewarmInFlightRef.current.delete(cacheKey);
        }
      });

    boardPrewarmInFlightRef.current.set(cacheKey, request);
    boardPrewarmQueueRef.current = request;
    return request;
  };

  const cancelBoardPrewarm = () => {
    boardPrewarmRunRef.current += 1;
    boardPrewarmControllersRef.current.forEach((controller) => controller.abort());
    boardPrewarmControllersRef.current.clear();
    boardPrewarmQueueRef.current = Promise.resolve();
  };

  const prioritizeSuperPacks = useCallback(() => {
    cancelBoardPrewarm();
    setPopularPrewarmComplete(true);
  }, []);

  const prefetchBoardPopularSport = (sportKey) => {
    if (!canPrewarmBoardAthletes()) {
      return;
    }

    const target = getBoardPopularPrewarmTarget(sportKey, verifiedStats);
    if (target) {
      void prewarmBoardAthleteTarget(target);
    }
  };

  const clearBoardAthleteCache = (changedSport = "") => {
    if (!changedSport) boardVisualCacheRef.current.clear();
    recentVerifiedBoardCacheRef.current.clear(normalizeSportKey(changedSport));
    boardAthleteCacheRef.current.clear();
    clearPersistedBoardCache();
    cancelBoardPrewarm();
  };

  useEffect(() => {
    if (!isTopPlaysSelected || loading || athletesLoading || !athletes.length || !canPrewarmBoardAthletes()) return undefined;
    const targets = sports.map(key => getBoardPopularPrewarmTarget(key, verifiedStats)).filter(Boolean).slice(0, 2);
    if (!targets.length) return undefined;
    const context = getBoardVisualKey("topplays", POPULAR_STAT_KEY);
    if (!context || popularPrewarmContextRef.current === context) return undefined;
    const timer = window.setTimeout(() => {
      if (athleteRequestLanesRef.current.hasForeground() || atomicBoardTransitionRef.current) return;
      popularPrewarmContextRef.current = context;
      // At most two, serialized, after the visible board settles. Foreground
      // navigation cancels both queued and active work; never retry in a loop.
      targets.forEach(target => { void prewarmBoardAthleteTarget(target); });
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [isTopPlaysSelected, loading, athletesLoading, athletes, sports, verifiedStats]);

  const getPersistedBootstrapCacheKey = (requestedSport = "") => [
    String(requestedSport || "").toLowerCase(),
    String(getShrkValue() ?? ""),
    getCurrentUserTierValue(),
    getCurrentShowOddsValue(),
  ].join("|");

  const getCachedBoardBootstrapPayload = (requestedSport = "") => {
    if (boardCacheBypassParam) {
      return null;
    }

    const now = Date.now();
    const state = readPersistedCacheState(PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY);
    const cacheKey = getPersistedBootstrapCacheKey(requestedSport);
    const entry = state.entries?.[cacheKey];

    if (!entry || now - Number(entry.cachedAt || 0) > PERSISTED_BOARD_CACHE_TTL_MS) {
      return null;
    }

    const payload = entry.payload;
    if (!payload || typeof payload !== "object" || !Array.isArray(payload.athletes)) {
      return null;
    }

    if (!cachedBoardRowsAreFresh(payload.athletes, now)) {
      return null;
    }

    return {
      ...payload,
      athletes: filterFreshCachedBoardRows(payload.athletes, now),
    };
  };

  // League/stat metadata remains useful much longer than athlete rows. During
  // a transient PHP backlog, use the last successful map to keep every league
  // selectable while fresh data recovers in the background.
  const getCachedBoardBootstrapMetadata = (requestedSport = "") => {
    if (boardCacheBypassParam) {
      return null;
    }

    const now = Date.now();
    const metadataState = readPersistedCacheState(PERSISTED_BOARD_METADATA_CACHE_KEY);
    const legacyState = readPersistedCacheState(PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY);
    const state = {
      entries: {
        ...(legacyState.entries || {}),
        ...(metadataState.entries || {}),
      },
    };
    const cacheKey = getPersistedBootstrapCacheKey(requestedSport);
    const expectedSuffix = cacheKey.slice(cacheKey.indexOf("|"));
    const entries = Object.entries(state.entries || {})
      .filter(([entryKey, entry]) => (
        (entryKey === cacheKey || entryKey.endsWith(expectedSuffix)) &&
        now - Number(entry?.cachedAt || 0) <= PERSISTED_BOARD_BOOTSTRAP_METADATA_TTL_MS
      ))
      .sort(([leftKey, left], [rightKey, right]) => {
        if (leftKey === cacheKey && rightKey !== cacheKey) return -1;
        if (rightKey === cacheKey && leftKey !== cacheKey) return 1;
        return Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0);
      });
    const payload = entries[0]?.[1]?.payload;
    const availableStats = payload?.availableStats;

    if (!availableStats || typeof availableStats !== "object" || Object.keys(availableStats).length === 0) {
      return null;
    }

    return {
      availableStats,
      sports: Array.isArray(payload.sports) ? payload.sports : Object.keys(availableStats),
    };
  };

  const setCachedBoardBootstrapMetadata = (requestedSport = "", availableStats = null, sports = []) => {
    if (
      boardCacheBypassParam ||
      !availableStats ||
      typeof availableStats !== "object" ||
      Object.keys(availableStats).length === 0
    ) {
      return;
    }

    const now = Date.now();
    const state = readPersistedCacheState(PERSISTED_BOARD_METADATA_CACHE_KEY);
    const cacheKey = getPersistedBootstrapCacheKey(requestedSport);
    const entries = Object.entries(state.entries || {})
      .filter(([, entry]) => now - Number(entry?.cachedAt || 0) <= PERSISTED_BOARD_BOOTSTRAP_METADATA_TTL_MS)
      .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
      .slice(0, Math.max(0, PERSISTED_BOARD_CACHE_MAX_ENTRIES - 1));
    const nextEntries = Object.fromEntries(entries);

    nextEntries[cacheKey] = {
      cachedAt: now,
      payload: {
        availableStats,
        sports: Array.isArray(sports) && sports.length > 0 ? sports : Object.keys(availableStats),
      },
    };

    writePersistedCacheState(PERSISTED_BOARD_METADATA_CACHE_KEY, {
      version: 1,
      entries: nextEntries,
    });
  };

  const setCachedBoardBootstrapPayload = (requestedSport = "", payload = null) => {
    if (payload && typeof payload === "object") {
      setCachedBoardBootstrapMetadata(requestedSport, payload.availableStats, payload.sports);
    }

    if (!payload || typeof payload !== "object" || !Array.isArray(payload.athletes) || payload.athletes.length === 0) {
      return;
    }

    if (payload.athletes.some((athlete) => athlete?._boardOptimisticPreview)) {
      return;
    }

    const now = Date.now();
    const state = readPersistedCacheState(PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY);
    const cacheKey = getPersistedBootstrapCacheKey(requestedSport);
    const entries = Object.entries(state.entries || {})
      .filter(([, entry]) => now - Number(entry?.cachedAt || 0) <= PERSISTED_BOARD_CACHE_TTL_MS)
      .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
      .slice(0, Math.max(0, PERSISTED_BOARD_CACHE_MAX_ENTRIES - 1));

    const nextEntries = Object.fromEntries(entries);
    nextEntries[cacheKey] = {
      cachedAt: now,
      payload,
    };

    writePersistedCacheState(PERSISTED_BOARD_BOOTSTRAP_CACHE_KEY, {
      version: 1,
      entries: nextEntries,
    });
  };

  const getInitialAthleteLimit = (selectedStat, options = {}) => {
    if (options.visibleLimit === false) {
      return 0;
    }

    return INITIAL_BOARD_ATHLETE_LIMIT;
  };

  const shouldHydrateBoardAthleteResult = (athleteResult, requestedLimit) => (
    requestedLimit > 0 &&
    Array.isArray(athleteResult?.rawList) &&
    athleteResult.rawList.length >= requestedLimit
  );

  const getAutoSwitchEmptyStatLimit = (statsMap) => {
    const statCount = Object.keys(statsMap && typeof statsMap === "object" ? statsMap : {}).length;
    if (statCount < 1) {
      return 0;
    }

    return Math.min(MAX_AUTO_SWITCH_EMPTY_STATS_PER_SPORT, statCount);
  };

  const shouldSkipRemainingStatsForSport = (attemptedStats, statsMap) => {
    const limit = getAutoSwitchEmptyStatLimit(statsMap);
    return limit > 0 && attemptedStats.length >= limit;
  };

  const getNextUnattemptedStat = (statsMap, currentStat, attemptedStats = []) => {
    const statKeys = Object.keys(statsMap && typeof statsMap === "object" ? statsMap : {})
      .filter((statKey) => statKey !== TEAMS_STAT_KEY);
    if (statKeys.length === 0) {
      return "";
    }

    const currentIndex = statKeys.indexOf(currentStat);
    const orderedStats =
      currentIndex >= 0
        ? [...statKeys.slice(currentIndex + 1), ...statKeys.slice(0, currentIndex)]
        : statKeys;

    const preferredNextStat = orderedStats.find(
      (statKey) => statKey !== POPULAR_STAT_KEY && !attemptedStats.includes(statKey)
    );
    if (preferredNextStat) {
      return preferredNextStat;
    }

    for (let i = 0; i < orderedStats.length; i++) {
      if (!attemptedStats.includes(orderedStats[i])) {
        return orderedStats[i];
      }
    }

    return "";
  };

  const getTeamLineupFallbackStat = (statsMap) => (
    getNextUnattemptedStat(statsMap, POPULAR_STAT_KEY, [TEAMS_STAT_KEY]) ||
    Object.keys(statsMap && typeof statsMap === "object" ? statsMap : {}).find((key) => key !== TEAMS_STAT_KEY) ||
    ""
  );

  const getNextSportSelection = (availableSports, attemptedSports, statsMapBySport) => {
    const statsLookup = statsMapBySport && typeof statsMapBySport === "object" ? statsMapBySport : {};

    for (const nextSport of Array.isArray(availableSports) ? availableSports : []) {
      if (attemptedSports.includes(nextSport)) {
        continue;
      }

      const nextStats = statsLookup[nextSport] || {};
      const firstNextStat = Object.keys(nextStats)[0] || "";
      if (!firstNextStat) {
        continue;
      }

      return {
        sport: nextSport,
        stats: nextStats,
        stat: firstNextStat,
      };
    }

    return null;
  };

  const removeTopPlaysFromStatsMap = (statsMapBySport = {}) => {
    const nextStatsMap = { ...(statsMapBySport && typeof statsMapBySport === "object" ? statsMapBySport : {}) };
    delete nextStatsMap[TOP_PLAYS_SPORT];
    return nextStatsMap;
  };

  const getTopPlaysFallbackSelection = (availableSports, statsMapBySport) => {
    const statsLookup = statsMapBySport && typeof statsMapBySport === "object" ? statsMapBySport : {};
    const fallbackStatsLookup = removeTopPlaysFromStatsMap(statsLookup);
    const orderedSports = Array.isArray(availableSports) && availableSports.length > 0
      ? availableSports
      : Object.keys(fallbackStatsLookup);
    const fallbackSports = orderedSports.filter((sportKey) => sportKey && !isTopPlaysSport(sportKey) && fallbackStatsLookup[sportKey]);
    const fallbackSelection = getNextSportSelection(fallbackSports, [], fallbackStatsLookup);

    if (!fallbackSelection) {
      return null;
    }

    return {
      ...fallbackSelection,
      sports: fallbackSports,
      verifiedStats: fallbackStatsLookup,
    };
  };

  const loadTopPlaysFallback = async ({
    requestToken = boardRequestTokenRef.current,
    availableSports = sports,
    statsMapBySport = verifiedStats,
  } = {}) => {
    const fallbackSelection = getTopPlaysFallbackSelection(availableSports, statsMapBySport);

    if (!fallbackSelection || !isBoardRequestCurrent(requestToken)) {
      return false;
    }

    setVerifiedStats(fallbackSelection.verifiedStats);
    setSports(fallbackSelection.sports);
    setSelectedGameId("");
    setSport(fallbackSelection.sport);
    setStats(fallbackSelection.stats);
    setStat(fallbackSelection.stat);
    setAthletesLoading(true);

    await getAthletes(
      fallbackSelection.sport,
      fallbackSelection.stat,
      [],
      [TOP_PLAYS_SPORT],
      false,
      true,
      fallbackSelection.stats,
      {
        requestToken,
        gameId: false,
        availableSports: fallbackSelection.sports,
        statsMapBySport: fallbackSelection.verifiedStats,
        preserveAvailableStatsOnEmpty: true,
      }
    );

    return true;
  };

  const retryTopPlaysAthleteLoad = async (
    requestToken = boardRequestTokenRef.current,
    selectedStat = POPULAR_STAT_KEY
  ) => {
    let lastResult = { aborted: false, rawList: [], list: [] };

    for (const delayMs of TOP_PLAYS_EMPTY_RETRY_DELAYS_MS) {
      if (!isBoardRequestCurrent(requestToken)) {
        return { aborted: true, rawList: [], list: [] };
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));

      if (!isBoardRequestCurrent(requestToken)) {
        return { aborted: true, rawList: [], list: [] };
      }

      lastResult = await fetchBoardAthletes(
        TOP_PLAYS_SPORT,
        selectedStat || POPULAR_STAT_KEY,
        false,
        requestToken,
        { cache: false }
      );

      if (lastResult.aborted || !isBoardRequestCurrent(requestToken)) {
        return lastResult;
      }

      if (Array.isArray(lastResult.list) && lastResult.list.length > 0) {
        return lastResult;
      }
    }

    return lastResult;
  };

  const recoverEmptyTopPlays = async ({
    requestToken = boardRequestTokenRef.current,
    availableSports = sports,
    statsMapBySport = verifiedStats,
    selectedStat = POPULAR_STAT_KEY,
    allowFallback = true,
  } = {}) => {
    const retryResult = await retryTopPlaysAthleteLoad(requestToken, selectedStat);
    if (retryResult.aborted || !isBoardRequestCurrent(requestToken)) {
      return true;
    }

    if (Array.isArray(retryResult.list) && retryResult.list.length > 0) {
      const topPlaysStats = statsMapBySport?.[TOP_PLAYS_SPORT] || { [POPULAR_STAT_KEY]: "TOP PICKS" };
      setLoading(false);
      setVerifiedStats(statsMapBySport);
      setSports(availableSports);
      setSelectedGameId("");
      setSport(TOP_PLAYS_SPORT);
      setStats(topPlaysStats);
      setStat(selectedStat || POPULAR_STAT_KEY);
      setAthletes(retryResult.list);
      setAthletesLoading(false);
      return true;
    }

    if (allowFallback) {
      return loadTopPlaysFallback({
        requestToken,
        availableSports,
        statsMapBySport,
      });
    }

    return false;
  };

  useEffect(() => {
    const hasEmptyTopPlaysBoard =
      isTopPlaysSelected &&
      !loading &&
      !athletesLoading &&
      Array.isArray(athletes) &&
      athletes.length === 0;

    if (!hasEmptyTopPlaysBoard) {
      if (!isTopPlaysSelected || (Array.isArray(athletes) && athletes.length > 0)) {
        topPlaysEmptyFallbackAttemptRef.current = "";
      }
      return;
    }

    const fallbackAttemptKey = [
      sports.join("|"),
      Object.keys(verifiedStats && typeof verifiedStats === "object" ? verifiedStats : {}).join("|"),
    ].join("::");

    if (topPlaysEmptyFallbackAttemptRef.current === fallbackAttemptKey) {
      return;
    }

    topPlaysEmptyFallbackAttemptRef.current = fallbackAttemptKey;
    const requestToken = startBoardRequest();
    setAthletesLoading(true);

    void recoverEmptyTopPlays({ requestToken, allowFallback: false }).then((recovered) => {
      if (!recovered && isBoardRequestCurrent(requestToken)) {
        setAthletesLoading(false);
      }
    });
  }, [athletes, athletesLoading, isTopPlaysSelected, loading, sports, verifiedStats]);

  const fetchBoardAthletes = async (
    selectedSport,
    selectedStat,
    gameId = false,
    requestToken = boardRequestTokenRef.current,
    options = {}
  ) => {
    if (!isBoardRequestCurrent(requestToken)) {
      return { aborted: true, rawList: [], list: [] };
    }

    const requestedLimit = getRequestedAthleteLimit(options);
    const requestedOffset = getRequestedAthleteOffset(options);
    if (options.cache !== false) {
      const cachedResult = getCachedBoardAthleteResult(
        selectedSport,
        selectedStat,
        gameId,
        requestedLimit,
        requestedOffset
      );
      if (cachedResult) {
        return cachedResult;
      }

      const prewarmCacheKey = getBoardAthleteCacheKey(
        selectedSport,
        selectedStat,
        gameId,
        requestedLimit,
        requestedOffset
      );
      const prewarmRequest = boardPrewarmInFlightRef.current.get(prewarmCacheKey);
      if (prewarmRequest) {
        // A visible league switch must never wait indefinitely for speculative
        // background work. This is especially important in iOS WebViews, where
        // an aborted fetch can occasionally remain pending.
        const prewarmedResult = await settleBoardRequestWithin(prewarmRequest, {
          timeoutMs: BOARD_PREWARM_FOREGROUND_WAIT_MS,
          timeoutValue: null,
        });
        if (!isBoardRequestCurrent(requestToken)) {
          return { aborted: true, rawList: [], list: [] };
        }
        if (prewarmedResult && Array.isArray(prewarmedResult.list) && prewarmedResult.list.length > 0) {
          return prewarmedResult;
        }
      }
    }

    const requestLanes = athleteRequestLanesRef.current;
    const verificationStartedAt = Date.now();
    const verificationKey = getRecentVerifiedBoardKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset);
    const laneRequest = requestLanes.begin(options.requestLane);
    const controller = laneRequest.controller;

    let response;
    try {
      if (!(await laneRequest.ready) || controller.signal.aborted || !isBoardRequestCurrent(requestToken)) {
        return { aborted: true, rawList: [], list: [] };
      }
      response = Array.isArray(options.initialResponse) ? options.initialResponse : await FetchAthletes(selectedSport, selectedStat, gameId, {
        signal: controller.signal,
        limit: requestedLimit,
        offset: requestedOffset,
        forceFresh: options.cache === false,
      });
    } finally {
      requestLanes.finish(laneRequest);
    }

    if (controller.signal.aborted || !isBoardRequestCurrent(requestToken) || response?.aborted) {
      return { aborted: true, rawList: [], list: [] };
    }

    let fetchFailed = false;
    if (response && response.timeout) {
	  fetchFailed = true;
      if (!timeoutNotifiedRef.current) {
        timeoutNotifiedRef.current = true;
        timeoutNotifiedRefGlobal.current = true;
      }
      response = response.data || [];
    } else if (response && response.error) {
	  fetchFailed = true;
      response = response.data || [];
    }

    const sourceRawList = Array.isArray(response) ? response : (response?.data || []);
    const rawList = filterFreshCachedBoardRows(sourceRawList);
    const availabilityFilteredList = filterAthleteList(rawList);
    const sportFilteredList = filterAthletesForRequestedSport(
      availabilityFilteredList,
      selectedSport,
      TOP_PLAYS_SPORT
    );
    const filteredList = filterAthletesForRequestedStat(
      sportFilteredList,
      selectedStat,
      POPULAR_STAT_KEY
    );

    if (availabilityFilteredList.length !== sportFilteredList.length) {
      void reportBoardIssue("athletes-league-mismatch", {
        sport: selectedSport,
        stat: selectedStat,
        gameId,
        requestedLimit,
        requestedOffset,
        rawCount: rawList.length,
        filteredCount: sportFilteredList.length,
        responseLeagues: Array.from(new Set(
          availabilityFilteredList.map((athlete) => normalizeSportKey(athlete?.league)).filter(Boolean)
        )),
      });
    }

    if (sportFilteredList.length !== filteredList.length) {
      void reportBoardIssue("athletes-stat-mismatch", {
        sport: selectedSport,
        stat: selectedStat,
        gameId,
        requestedLimit,
        requestedOffset,
        rawCount: rawList.length,
        filteredCount: filteredList.length,
        responseStats: Array.from(new Set(
          sportFilteredList.map((athlete) => normalizeProjectedStatKey(athlete?.stat_key)).filter(Boolean)
        )),
      });
    }

    if (rawList.length > 0 && filteredList.length === 0) {
      void reportBoardIssue("athletes-filtered-empty", {
        sport: selectedSport,
        stat: selectedStat,
        gameId,
        requestedLimit,
        requestedOffset,
        rawCount: rawList.length,
        filteredCount: filteredList.length,
        athletesCount: filteredList.length,
      });
    }

    const athleteResult = {
      aborted: false,
	  failed: fetchFailed,
      rawList,
      list: filteredList,
    };

    if (!fetchFailed && !requestedOffset && filteredList.length === 0) {
      boardVisualCacheRef.current.set(getBoardVisualKey(selectedSport, selectedStat, gameId), []);
    }

    // A composed response began before metadata resolved: do not restart the
    // short verified-return freshness clock when consuming its embedded rows.
    if (!Array.isArray(options.initialResponse) && options.cache === false && verificationKey && verificationKey ===
      getRecentVerifiedBoardKey(selectedSport, selectedStat, gameId, requestedLimit, requestedOffset)) {
      recentVerifiedBoardCacheRef.current.set(verificationKey, athleteResult, verificationStartedAt, normalizeSportKey(selectedSport));
      // The feed watcher requests an unsliced board. Seed the first-page key
      // as well, otherwise its fresh result cannot satisfy a 15-card stat return.
      const initialLimit = getInitialAthleteLimit(selectedStat);
      if (!requestedLimit && !requestedOffset && initialLimit > 0) {
        recentVerifiedBoardCacheRef.current.set(
          getRecentVerifiedBoardKey(selectedSport, selectedStat, gameId, initialLimit),
          { ...athleteResult, list: filteredList.slice(0, initialLimit), rawList: rawList.slice(0, initialLimit) },
          verificationStartedAt, normalizeSportKey(selectedSport)
        );
      }
    }

    // Force-fresh bypasses reads, not writes: a successfully verified response
    // should be available when returning to this stat. Cache reads still apply
    // viewer/version keys and row-freshness checks; failures never replace it.
    const shouldCacheAthleteResult =
      !fetchFailed && (!isTopPlaysSport(selectedSport) || filteredList.length > 0);

    if (shouldCacheAthleteResult) {
      setCachedBoardAthleteResult(
        selectedSport,
        selectedStat,
        gameId,
        requestedLimit,
        requestedOffset,
        athleteResult
      );
    }

    return athleteResult;
  };

  const fetchBoardEventStats = async (
    selectedSport,
    gameId,
    statsMap,
    requestToken = boardRequestTokenRef.current,
    options = {}
  ) => {
    const eventStatKeys = getEventStatKeys(statsMap);
    const combined = [];
    let failed = false;
    const seen = new Set();
    const targetCount =
      Number.isFinite(Number(options.targetCount)) && Number(options.targetCount) > 0
        ? Math.floor(Number(options.targetCount))
        : 0;
    const perStatLimit =
      Number.isFinite(Number(options.perStatLimit)) && Number(options.perStatLimit) > 0
        ? Math.floor(Number(options.perStatLimit))
        : (targetCount > 0 ? EVENT_BOARD_INITIAL_ATHLETE_LIMIT : 0);
    const skipStats = new Set(
      (Array.isArray(options.skipStats) ? options.skipStats : [])
        .map((statKey) => normalizeProjectedStatKey(statKey))
        .filter(Boolean)
    );

    const addAthletes = (athleteList) => {
      (Array.isArray(athleteList) ? athleteList : []).forEach((athlete) => {
        const key = getAthleteVariantKey(athlete);
        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        combined.push(athlete);
      });
    };

    addAthletes(options.seedAthletes);

    if (targetCount > 0 && combined.length >= targetCount) {
      const sorted = sortEventAthleteStats(combined, eventStatKeys).slice(0, targetCount);
      return {
        aborted: false,
        partial: true,
        rawList: sorted,
        list: sorted,
      };
    }

    const requestedMaxStats = Number(options.maxStats);
    const maxStats = Number.isFinite(requestedMaxStats) && requestedMaxStats > 0
      ? Math.floor(requestedMaxStats)
      : 0;
    const pendingEventStats = eventStatKeys.filter(
      (eventStat) => !skipStats.has(normalizeProjectedStatKey(eventStat))
    ).slice(0, maxStats || undefined);

    // fetchBoardAthletes intentionally owns one AbortController at a time.
    // Concurrent event-stat requests would abort their previous sibling, and
    // some mobile WebViews can leave that aborted promise pending. Fetch the
    // first advertised market for paint, then let the bounded background
    // hydrator collect the remaining markets. Event cards are built from
    // currently available offers, so scanning every league market here only
    // turns a stale/closed event into a long chain of empty requests.
    for (const eventStat of pendingEventStats) {
      if (!isBoardRequestCurrent(requestToken)) {
        return { aborted: true, rawList: [], list: [] };
      }

      const athleteResult = await fetchBoardAthletes(
        selectedSport,
        eventStat,
        gameId,
        requestToken,
        {
          limit: perStatLimit,
          cache: options.cache,
        }
      );

      if (athleteResult.aborted || !isBoardRequestCurrent(requestToken)) {
        return { aborted: true, rawList: [], list: [] };
      }

      addAthletes(athleteResult.list);
      failed = failed || athleteResult.failed === true;

      if (
        combined.length > 0 &&
        (options.returnOnFirstResult === true || (targetCount > 0 && combined.length >= targetCount))
      ) {
        const sorted = sortEventAthleteStats(combined, eventStatKeys);
        const visible = targetCount > 0 ? sorted.slice(0, targetCount) : sorted;
        return {
          aborted: false,
          partial: true,
          rawList: visible,
          list: visible,
        };
      }
    }

    const sorted = sortEventAthleteStats(combined, eventStatKeys);

    return {
      aborted: false,
      partial: false,
      failed,
      rawList: sorted,
      list: targetCount > 0 ? sorted.slice(0, targetCount) : sorted,
    };
  };

  const hydrateBoardEventStats = async (selectedSport, gameId, statsMap, requestToken) => {
    const eventStatKeys = getEventStatKeys(statsMap);
    if (eventStatKeys.length === 0) {
      return;
    }

    setBoardLoadingMoreAthletes(true);

    try {
      let offset = 0;
      let hasMore = true;

      while (
        hasMore &&
        offset <= EVENT_BOARD_HYDRATION_MAX_OFFSET &&
        isBoardRequestCurrent(requestToken)
      ) {
        hasMore = false;

        for (const eventStat of eventStatKeys) {
          if (!isBoardRequestCurrent(requestToken)) {
            return;
          }

          const athleteResult = await fetchBoardAthletes(
            selectedSport,
            eventStat,
            gameId,
            requestToken,
            {
              limit: EVENT_BOARD_HYDRATION_CHUNK_SIZE,
              offset,
              requestLane: "hydration",
            }
          );

          if (athleteResult.aborted || !isBoardRequestCurrent(requestToken)) {
            return;
          }

          if (athleteResult.list.length > 0) {
            setAthletes((currentAthletes) => appendAthleteList(currentAthletes, athleteResult.list));
          }

          if (athleteResult.rawList.length >= EVENT_BOARD_HYDRATION_CHUNK_SIZE) {
            hasMore = true;
          }
        }

        offset += EVENT_BOARD_HYDRATION_CHUNK_SIZE;
      }
    } finally {
      if (isBoardRequestCurrent(requestToken)) {
        setBoardLoadingMoreAthletes(false);
      }
    }
  };

  const scheduleBoardEventStatsHydration = (selectedSport, gameId, statsMap, requestToken) => {
    const runHydration = () => {
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }

      void hydrateBoardEventStats(selectedSport, gameId, statsMap, requestToken);
    };
    window.setTimeout(runHydration, BOARD_ATHLETE_HYDRATION_DELAY_MS);
  };

  const areAltStatsEqual = (left, right) => {
    const leftStats = left && typeof left === "object" ? left : {};
    const rightStats = right && typeof right === "object" ? right : {};
    const leftKeys = Object.keys(leftStats);
    const rightKeys = Object.keys(rightStats);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    for (const key of leftKeys) {
      if (!Object.prototype.hasOwnProperty.call(rightStats, key) || leftStats[key] !== rightStats[key]) {
        return false;
      }
    }

    return true;
  };

  const areBoardOddsEqual = (left, right) => {
    try {
      return JSON.stringify(left || null) === JSON.stringify(right || null);
    } catch (error) {
      return left === right;
    }
  };

  const areAthletesEquivalent = (left, right) => {
    const stableLeft = stripBoardRefreshMeta(left);
    const stableRight = stripBoardRefreshMeta(right);

    if (!stableLeft || !stableRight) {
      return false;
    }

    if (!left || !right) {
      return false;
    }

    return (
      stableLeft.athlete_id === stableRight.athlete_id &&
      stableLeft.stat_id === stableRight.stat_id &&
      stableLeft.term_id === stableRight.term_id &&
      stableLeft.uid === stableRight.uid &&
      stableLeft.name === stableRight.name &&
      stableLeft.image === stableRight.image &&
      stableLeft.stat_display === stableRight.stat_display &&
      stableLeft.stat_key === stableRight.stat_key &&
      stableLeft.stat === stableRight.stat &&
      stableLeft.pp_stat === stableRight.pp_stat &&
      stableLeft.team === stableRight.team &&
      stableLeft.team_color === stableRight.team_color &&
      stableLeft.team_color_two === stableRight.team_color_two &&
      stableLeft.team_logo === stableRight.team_logo &&
      stableLeft.opponent_logo === stableRight.opponent_logo &&
      stableLeft.game_id === stableRight.game_id &&
      stableLeft.team_short === stableRight.team_short &&
      stableLeft.opponent_short === stableRight.opponent_short &&
      stableLeft.start_time === stableRight.start_time &&
      stableLeft.start_time_iso === stableRight.start_time_iso &&
      stableLeft.start_time_obj === stableRight.start_time_obj &&
      stableLeft.result_time === stableRight.result_time &&
      stableLeft.position === stableRight.position &&
      stableLeft.location === stableRight.location &&
      stableLeft.league === stableRight.league &&
      stableLeft.promo === stableRight.promo &&
      stableLeft.easy === stableRight.easy &&
      stableLeft.hard === stableRight.hard &&
      stableLeft.og_value === stableRight.og_value &&
		String(stableLeft.current_stat ?? "") === String(stableRight.current_stat ?? "") &&
		Boolean(stableLeft.is_live) === Boolean(stableRight.is_live) &&
      stableLeft.src === stableRight.src &&
      stableLeft.last_updated === stableRight.last_updated &&
      stableLeft.lineup === stableRight.lineup &&
      areBoardOddsEqual(stableLeft.ud_odds, stableRight.ud_odds) &&
      areAltStatsEqual(stableLeft.alt_stats, stableRight.alt_stats)
    );
  };

  const mergeAthleteList = (currentAthletes, nextAthletes) => {
    if (!Array.isArray(nextAthletes)) {
      return Array.isArray(currentAthletes) ? currentAthletes : [];
    }

    const refreshToken = Date.now();
    const currentMap = new Map(
      (Array.isArray(currentAthletes) ? currentAthletes : []).map((athlete) => [
        getAthleteVariantKey(athlete),
        athlete,
      ])
    );

    // A live refresh is authoritative about membership and values, not visual
    // position. Retain surviving card positions and append genuinely new offers.
    const nextMap = new Map(nextAthletes.map((athlete) => [getAthleteVariantKey(athlete), athlete]));
    const orderedNext = [];
    currentMap.forEach((athlete, key) => {
      if (nextMap.has(key)) {
        orderedNext.push(nextMap.get(key));
        nextMap.delete(key);
      }
    });
    nextMap.forEach((athlete) => orderedNext.push(athlete));
    let changed = !Array.isArray(currentAthletes) || currentAthletes.length !== orderedNext.length;
    const merged = orderedNext.map((athlete) => {
      const key = getAthleteVariantKey(athlete);
      const existing = currentMap.get(key);
      if (existing && areAthletesEquivalent(existing, athlete)) {
        return existing;
      }

      changed = true;
      if (existing) {
        const stableExisting = stripBoardRefreshMeta(existing);
        const lineChanged =
          String(stableExisting?.stat ?? "") !== String(athlete?.stat ?? "") ||
          String(stableExisting?.og_value ?? "") !== String(athlete?.og_value ?? "") ||
          String(stableExisting?.stat_display ?? "") !== String(athlete?.stat_display ?? "");

        if (lineChanged) {
          return {
            ...athlete,
            _boardRefreshState: "changed",
            _boardRefreshToken: refreshToken,
            _boardPreviousStat: stableExisting?.stat ?? null,
          };
        }
      }

      return athlete;
    });

    return changed ? merged : currentAthletes;
  };

  const appendAthleteList = (currentAthletes, nextAthletes) => {
    if (!Array.isArray(nextAthletes) || nextAthletes.length === 0) {
      return Array.isArray(currentAthletes) ? currentAthletes : [];
    }

    const currentList = Array.isArray(currentAthletes) ? currentAthletes : [];
    const refreshToken = Date.now();
    const nextByKey = new Map(nextAthletes.map((athlete) => [getAthleteVariantKey(athlete), athlete]));
    const seenKeys = new Set();
    let changed = false;

    const merged = currentList.map((athlete) => {
      const key = getAthleteVariantKey(athlete);
      seenKeys.add(key);

      if (!nextByKey.has(key)) {
        return athlete;
      }

      const nextAthlete = nextByKey.get(key);
      if (areAthletesEquivalent(athlete, nextAthlete)) {
        return athlete;
      }

      changed = true;
      const stableExisting = stripBoardRefreshMeta(athlete);
      const lineChanged =
        String(stableExisting?.stat ?? "") !== String(nextAthlete?.stat ?? "") ||
        String(stableExisting?.og_value ?? "") !== String(nextAthlete?.og_value ?? "") ||
        String(stableExisting?.stat_display ?? "") !== String(nextAthlete?.stat_display ?? "");

      if (!lineChanged) {
        return nextAthlete;
      }

      return {
        ...nextAthlete,
        _boardRefreshState: "changed",
        _boardRefreshToken: refreshToken,
        _boardPreviousStat: stableExisting?.stat ?? null,
      };
    });

    nextAthletes.forEach((athlete) => {
      const key = getAthleteVariantKey(athlete);
      if (seenKeys.has(key)) {
        return;
      }

      seenKeys.add(key);
      changed = true;
      merged.push(athlete);
    });

    return changed ? merged : currentAthletes;
  };

  const syncSelectedLineStatuses = useCallback(async (selectedEntries = selectedRef.current) => {
	// Logged-out lineups are previews only. Do not spend an admin-ajax request
	// checking live line changes or attach warning metadata that desktop/mobile
	// lineup views could render before the customer signs in.
	if (!loggedIn) {
		return;
	}

    const sanitizedSelected = Array.isArray(selectedEntries)
      ? selectedEntries
          .map((athlete) => normalizeSelectedAthlete(athlete))
          .map((athlete) => stripSelectionSyncMeta(stripBoardRefreshMeta(athlete)))
      : [];

    if (sanitizedSelected.length === 0) {
      return;
    }

    const requestSignature = JSON.stringify(sanitizedSelected);
    if (selectedLineSyncRetrySignatureRef.current !== requestSignature) {
      selectedLineSyncRetrySignatureRef.current = requestSignature;
      selectedLineSyncDeferredRetryRef.current = 0;
    }
    if (selectedLineSyncActiveSignatureRef.current) {
      selectedLineSyncQueuedRef.current =
        selectedLineSyncActiveSignatureRef.current !== requestSignature;
      return;
    }

    const refreshTickets = selectedLineSyncRefreshTicketsRef.current;
    const hasRefreshTickets = Object.keys(refreshTickets).length > 0;
    if (selectedLineSyncCompletedSignatureRef.current === requestSignature && !hasRefreshTickets) {
      return;
    }

    selectedLineSyncActiveSignatureRef.current = requestSignature;
    const requestId = selectedLineSyncRequestRef.current + 1;
    selectedLineSyncRequestRef.current = requestId;
    const requestedSelectionLookup = new Map(
      sanitizedSelected.map((selection) => [getAthleteVariantKey(selection), selection])
    );
    let shouldRetryUncertainSelection = false;

    try {
      const formData = new FormData();
      formData.append("action", "sync_selected_athletes");
      formData.append("athletes", JSON.stringify(sanitizedSelected));
      if (hasRefreshTickets) {
        formData.append("refresh_tickets", JSON.stringify(refreshTickets));
      }

      const shrkValue = getShrkValue();
      if (shrkValue !== undefined && shrkValue !== null) {
        formData.append("shrk", shrkValue);
      }

      const response = await fetch(window.ajaxurl || "/wp-admin/admin-ajax.php", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      });

      const payload = await response.json();
      if (requestId !== selectedLineSyncRequestRef.current) {
        return;
      }

      selectedLineSyncCompletedSignatureRef.current = requestSignature;
      const items = Array.isArray(payload?.data?.items) ? payload.data.items : [];
      if (items.length === 0) {
        return;
      }
      shouldRetryUncertainSelection = items.some((item) => (
        item?.status === "uncertain" &&
        ["temporary_feed_uncertainty", "athlete_data_mapping_failure"].includes(item?.classification)
      ));

      const nextRefreshTickets = {};
      let nextRefreshDeadline = 0;
      items.forEach((item) => {
        const league = String(item?.league || "").trim().toLowerCase();
		const source = String(item?.refresh_source || item?.validation_source || "").trim().toLowerCase();
		const dependency = String(item?.refresh_dependency || (source && league ? `${source}|${league}` : league)).trim();
        const ticket = String(item?.refresh_ticket || "").trim();
        if (dependency && ticket && item?.refresh_state === "pending") {
          nextRefreshTickets[dependency] = ticket;
          const startedAt = Number(item?.refresh_started_at || 0) * 1000;
          const timeoutMs = Number(item?.refresh_timeout_seconds || 0) * 1000;
          if (startedAt > 0 && timeoutMs > 0) {
            nextRefreshDeadline = Math.max(nextRefreshDeadline, startedAt + timeoutMs + 5000);
          }
        }
      });
      selectedLineSyncRefreshTicketsRef.current = nextRefreshTickets;
      selectedLineSyncRefreshDeadlineRef.current = Object.keys(nextRefreshTickets).length > 0
        ? nextRefreshDeadline || Date.now() + 30000
        : 0;

      const syncLookup = new Map(
        items.map((item) => [getAthleteVariantKey(item), item])
      );
      const removedSelectionKeys = new Set(
        items
          .filter(isConfirmedUnavailableSelection)
          .map((item) => getAthleteVariantKey(item))
      );
      const removedSelections = selectedRef.current.filter((entry) => {
        const selectionKey = getAthleteVariantKey(entry);
		const syncedItem = syncLookup.get(selectionKey);
        return removedSelectionKeys.has(selectionKey) && selectionMatchesSyncRequest(
          entry,
          requestedSelectionLookup.get(selectionKey)
		) && !(
		  entry?._selectionSyncStatus === syncedItem?.status &&
		  entry?._selectionSyncClassification === syncedItem?.classification
		);
      });

      setSelected((currentSelected) => {
        let changed = false;
        const nextSelected = currentSelected.reduce((entries, entry) => {
          const normalizedEntry = stripSelectionSyncMeta(entry);
          const localBoardState = getLocalBoardSelectionStatus(normalizedEntry);
          const selectionKey = getAthleteVariantKey(entry);
          const requestedSelection = requestedSelectionLookup.get(selectionKey);
          const syncedItem = syncLookup.get(selectionKey);
          const syncItem = syncedItem && selectionMatchesSyncRequest(entry, requestedSelection)
            ? syncedItem
            : localBoardState;

          if (syncItem?.status === "ok") {
            const currentGameId = String(syncItem?.current_game_id || "").trim();
            const eventIdentityChanged =
              currentGameId && currentGameId !== String(entry?.game_id || "").trim();
            if (!entry?._selectionSyncStatus && !eventIdentityChanged) {
              entries.push(entry);
              return entries;
            }

            changed = true;
            entries.push(eventIdentityChanged
              ? { ...normalizedEntry, game_id: currentGameId }
              : normalizedEntry);
            return entries;
          }

          if (!syncItem) {
            entries.push(entry);
            return entries;
          }

          if (isConfirmedUnavailableSelection(syncItem)) {
            changed = true;
            return entries;
          }

          if (syncItem.status === "uncertain") {
            const refreshState = String(syncItem.refresh_state || "");
            if (
              entry?._selectionSyncStatus === "uncertain" &&
              entry?._selectionSyncClassification === syncItem.classification &&
              entry?._selectionSyncRefreshState === refreshState
            ) {
              entries.push(entry);
              return entries;
            }

            changed = true;
            entries.push({
              ...normalizedEntry,
              _selectionSyncStatus: "uncertain",
              _selectionSyncClassification: syncItem.classification || "temporary_feed_uncertainty",
              _selectionSyncReason: syncItem.removal_reason || "",
              _selectionSyncRefreshState: refreshState,
              _selectionSyncRefreshStartedAt: Number(syncItem.refresh_started_at || 0),
              _selectionSyncRefreshTimeout: Number(syncItem.refresh_timeout_seconds || 0),
            });
            return entries;
          }

          if (syncItem.status === "removed") {
            if (
              entry?._selectionSyncStatus === syncItem.status &&
              entry?._selectionSyncClassification === syncItem.classification
            ) {
              entries.push(entry);
              return entries;
            }

			changed = true;
			entries.push({
			  ...normalizedEntry,
			  _selectionSyncStatus: syncItem.status,
			  _selectionSyncClassification: syncItem.classification || "",
			  _selectionSyncReason: syncItem.removal_reason || "",
			});
			return entries;
          }

          if (syncItem.status === "changed") {
            if (
              entry?._selectionSyncStatus === "changed" &&
              String(entry?._selectionCurrentStat ?? "") === String(syncItem.current_stat ?? "") &&
              String(entry?._selectionCurrentStatDisplay ?? "") === String(syncItem.current_stat_display ?? "")
            ) {
              entries.push(entry);
              return entries;
            }

            changed = true;
            entries.push({
              ...normalizedEntry,
              _selectionSyncStatus: "changed",
              _selectionCurrentStat: syncItem.current_stat ?? null,
              _selectionCurrentStatDisplay: syncItem.current_stat_display ?? normalizedEntry.stat_display ?? "",
			  _selectionSyncClassification: syncItem.classification || "line_changed",
            });
            return entries;
          }

          if (!entry?._selectionSyncStatus) {
            entries.push(entry);
            return entries;
          }

          changed = true;
          entries.push(normalizedEntry);
          return entries;
        }, []);

        return changed ? nextSelected : currentSelected;
      });

      setAthletes((currentAthletes) => (
        applyChangedSelectionLinesToBoard(currentAthletes, items, getAthleteVariantKey)
      ));

      if (removedSelections.length > 0) {
        const removedKeys = new Set(removedSelections.map((selection) => getAthleteVariantKey(selection)));
        const removedItems = items.filter((item) => (
          isConfirmedUnavailableSelection(item) && removedKeys.has(getAthleteVariantKey(item))
        ));
        removedKeys.forEach((selectionKey) => seenBoardAthletesRef.current.delete(selectionKey));
        setAthletes((currentAthletes) => (
          Array.isArray(currentAthletes)
            ? currentAthletes.filter((athlete) => !removedKeys.has(getAthleteVariantKey(athlete)))
            : currentAthletes
        ));

        const removedLineNotice = getRemovedLineNotice(removedSelections);
        toastr["warning"](removedLineNotice, "Line unavailable");
		logBoardSelectionRemoval(removedSelections, removedItems, removedLineNotice, payload?.data?.attempt_id || "");

        try {
          setEntryAvailabilityState(removedSelections);
        } catch (error) {
          console.error("Error saving unavailable board selections:", error);
        }

        // Remove the stale card immediately, then bypass every board cache so
        // a customer cannot select the same unavailable display in a loop.
        clearBoardAthleteCache();
        const refreshToken = startBoardRequest();
        const refreshedBoard = await fetchBoardAthletes(
          sport,
          stat,
          selectedGameId || false,
          refreshToken,
          { cache: false }
        );
        if (
          !refreshedBoard.aborted &&
          !refreshedBoard.failed &&
          isBoardRequestCurrent(refreshToken) &&
          Array.isArray(refreshedBoard.list)
        ) {
          const reconciledBoard = refreshedBoard.list.filter(
            (athlete) => !removedKeys.has(getAthleteVariantKey(athlete))
          );
          setAthletes((currentAthletes) => mergeAthleteList(currentAthletes, reconciledBoard));
        }
      }
    } catch (error) {
      console.error("Error syncing selected line statuses:", error);
    } finally {
      if (selectedLineSyncActiveSignatureRef.current === requestSignature) {
        selectedLineSyncActiveSignatureRef.current = "";
      }
      if (selectedLineSyncQueuedRef.current) {
        selectedLineSyncQueuedRef.current = false;
        window.setTimeout(() => {
          void syncSelectedLineStatuses(selectedRef.current);
        }, 0);
      } else if (
        Object.keys(selectedLineSyncRefreshTicketsRef.current).length > 0 &&
        Date.now() < selectedLineSyncRefreshDeadlineRef.current
      ) {
        if (selectedLineSyncRefreshTimerRef.current) {
          window.clearTimeout(selectedLineSyncRefreshTimerRef.current);
        }
        selectedLineSyncCompletedSignatureRef.current = "";
        selectedLineSyncRefreshTimerRef.current = window.setTimeout(() => {
          selectedLineSyncRefreshTimerRef.current = null;
          void syncSelectedLineStatuses(selectedRef.current);
        }, 2000);
      } else if (Date.now() >= selectedLineSyncRefreshDeadlineRef.current) {
        selectedLineSyncRefreshTicketsRef.current = {};
        selectedLineSyncRefreshDeadlineRef.current = 0;
        if (
          shouldRetryUncertainSelection &&
          selectedRef.current.length > 0 &&
          selectedLineSyncDeferredRetryRef.current < 1
        ) {
          // A timed-out provider pass is uncertainty, not a terminal answer. Do
          // one later reconciliation after the demand scheduler has had time to
          // publish. Never keep a card in an endless polling/status cycle.
          selectedLineSyncDeferredRetryRef.current += 1;
          selectedLineSyncCompletedSignatureRef.current = "";
          selectedLineSyncRefreshTimerRef.current = window.setTimeout(() => {
            selectedLineSyncRefreshTimerRef.current = null;
            void syncSelectedLineStatuses(selectedRef.current);
          }, 30000);
        }
      }
    }
  }, [getLocalBoardSelectionStatus, selectedGameId, sport, stat]);

  const applyLineValidationIssues = useCallback((issues = []) => {
    if (!Array.isArray(issues) || issues.length === 0) {
      return;
    }

    const issueLookup = new Map(issues.map((issue) => [getAthleteVariantKey(issue), issue]));
    const confirmedUnavailableKeys = new Set(
      issues
        .filter(isConfirmedUnavailableSelection)
        .map((issue) => getAthleteVariantKey(issue))
    );
    const confirmedUnavailableSelections = selectedRef.current.filter((selection) => (
      confirmedUnavailableKeys.has(getAthleteVariantKey(selection))
    ));

    setSelected((currentSelected) => currentSelected.reduce((selections, selection) => {
      const issue = issueLookup.get(getAthleteVariantKey(selection));
      if (!issue) {
        selections.push(selection);
        return selections;
      }

      if (isConfirmedUnavailableSelection(issue)) {
        return selections;
      }

      selections.push({
        ...stripSelectionSyncMeta(selection),
        _selectionSyncStatus: issue.classification === "line_changed" ? "changed" : (issue.status || "removed"),
        _selectionCurrentStat: issue.new_line ?? null,
        _selectionCurrentStatDisplay: issue.stat_display || selection.stat_display || "",
        _selectionSyncClassification: issue.classification || "",
        _selectionSyncReason: issue.removal_reason || "",
      });
      return selections;
    }, []));

    if (confirmedUnavailableSelections.length > 0) {
      confirmedUnavailableKeys.forEach((selectionKey) => seenBoardAthletesRef.current.delete(selectionKey));
      setAthletes((currentAthletes) => (
        Array.isArray(currentAthletes)
          ? currentAthletes.filter((athlete) => !confirmedUnavailableKeys.has(getAthleteVariantKey(athlete)))
          : currentAthletes
      ));

      try {
        setEntryAvailabilityState(confirmedUnavailableSelections);
      } catch (error) {
        console.error("Error saving unavailable board selections:", error);
      }

      clearBoardAthleteCache();
    }
  }, []);

  const acceptLineChange = useCallback((selectionToAccept) => {
    const selectionKey = getAthleteVariantKey(selectionToAccept);
    setSelected((currentSelected) => currentSelected.map((selection) => {
      if (getAthleteVariantKey(selection) !== selectionKey || selection?._selectionSyncStatus !== "changed") {
        return selection;
      }

      const acceptedLine = selection._selectionCurrentStat;
      if (acceptedLine === null || acceptedLine === undefined || String(acceptedLine).trim() === "") {
        return selection;
      }

      return normalizeSelectedAthlete({
        ...stripSelectionSyncMeta(selection),
        stat: acceptedLine,
      });
    }));
  }, []);

  const maybeRefreshFeedIfStale = useCallback(async (targetSport, options = {}) => {
    if (!loggedIn) {
      return { attempted: false, updated: false, skipped: "logged_out" };
    }

    if (!currentUserHasRefreshBalance()) {
      return { attempted: false, updated: false, skipped: "no_balance" };
    }

    const sportKey = String(targetSport || "").toLowerCase();
    if (!sportKey) {
      return { attempted: false, updated: false };
    }
    if (isTopPlaysSport(sportKey)) {
      return { attempted: false, updated: false, skipped: "synthetic_sport" };
    }

    const now = Date.now();
    const lastAttempt = staleFeedRefreshAttemptRef.current[sportKey] || 0;

    if (!options.force && now - lastAttempt < STALE_FEED_REFRESH_COOLDOWN_MS) {
      return { attempted: false, updated: false, skipped: "cooldown" };
    }

    if (staleFeedRefreshInFlightRef.current[sportKey]) {
      return staleFeedRefreshInFlightRef.current[sportKey];
    }

    const runner = (async () => {
      staleFeedRefreshAttemptRef.current[sportKey] = now;
      let meta = options.meta || null;

      if (!meta && !options.force) {
        meta = await FetchFeedVersion(sportKey, getShrkValue());
      }

      if (!options.force && meta && !feedMetaIsStale(meta)) {
        return {
          attempted: false,
          updated: false,
          meta,
        };
      }

      const result = await RefreshFeedIfStale(sportKey, getShrkValue());
      if (result?.updated) {
        clearBoardAthleteCache();
      }

      return {
        attempted: true,
        updated: Boolean(result?.updated),
        locked: Boolean(result?.locked),
        meta: result?.meta || meta,
      };
    })();

    staleFeedRefreshInFlightRef.current[sportKey] = runner;

    try {
      return await runner;
    } finally {
      delete staleFeedRefreshInFlightRef.current[sportKey];
    }
  }, []);

  const hydrateBoardAthletes = async (
    selectedSport,
    selectedStat,
    requestToken,
    gameIdOverride = undefined
  ) => {
    const activeGameFilter =
      gameIdOverride !== undefined
        ? gameIdOverride
        : (selectedSport === sport ? (selectedGameId || false) : false);
    const hydrationKey = [
      normalizeSportKey(selectedSport),
      normalizeProjectedStatKey(selectedStat),
      String(activeGameFilter || ""),
      requestToken,
    ].join("|");
    let hydrationState = boardHydrationStateRef.current;
    if (!hydrationState || hydrationState.key !== hydrationKey) {
      hydrationState = {
        key: hydrationKey,
        selectedSport,
        selectedStat,
        activeGameFilter,
        requestToken,
        nextOffset: 0,
        athletes: [],
        seenAthletes: new Set(),
        inFlight: false,
        exhausted: false,
      };
      boardHydrationStateRef.current = hydrationState;
    }

    if (hydrationState.inFlight || hydrationState.exhausted || !isBoardRequestCurrent(requestToken)) {
      return;
    }

    const requestedOffset = hydrationState.nextOffset;
    if (requestedOffset > BOARD_ATHLETE_HYDRATION_MAX_OFFSET) {
      hydrationState.exhausted = true;
      setBoardHydrationHasMore(false);
      return;
    }

    hydrationState.inFlight = true;
    setBoardLoadingMoreAthletes(true);

    try {
      const athleteResult = await fetchBoardAthletes(
        selectedSport,
        selectedStat,
        activeGameFilter,
        requestToken,
        {
          limit: BOARD_ATHLETE_HYDRATION_PAGE_SIZE,
          offset: requestedOffset,
          requestLane: "hydration",
        }
      );
      if (
        athleteResult.aborted ||
        !isBoardRequestCurrent(requestToken) ||
        boardHydrationStateRef.current !== hydrationState
      ) {
        return;
      }

      athleteResult.list.forEach((athlete) => {
        const athleteKey = getAthleteVariantKey(athlete);
        if (hydrationState.seenAthletes.has(athleteKey)) {
          return;
        }

        hydrationState.seenAthletes.add(athleteKey);
        hydrationState.athletes.push(athlete);
      });

      hydrationState.nextOffset += BOARD_ATHLETE_HYDRATION_PAGE_SIZE;
      hydrationState.exhausted = Boolean(
        athleteResult.failed ||
        athleteResult.rawList.length < BOARD_ATHLETE_HYDRATION_PAGE_SIZE ||
        hydrationState.nextOffset > BOARD_ATHLETE_HYDRATION_MAX_OFFSET
      );

      if (!athleteResult.failed && athleteResult.list.length > 0) {
        // A partial page cannot replace the displayed board. Replaying the
        // accumulated pages also overwrote live updates made after earlier pages.
        setAthletes((currentAthletes) => appendAthleteList(currentAthletes, athleteResult.list));
      }
      setBoardHydrationHasMore(!hydrationState.exhausted);
    } finally {
      hydrationState.inFlight = false;
      if (isBoardRequestCurrent(requestToken)) {
        setBoardLoadingMoreAthletes(false);
      }
    }
  };

  const scheduleBoardAthleteHydration = (
    selectedSport,
    selectedStat,
    requestToken,
    gameIdOverride = undefined
  ) => {
    resetBoardHydration();
    const activeGameFilter =
      gameIdOverride !== undefined
        ? gameIdOverride
        : (selectedSport === sport ? (selectedGameId || false) : false);
    const hydrationState = {
      key: [
        normalizeSportKey(selectedSport),
        normalizeProjectedStatKey(selectedStat),
        String(activeGameFilter || ""),
        requestToken,
      ].join("|"),
      selectedSport,
      selectedStat,
      activeGameFilter,
      requestToken,
      nextOffset: 0,
      athletes: [],
      seenAthletes: new Set(),
      inFlight: false,
      exhausted: false,
    };
    boardHydrationStateRef.current = hydrationState;

    const enableHydration = () => {
      boardHydrationTimerRef.current = null;
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }
      setBoardHydrationHasMore(true);
    };

    // Hydration becomes eligible after the first viewport settles, but the
    // next page is requested only when its sentinel approaches the viewport.
    boardHydrationTimerRef.current = window.setTimeout(
      enableHydration,
      BOARD_VIEWPORT_HYDRATION_DELAY_MS
    );
  };

  useEffect(() => {
    const sentinel = boardHydrationSentinelRef.current;
    if (
      !boardHydrationHasMore ||
      !sentinel ||
      typeof IntersectionObserver !== "function"
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      const hydrationState = boardHydrationStateRef.current;
      if (!hydrationState || hydrationState.inFlight || hydrationState.exhausted) {
        return;
      }

      void hydrateBoardAthletes(
        hydrationState.selectedSport,
        hydrationState.selectedStat,
        hydrationState.requestToken,
        hydrationState.activeGameFilter
      );
    }, { rootMargin: "600px 0px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [boardHydrationHasMore, boardLoadingMoreAthletes]);

  const selectAvailableStats = (latest, preferredSport = "", preferredStat = "") => {
    if (!latest || typeof latest !== "object") {
      return null;
    }

    const latestSports = Object.keys(latest);
    if (latestSports.length === 0) {
      return {
        verifiedStats: latest,
        sports: latestSports,
        sport: "",
        stats: {},
        stat: "",
      };
    }

    const selectedSport = preferredSport && latest[preferredSport] ? preferredSport : latestSports[0];
    const selectedStats = latest[selectedSport] || {};
    const statKeys = Object.keys(selectedStats);
    const normalizedPreferredStat = normalizeProjectedStatKey(preferredStat);
    const selectedStat =
      normalizedPreferredStat && Object.prototype.hasOwnProperty.call(selectedStats, normalizedPreferredStat)
        ? normalizedPreferredStat
        : (statKeys[0] || "");

    return {
      verifiedStats: latest,
      sports: latestSports,
      sport: selectedSport,
      stats: selectedStats,
      stat: selectedStat,
    };
  };

  const applyAvailableStatsSelection = (selection) => {
    if (!selection) {
      return null;
    }

    setVerifiedStats(selection.verifiedStats);
    setSports(selection.sports);
    setSport(selection.sport);
    setStats(selection.stats);
    setStat(selection.stat);

    return selection;
  };

  const syncAvailableStats = (latest, preferredSport = "", preferredStat = "") => {
    return applyAvailableStatsSelection(selectAvailableStats(latest, preferredSport, preferredStat));
  };

  const pruneSportFromAvailableStats = (
    currentVerifiedStats,
    currentSports,
    sportToRemove,
    preferredSport = "",
    preferredStat = ""
  ) => {
    const nextVerifiedStats = { ...(currentVerifiedStats || {}) };
    if (sportToRemove) {
      delete nextVerifiedStats[sportToRemove];
    }

    const nextSports = Array.isArray(currentSports) && currentSports.length > 0
      ? currentSports.filter((sportKey) => sportKey !== sportToRemove && nextVerifiedStats[sportKey])
      : Object.keys(nextVerifiedStats);

    const selectedSport = preferredSport && nextVerifiedStats[preferredSport]
      ? preferredSport
      : (nextSports[0] || "");
    const selectedStats = selectedSport ? (nextVerifiedStats[selectedSport] || {}) : {};
    const statKeys = Object.keys(selectedStats);
    const normalizedPreferredStat = normalizeProjectedStatKey(preferredStat);
    const selectedStat =
      normalizedPreferredStat && Object.prototype.hasOwnProperty.call(selectedStats, normalizedPreferredStat)
        ? normalizedPreferredStat
        : (statKeys[0] || "");

    return applyAvailableStatsSelection({
      verifiedStats: nextVerifiedStats,
      sports: nextSports,
      sport: selectedSport,
      stats: selectedStats,
      stat: selectedStat,
    });
  };

  const syncValidatedSelection = (selection) => {
    if (!selection || typeof selection !== "object") {
      return null;
    }

    let nextVerifiedStats = null;
    setVerifiedStats((currentVerifiedStats) => {
      const updatedVerifiedStats = { ...(currentVerifiedStats || {}) };
      // A budget-limited response may be missing leagues that were not checked.
      // Merge its selected league, but only prune the existing map after a
      // complete server pass.
      const validSports = selection.complete !== false && Array.isArray(selection.sports)
        ? new Set(selection.sports)
        : null;

      if (validSports) {
        Object.keys(updatedVerifiedStats).forEach((sportKey) => {
          if (!validSports.has(sportKey)) {
            delete updatedVerifiedStats[sportKey];
          }
        });
      }

      if (selection.sport && selection.stats && typeof selection.stats === "object") {
        updatedVerifiedStats[selection.sport] = selection.stats;
      }

      nextVerifiedStats = updatedVerifiedStats;
      return updatedVerifiedStats;
    });

    // React may defer the state-updater callback. Build the same non-pruning
    // fallback from the current render so an incomplete response never clears
    // the sport rail while that updater is pending.
    const mergedVerifiedStats = nextVerifiedStats || { ...(verifiedStats || {}) };
    if (selection.sport && selection.stats && typeof selection.stats === "object") {
      mergedVerifiedStats[selection.sport] = selection.stats;
    }

    const nextSports = selection.complete !== false && Array.isArray(selection.sports)
      ? selection.sports
      : Object.keys(mergedVerifiedStats);
    const nextSport = selection.sport || "";
    const nextStats = selection.stats && typeof selection.stats === "object" ? selection.stats : {};
    const nextStat = selection.stat || "";

    setSports(nextSports);
    setSport(nextSport);
    setStats(nextStats);
    setStat(nextStat);

    return {
      verifiedStats: mergedVerifiedStats,
      sports: nextSports,
      sport: nextSport,
      stats: nextStats,
      stat: nextStat,
      requestedSportAvailable: selection.requestedSportAvailable !== false,
    };
  };

  // Pull latest stat map from server and return a valid sport/stat pair.
  const refreshStatsForSport = async (preferredSport = "", preferredStat = "", options = {}) => {
    const latest = await FetchStats({ force: options.force, ttlMs: options.ttlMs });
    const requestToken = options.requestToken;

    if (requestToken !== undefined && !isBoardRequestCurrent(requestToken)) {
      return null;
    }

    setCachedBoardBootstrapMetadata(preferredSport, latest, Object.keys(latest || {}));

    return syncAvailableStats(latest, preferredSport, preferredStat);
  };

  const rememberSportCacheClear = (targetSport) => {
    cacheClearedRef.current = true;
    if (targetSport) {
      clearedSportsRef.current.add(targetSport);
    }
    setCacheCleared(true);
    clearStatsCache();
    clearBoardAthleteCache();
  };

  const recoverBoardSelection = async (
    preferredSport = "",
    preferredStat = "",
    requestToken = boardRequestTokenRef.current
  ) => {
    const loadLatestSelection = async () => {
      const latest = await FetchStats({ force: true, ttlMs: 0 });

      if (requestToken !== undefined && !isBoardRequestCurrent(requestToken)) {
        return null;
      }

      return selectAvailableStats(latest, preferredSport, preferredStat);
    };

    let recoveredSelection = await loadLatestSelection();
    if (
      recoveredSelection?.sport &&
      recoveredSelection?.stat &&
      Object.keys(recoveredSelection.stats || {}).length > 0
    ) {
      return recoveredSelection;
    }

    if (preferredSport && !clearedSportsRef.current.has(preferredSport)) {
      const cacheCleared = await ClearSportCache(preferredSport);

      if (requestToken !== undefined && !isBoardRequestCurrent(requestToken)) {
        return null;
      }

      if (cacheCleared) {
        rememberSportCacheClear(preferredSport);
        recoveredSelection = await loadLatestSelection();
      }
    }

    return recoveredSelection;
  };

  const validateSportSelection = async (
    requestedSport,
    preferredStat = "",
    validationToken = sportValidationTokenRef.current,
    didFetchImmediately = false,
    retryAttempt = 0
  ) => {
    let retryScheduled = false;
    try {
      const selection = await FetchStatsSelection(requestedSport, preferredStat);
      if (!selection || typeof selection !== "object") {
        return;
      }

      if (validationToken !== sportValidationTokenRef.current) {
        return;
      }

      const requestedSportAvailable = selection.requestedSportAvailable !== false;

      // An incomplete rebuild cannot prove that an omitted league disappeared.
      // Keep the already-loaded board and retry validation on a later interaction.
      if (!requestedSportAvailable && selection.complete === false) {
        return;
      }

      const synced = syncValidatedSelection(selection);
      if (!synced || validationToken !== sportValidationTokenRef.current) {
        return;
      }

      if (!requestedSportAvailable) {
        toastr["info"](`${requestedSport.toUpperCase()} is no longer available.`, "League Removed");

        if (!synced.sport || !synced.stat) {
          setAthletes([]);
          setAthletesLoading(false);
          return;
        }

        setAthletesLoading(true);
        handleStatChange(synced.sport, synced.stat, synced.stats);
        return;
      }

      const shouldRefetchCurrentSport = !didFetchImmediately || synced.stat !== preferredStat;
      if (!shouldRefetchCurrentSport) {
        return;
      }

      if (!synced.sport || !synced.stat) {
        setAthletes([]);
        setAthletesLoading(false);
        return;
      }

      setAthletesLoading(true);
      handleStatChange(synced.sport, synced.stat, synced.stats);
    } catch (error) {
      const selectionStillCurrent =
        validationToken === sportValidationTokenRef.current &&
        normalizeSportKey(requestedSport) === activeSportRef.current;

      // A first-ever league click may not have stat metadata in the partial
      // bootstrap map. If its targeted request waits behind busy PHP workers,
      // retry once while retaining the loading shell instead of committing a
      // false permanent "No Athletes Found" state.
      if (!didFetchImmediately && selectionStillCurrent && retryAttempt < 1) {
        retryScheduled = true;
        setSportStatsLoading(true);
        setAthletesLoading(true);
        window.setTimeout(() => {
          if (
            validationToken !== sportValidationTokenRef.current ||
            normalizeSportKey(requestedSport) !== activeSportRef.current
          ) {
            return;
          }
          void validateSportSelection(
            requestedSport,
            preferredStat,
            validationToken,
            false,
            retryAttempt + 1
          );
        }, 400);
        return;
      }

      if (!didFetchImmediately && selectionStillCurrent) {
        // The selected tab is authoritative. Restoring fallbackSelection here
        // silently switched customers back to the previous league whenever a
        // cold stats request timed out, even though the requested league was
        // still available and its Popular cache was merely warming.
        const requestedStats = verifiedStats[requestedSport] || {};
        const requestedStat = preferredStat || Object.keys(requestedStats)[0] || "";
        setSport(requestedSport);
        setStats(requestedStats);
        setStat(requestedStat);
        setAthletes([]);
        setAthletesLoadFailed(true);
        setAthletesLoading(false);
        showBoardRefreshWarning();
      }

      if (error?.name === "TimeoutError") {
        console.warn("Sport selection validation timed out after a bounded retry.");
        return;
      }
      console.error("Error validating sport selection:", error);
    } finally {
      if (!retryScheduled && validationToken === sportValidationTokenRef.current) {
        setSportStatsLoading(false);
      }
    }
  };

  const fetchInitialData = async () => {
    if (initialBoardLoadInFlightRef.current) {
      return;
    }

    initialBoardLoadInFlightRef.current = true;
    const requestToken = startBoardRequest();
    let topPlaysPreviewRows = [];
    let topPlaysPreviewSourceLeagues = [];
    let cachedPreviewSports = [];
    let cachedPreviewAvailableStats = {};
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sportParam = urlParams.get("sport");
      if (boardCacheBypassParam) {
        clearBoardAthleteCache();
      }
      const cachedBoot = getCachedBoardBootstrapPayload(sportParam || "");
      const cachedMetadata = getCachedBoardBootstrapMetadata(sportParam || "");
      if (cachedMetadata) {
        cachedPreviewAvailableStats = cachedMetadata.availableStats || {};
        cachedPreviewSports = Array.isArray(cachedMetadata.sports)
          ? cachedMetadata.sports
          : Object.keys(cachedPreviewAvailableStats);
      }
      if (cachedBoot && isBoardRequestCurrent(requestToken)) {
        syncActivePromoSummaryFromBootstrap(cachedBoot);
        const cachedSport = cachedBoot.sport || "";
        const cachedStat = cachedBoot.stat || "";
        const cachedAvailableStats = cachedBoot.availableStats || {};
        const cachedSports = Array.isArray(cachedBoot.sports)
          ? cachedBoot.sports
          : Object.keys(cachedAvailableStats);
        const cachedStats = cachedBoot.stats || cachedAvailableStats[cachedSport] || {};
        const cachedAthletes = filterAthleteList(cachedBoot.athletes);

        if (cachedSport && cachedStat && Object.keys(cachedStats).length > 0 && cachedAthletes.length > 0) {
          cachedPreviewSports = cachedSports;
          cachedPreviewAvailableStats = cachedAvailableStats;
          setVerifiedStats(cachedAvailableStats);
          setSports(cachedSports);
          setSport(cachedSport);
          setStats(cachedStats);
          setStat(cachedStat);
          setAthletes(cachedAthletes);
          setLoading(false);
          setAthletesLoading(false);
        }
      }

      const bootstrapRequest = FetchBoardBootstrap({
        sport: sportParam || "",
        limit: INITIAL_BOARD_ATHLETE_LIMIT,
      });
      const normalizedRequestedSport = normalizeSportKey(sportParam);
      const canUseTopPlaysPreview =
        (!normalizedRequestedSport || isTopPlaysSport(normalizedRequestedSport)) &&
        String(getShrkValue() ?? "0") !== "1" &&
        Number(getCurrentUserTierValue()) <= 1 &&
        String(getCurrentShowOddsValue()) !== "1";

      if (canUseTopPlaysPreview) {
        const topPlaysStaticCache = await FetchTopPlaysStaticCache();
        topPlaysPreviewRows = filterAthleteList(topPlaysStaticCache.rows);
        topPlaysPreviewSourceLeagues = topPlaysStaticCache.sourceLeagues
          .filter((sportKey) => !isTopPlaysSport(sportKey));
        if (topPlaysPreviewRows.length > 0 && isBoardRequestCurrent(requestToken)) {
          const previewStats = { [POPULAR_STAT_KEY]: "TOP PICKS" };
          const fallbackPreviewLeagues = Array.from(new Set([
            ...recentBoardLeagues,
            ...topPlaysPreviewSourceLeagues,
          ].filter((sportKey) => sportKey && !isTopPlaysSport(sportKey))));
          const previewSports = cachedPreviewSports.length > 0
            ? [
              TOP_PLAYS_SPORT,
              ...cachedPreviewSports.filter((sportKey) => !isTopPlaysSport(sportKey)),
            ]
            : [TOP_PLAYS_SPORT, ...fallbackPreviewLeagues];
          setVerifiedStats(cachedPreviewSports.length > 0
            ? {
              ...cachedPreviewAvailableStats,
              [TOP_PLAYS_SPORT]: previewStats,
            }
            : { [TOP_PLAYS_SPORT]: previewStats });
          setSports(previewSports);
          setSelectedGameId("");
          setSport(TOP_PLAYS_SPORT);
          setStats(previewStats);
          setStat(POPULAR_STAT_KEY);
          setAthletes(topPlaysPreviewRows);
          setLoading(false);
          setAthletesLoading(false);
        }
      }

      const boot = await bootstrapRequest;

      // The static preview allows navigation while bootstrap is still pending.
      // A late bootstrap must not replace a newer league/stat's athletes with
      // Top Picks rows while leaving that newer selection's tabs in place.
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }
      if (!boot) {
        setLoading(false);
        setAthletes(topPlaysPreviewRows);
        setAthletesLoading(false);
        return;
      }
      syncActivePromoSummaryFromBootstrap(boot);

      let selectedSport = boot.sport || "";
      let selectedStat = boot.stat || "";
      let availableStats = boot.availableStats || {};
      let availableSports = Array.isArray(boot.sports) ? boot.sports : Object.keys(availableStats);
      let currentStats = boot.stats || availableStats[selectedSport] || {};
      let athleteList = filterAthleteList(boot.athletes);

      if (topPlaysPreviewRows.length > 0) {
        const topPlaysStats = availableStats[TOP_PLAYS_SPORT] || { [POPULAR_STAT_KEY]: "TOP PICKS" };
        availableStats = {
          ...availableStats,
          [TOP_PLAYS_SPORT]: topPlaysStats,
        };
        availableSports = [
          TOP_PLAYS_SPORT,
          ...availableSports.filter((sportKey) => !isTopPlaysSport(sportKey)),
        ];
        selectedSport = TOP_PLAYS_SPORT;
        selectedStat = POPULAR_STAT_KEY;
        currentStats = topPlaysStats;
        athleteList = topPlaysPreviewRows;
      }

      setCachedBoardBootstrapMetadata(sportParam || "", availableStats, availableSports);

      const hasResolvedBootSelection =
        Boolean(selectedSport && selectedStat) && Object.keys(currentStats).length > 0;
      if (!hasResolvedBootSelection) {
        const recoveredSelection = await recoverBoardSelection(sportParam || selectedSport, selectedStat, requestToken);
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }

        if (recoveredSelection) {
          availableStats = recoveredSelection.verifiedStats || {};
          availableSports = recoveredSelection.sports || Object.keys(availableStats);
          selectedSport = recoveredSelection.sport || selectedSport;
          selectedStat = recoveredSelection.stat || selectedStat;
          currentStats = recoveredSelection.stats || availableStats[selectedSport] || {};
        }
      }

      setVerifiedStats(availableStats);
      setSports(availableSports);
      setSport(selectedSport);
      setStats(currentStats);
      setStat(selectedStat);
      setLoading(false);

      if (!selectedSport || !selectedStat || Object.keys(currentStats).length === 0) {
        setAthletes([]);
        setAthletesLoading(false);
        return;
      }

      void maybeRefreshFeedIfStale(selectedSport, { force: true });

      const initialFetchLimit = getInitialAthleteLimit(selectedStat);
      const athleteResult = {
        aborted: false,
        rawList: athleteList,
        list: athleteList,
      };

      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }

      if (athleteResult.list.length === 0) {
        if (isTopPlaysSport(selectedSport)) {
          const recovered = await recoverEmptyTopPlays({
            requestToken,
            availableSports,
            statsMapBySport: availableStats,
            selectedStat,
            allowFallback: true,
          });

          if (!recovered) {
            setAthletes([]);
            setAthletesLoading(false);
          }
          return;
        }

        setAthletes([]);
        setAthletesLoading(true);
        return handleStatChange(
          selectedSport,
          selectedStat,
          currentStats,
          [],
          clearedSportsRef.current.has(selectedSport),
          false,
          requestToken
        );
      }

      const hasStalePreview = athleteResult.list.some((athlete) => athlete?._boardOptimisticPreview);
      setAthletes(athleteResult.list);
      setAthletesLoading(hasStalePreview);
      if (hasStalePreview) {
        return handleStatChange(
          selectedSport,
          selectedStat,
          currentStats,
          [],
          false,
          false,
          requestToken,
          true
        );
      }
      setCachedBoardAthleteResult(
        selectedSport,
        selectedStat,
        false,
        initialFetchLimit > 0 ? initialFetchLimit : 0,
        0,
        athleteResult
      );
      setCachedBoardBootstrapPayload(sportParam || "", {
        ...boot,
        availableStats,
        sports: availableSports,
        sport: selectedSport,
        stats: currentStats,
        stat: selectedStat,
        athletes: athleteResult.rawList,
      });
      if (!isTopPlaysSport(selectedSport) && shouldHydrateBoardAthleteResult(athleteResult, initialFetchLimit)) {
        scheduleBoardAthleteHydration(selectedSport, selectedStat, requestToken);
      }
    } catch (error) {
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }
      const isBootstrapTimeout = /Board bootstrap timed out/i.test(String(error?.message || ""));
      if (isBootstrapTimeout) {
        console.warn("Board bootstrap exceeded its deadline; recovering league metadata.");
      } else {
        console.error("Error:", error);
      }

      if (topPlaysPreviewRows.length > 0 && isBoardRequestCurrent(requestToken)) {
        setLoading(false);
        setAthletes(topPlaysPreviewRows);
        setAthletesLoading(false);
      }

      try {
        const requestedSport = new URLSearchParams(window.location.search).get("sport") || "";
        const cachedMetadata = getCachedBoardBootstrapMetadata(requestedSport);
        let latestStats = cachedMetadata?.availableStats || null;
        let latestStatsError = null;

        for (
          let attempt = 0;
          !latestStats && attempt < BOARD_BOOTSTRAP_RECOVERY_RETRY_DELAYS_MS.length;
          attempt += 1
        ) {
          const retryDelayMs = BOARD_BOOTSTRAP_RECOVERY_RETRY_DELAYS_MS[attempt];
          if (retryDelayMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, retryDelayMs));
          }
          if (!isBoardRequestCurrent(requestToken)) {
            return;
          }

          try {
            latestStats = await FetchStats({
              force: true,
              ttlMs: 0,
              timeoutMs: BOARD_BOOTSTRAP_RECOVERY_TIMEOUT_MS,
            });
          } catch (statsError) {
            latestStatsError = statsError;
          }
        }

        if (!latestStats) {
          throw latestStatsError || new Error("League metadata is unavailable.");
        }
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }

        setCachedBoardBootstrapMetadata(requestedSport, latestStats, Object.keys(latestStats));

        const latestSports = Object.keys(latestStats && typeof latestStats === "object" ? latestStats : {});
        if (topPlaysPreviewRows.length > 0 && latestSports.length > 0) {
          const recovery = buildTopPlaysMetadataRecovery(
            latestStats,
            cachedMetadata?.sports,
            TOP_PLAYS_SPORT,
            POPULAR_STAT_KEY
          );
          const recoveredStatsMap = recovery.verifiedStats;
          const recoveredSports = recovery.sports;

          primeStatsCache(recoveredStatsMap);
          setVerifiedStats(recoveredStatsMap);
          setSports(recoveredSports);
          setSport(recovery.sport);
          setStats(recovery.stats);
          setStat(recovery.stat);
          setLoading(false);
          setAthletes(topPlaysPreviewRows);
          setAthletesLoading(false);
          return;
        }

        const fallbackSelection = getTopPlaysFallbackSelection(latestSports, latestStats) || selectAvailableStats(latestStats);
        if (fallbackSelection?.sport && fallbackSelection?.stat) {
          const fallbackStatsMap = fallbackSelection.verifiedStats || latestStats || {};
          const fallbackSports = fallbackSelection.sports || Object.keys(fallbackStatsMap);

          setVerifiedStats(fallbackStatsMap);
          setSports(fallbackSports);
          setSport(fallbackSelection.sport);
          setStats(fallbackSelection.stats);
          setStat(fallbackSelection.stat);
          setLoading(false);
          setAthletesLoading(true);

          return getAthletes(
            fallbackSelection.sport,
            fallbackSelection.stat,
            [],
            [TOP_PLAYS_SPORT],
            false,
            true,
            fallbackSelection.stats,
            {
              requestToken,
              availableSports: fallbackSports,
              statsMapBySport: fallbackStatsMap,
              preserveAvailableStatsOnEmpty: true,
            }
          );
        }
      } catch (recoveryError) {
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }
        if (topPlaysPreviewRows.length > 0) {
          console.warn("League metadata recovery is still pending; keeping Top Picks visible.");
        } else {
          console.error("Bootstrap recovery failed:", recoveryError);
        }
      }

      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }
      if (topPlaysPreviewRows.length > 0) {
        setLoading(false);
        setAthletes(topPlaysPreviewRows);
        setAthletesLoading(false);
        return;
      }

      setLoading(false);
      setAthletes([]);
      setAthletesLoading(false);
    } finally {
      initialBoardLoadInFlightRef.current = false;
    }
  };

  useEffect(() => {

    const parsedSelected = readSelectedAthletesFromStorage({
      storageKey: selectedStorageKey,
    });
    if (parsedSelected.length > 0) {
      // Make sure no athletes start time has passed
      let currentTime = new Date();
      let validSelected = (parsedSelected || [])
        .map(normalizeSelectedAthlete)
        .filter(Boolean)
        .filter((x) => {
          const parsedStart = parseBoardStartDate(x?.start_time_iso || x?.start_time_obj || x?.start_time || "");
          return Boolean(x?.is_live) || !parsedStart || parsedStart >= currentTime;
        });
      const unavailableSelections = getUnavailableSelectionSet();
      if (unavailableSelections.size > 0) {
        validSelected = validSelected.filter(
          (entry) => !unavailableSelections.has(getSelectionAvailabilityKey(entry))
        );
      }
      setSelected(
        win100ModeActive
          ? filterWin100CompatibleSelections(validSelected)
          : validSelected
      );
    }

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    toastr.options = {
      closeButton: true,
      debug: false,
      newestOnTop: true,
      progressBar: false,
      positionClass: "toast-top-center",
      preventDuplicates: true,
      onclick: null,
      showDuration: prefersReducedMotion ? "0" : "240",
      hideDuration: prefersReducedMotion ? "0" : "180",
      timeOut: "5000",
      extendedTimeOut: "2000",
      showEasing: "swing",
      hideEasing: "swing",
      showMethod: "fadeIn",
      hideMethod: "fadeOut",
    };

    const toastPreviewType = new URLSearchParams(window.location.search).get("toastr");
    const toastPreviews = {
      test: {
        type: "info",
        title: "Toast Preview",
        message: "Notifications are displaying correctly.",
      },
      success: {
        type: "success",
        title: "Success",
        message: "Your changes have been saved.",
      },
      info: {
        type: "info",
        title: "Update",
        message: "Here is a helpful notification.",
      },
      warning: {
        type: "warning",
        title: "Notice",
        message: "Please review this before continuing.",
      },
      error: {
        type: "error",
        title: "Something went wrong",
        message: "Please try again in a moment.",
      },
    };
    const toastPreview = toastPreviews[toastPreviewType];

    if (toastPreview) {
      toastr[toastPreview.type](toastPreview.message, toastPreview.title);
    }

    fetchInitialData();

    if (h2hCount > 0) {
      setMaxAthletes(h2hCount);
      setTimeout(() => {
        $("#h2h-match-modal").modal('show');
      }, 1000);
    }

  }, []);

  useEffect(() => () => {
    cancelBoardPrewarm();
    deferredBoardPersistenceRef.current.clear();
    cancelTeamLineupValidation();
    if (boardHydrationTimerRef.current) {
      window.clearTimeout(boardHydrationTimerRef.current);
      boardHydrationTimerRef.current = null;
    }
    boardHydrationStateRef.current = null;
  }, []);

  useEffect(() => {
    if (loading || !teamsBaseEnabled) {
      return undefined;
    }

    const controller = new AbortController();
    void fetchTeamLineupAvailability(sport, controller.signal).catch((error) => {
      if (error?.name !== "AbortError") {
        console.error("Error checking team lineup availability:", error);
      }
    });

    return () => controller.abort();
  }, [loading, sport, teamsBaseEnabled]);

  useEffect(() => {
    if (loading || !sport || !stats || Object.keys(stats).length === 0) {
      return undefined;
    }

    if (!teamsStatEnabled || !isTeamsSelected) {
      cancelTeamLineupRequest();
      setTeamLineupAthletes([]);
      setTeamLineupQuotedGroups([]);
      setTeamLineupQuotesReady(false);
      setTeamLineupLoading(false);
      if (!teamsStatEnabled && isTeamsSelected) {
        setStat(getTeamLineupFallbackStat(stats));
      }
      return undefined;
    }

    void fetchTeamLineupAthletePool(sport, stats, selectedGameId || false);

    return () => {
      cancelTeamLineupRequest();
    };
  }, [loading, sport, teamsStatSignature, selectedGameId, maxAthletes, isH2H, teamsStatEnabled, isTeamsSelected]);

  useEffect(() => {
    if (teamsHubEnabled || !teamsHubOpen) {
      return undefined;
    }

    cancelTeamsHubRequest();
    setTeamsHubOpen(false);
    setTeamsHubGroups([]);
    setTeamsHubQuotedGroups([]);
    setTeamsHubQuotesReady(false);
    setTeamsHubLoading(false);

    return undefined;
  }, [teamsHubEnabled, teamsHubOpen]);

  useEffect(() => {
    let cancelled = false;

    if (!teamsBaseEnabled || !isTeamsSelected || teamLineupAthletes.length === 0) {
      setTeamLineupGroups([]);
      return undefined;
    }

    void buildTeamLineupGroupsOnDemand(teamLineupAthletes, sport).then((groups) => {
      if (!cancelled) {
        setTeamLineupGroups(groups);
      }
    }).catch((error) => {
      if (!cancelled) {
        console.error("Error building team lineups:", error);
        setTeamLineupGroups([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [teamLineupAthletes, sport, teamsBaseEnabled, isTeamsSelected]);

  useEffect(() => {
    if (!Array.isArray(teamLineupGroups) || teamLineupGroups.length === 0) {
      setTeamLineupQuotedGroups([]);
      setTeamLineupQuotesReady(false);
      return undefined;
    }

    setTeamLineupQuotedGroups(teamLineupGroups);
    setTeamLineupQuotesReady(false);
    const controller = new AbortController();
    void quoteTeamLineupGroups(teamLineupGroups, controller.signal, (quotedGroup) => {
      setTeamLineupQuotedGroups((currentGroups) => mergeTeamLineupGroup(currentGroups, quotedGroup));
    }).then((quotedGroups) => {
      if (!controller.signal.aborted) {
        setTeamLineupQuotedGroups(quotedGroups);
        setTeamLineupQuotesReady(true);
        setTeamLineupSportAvailability(sport, teamLineupGroupsHaveAvailableOptions(quotedGroups));
      }
    });

    return () => controller.abort();
  }, [teamLineupGroups, sport]);

  useEffect(() => {
    if (!Array.isArray(teamsHubGroups) || teamsHubGroups.length === 0) {
      setTeamsHubQuotedGroups([]);
      setTeamsHubQuotesReady(false);
      return undefined;
    }

    setTeamsHubQuotedGroups(teamsHubGroups);
    setTeamsHubQuotesReady(false);
    const controller = new AbortController();
    void quoteTeamLineupGroups(teamsHubGroups, controller.signal, (quotedGroup) => {
      setTeamsHubQuotedGroups((currentGroups) => mergeTeamLineupGroup(currentGroups, quotedGroup));
    }).then((quotedGroups) => {
      if (!controller.signal.aborted) {
        setTeamsHubQuotedGroups(quotedGroups);
        setTeamsHubQuotesReady(true);
      }
    });

    return () => controller.abort();
  }, [teamsHubGroups]);

  // If registration is in progress, pause auto-refresh so users can leave for email OTP and return safely.
  useEffect(() => {
    if (loggedIn) {
      clearRegistrationFlowStarted();
      return;
    }

    const isRegistrationTrigger = (target) => (
      target instanceof Element && (
        target.id === "register-link" ||
        !!target.closest("#register-link") ||
        !!target.closest('[data-target="#register-modal"]')
      )
    );

    const prepareRegistration = (event) => {
      if (isRegistrationTrigger(event.target)) {
        void loadBoardRegistration().catch((error) => {
          console.error("Unable to load registration form:", error);
        });
      }
    };

    const markFromClick = (event) => {
      const target = event.target;
      if (isRegistrationTrigger(target)) {
        markRegistrationFlowStarted();
        prepareRegistration(event);
      }
    };

    const markFromInput = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("#register-modal")) {
        markRegistrationFlowStarted();
      }
    };

    document.addEventListener("pointerover", prepareRegistration, true);
    document.addEventListener("pointerdown", prepareRegistration, true);
    document.addEventListener("focusin", prepareRegistration, true);
    document.addEventListener("click", markFromClick, true);
    document.addEventListener("input", markFromInput, true);

    return () => {
      document.removeEventListener("pointerover", prepareRegistration, true);
      document.removeEventListener("pointerdown", prepareRegistration, true);
      document.removeEventListener("focusin", prepareRegistration, true);
      document.removeEventListener("click", markFromClick, true);
      document.removeEventListener("input", markFromInput, true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (topPlaysDragRef.current.animationFrame) {
        window.cancelAnimationFrame(topPlaysDragRef.current.animationFrame);
      }
      if (topPlaysDragRef.current.inertiaFrame) {
        window.cancelAnimationFrame(topPlaysDragRef.current.inertiaFrame);
      }
      boardRequestTokenRef.current += 1;
      cancelInFlightAthleteRequest();
      cancelTeamLineupRequest();
      cancelTeamsHubRequest();
      if (selectedLineSyncRefreshTimerRef.current) {
        window.clearTimeout(selectedLineSyncRefreshTimerRef.current);
      }
      selectedLineSyncRefreshTicketsRef.current = {};
      selectedLineSyncRefreshDeadlineRef.current = 0;
      selectedLineSyncDeferredRetryRef.current = 0;
      selectedLineSyncRetrySignatureRef.current = "";
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    let checkInFlight = false;

    const cleanAssetRefreshParam = () => {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has(BOARD_ASSET_REFRESH_PARAM)) {
        return;
      }

      currentUrl.searchParams.delete(BOARD_ASSET_REFRESH_PARAM);
      window.history.replaceState(window.history.state, "", currentUrl.toString());
    };

    const verifyBoardAssetVersion = async () => {
      if (stopped || checkInFlight) {
        return;
      }

      checkInFlight = true;
      try {
        const manifestUrl = new URL(BOARD_ASSET_MANIFEST_URL, window.location.origin);
        manifestUrl.searchParams.set("ww_asset_check", String(Date.now()));
        const response = await fetch(manifestUrl.toString(), {
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!response.ok || stopped) {
          return;
        }

        const manifest = await response.json();
        const scriptSources = Array.from(document.scripts).map((script) => script.src);
        if (!boardBundleIsOutdated(scriptSources, manifest)) {
          return;
        }

        const expectedPath = getBoardBundlePathFromManifest(manifest);
        const expectedAssetUrl = new URL(
          `/wp-content/themes/understrap-child/dist/${expectedPath}`,
          window.location.origin
        );
        expectedAssetUrl.searchParams.set("ww_asset_check", String(Date.now()));
        const assetResponse = await fetch(expectedAssetUrl.toString(), {
          cache: "no-store",
          credentials: "same-origin",
          method: "HEAD",
        });
        const assetContentType = String(assetResponse.headers.get("content-type") || "").toLowerCase();
        if (!assetResponse.ok || !assetContentType.includes("javascript") || stopped) {
          return;
        }

        const reloadMarker = `ww-board-asset-reload:${expectedPath}`;
        try {
          if (window.sessionStorage.getItem(reloadMarker) === "1") {
            return;
          }
          window.sessionStorage.setItem(reloadMarker, "1");
        } catch (error) {
          // A private browsing storage failure should not block the refresh.
        }

        const refreshUrl = new URL(window.location.href);
        refreshUrl.searchParams.set(BOARD_ASSET_REFRESH_PARAM, expectedPath.split("-").pop().replace(/\.js$/i, ""));
        window.location.replace(refreshUrl.toString());
      } catch (error) {
        // Asset verification is best-effort and must never interrupt the board.
      } finally {
        checkInFlight = false;
      }
    };

    const handleVisibleAssetCheck = () => {
      if (!document.hidden) {
        void verifyBoardAssetVersion();
      }
    };

    cleanAssetRefreshParam();
    void verifyBoardAssetVersion();
    document.addEventListener("visibilitychange", handleVisibleAssetCheck);
    window.addEventListener(BOARD_RESUME_REFRESH_EVENT, verifyBoardAssetVersion);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", handleVisibleAssetCheck);
      window.removeEventListener(BOARD_RESUME_REFRESH_EVENT, verifyBoardAssetVersion);
    };
  }, []);

  useEffect(() => {
    athletesRef.current = athletes;

    if (!Array.isArray(athletes) || athletes.length === 0) {
      return;
    }

    const authoritativeAthletes = athletes.filter((athlete) => !athlete?._boardOptimisticPreview);
    if (authoritativeAthletes.length === 0) {
      return;
    }

    if (sport && stat && !searchMode && !isTeamsViewSelected) {
      const selectionAthletes = filterAthletesForRequestedStat(
        filterAthletesForRequestedSport(
          authoritativeAthletes,
          sport,
          TOP_PLAYS_SPORT
        ),
        stat,
        POPULAR_STAT_KEY
      );
      const displayedSelectionKey = [
        normalizeSportKey(sport),
        normalizeProjectedStatKey(stat),
        String(selectedGameId || ""),
      ].join("|");
      if (selectionAthletes.length > 0) {
        displayedAthletesBySelectionRef.current.set(displayedSelectionKey, selectionAthletes);
      }
    }

    const nextSeenBoardAthletes = new Map(seenBoardAthletesRef.current);
    authoritativeAthletes.forEach((athlete) => {
      const normalizedAthlete = stripSelectionSyncMeta(stripBoardRefreshMeta(athlete));
      const variantKey = getAthleteVariantKey(normalizedAthlete);
      if (!variantKey) {
        return;
      }

      nextSeenBoardAthletes.set(variantKey, normalizedAthlete);
    });

    seenBoardAthletesRef.current = nextSeenBoardAthletes;
  }, [athletes, isTeamsViewSelected, searchMode, selectedGameId, sport, stat]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    writeSelectedAthletesToStorage(selected, {
      freeStorage: clearSelectedStoragePressure,
      storageKey: selectedStorageKey,
    });
  }, [selected]);

  const selectedSyncSignature = selected
    .map((athlete) => {
      const normalizedAthlete = normalizeSelectedAthlete(athlete) || {};
      return [
        getAthleteVariantKey(normalizedAthlete),
        String(normalizedAthlete?.stat ?? ""),
        String(normalizedAthlete?.selected ?? ""),
      ].join("|");
    })
    .join("||");
  const boardAthletesReady = athletes !== false;

  useEffect(() => {
    if (selected.length === 0) {
      selectedLineSyncRequestRef.current += 1;
      selectedLineSyncActiveSignatureRef.current = "";
      selectedLineSyncCompletedSignatureRef.current = "";
      selectedLineSyncQueuedRef.current = false;
      selectedLineSyncRefreshTicketsRef.current = {};
      selectedLineSyncRefreshDeadlineRef.current = 0;
      selectedLineSyncDeferredRetryRef.current = 0;
      selectedLineSyncRetrySignatureRef.current = "";
      if (selectedLineSyncRefreshTimerRef.current) {
        window.clearTimeout(selectedLineSyncRefreshTimerRef.current);
        selectedLineSyncRefreshTimerRef.current = null;
      }
      return;
    }

	if (!loggedIn) {
		return;
	}

    if (!boardAthletesReady || loading) {
      return;
    }

    void syncSelectedLineStatuses();
  }, [boardAthletesReady, loading, selectedSyncSignature, syncSelectedLineStatuses, selected.length]);

  useEffect(() => {
    if (!Array.isArray(athletes) || athletes.length === 0) {
      return undefined;
    }

    if (!athletes.some((athlete) => athlete?._boardRefreshState)) {
      return undefined;
    }

    const cleanupTimer = window.setTimeout(() => {
      setAthletes((currentAthletes) => clearBoardRefreshMeta(currentAthletes));
    }, BOARD_REFRESH_HIGHLIGHT_MS);

    return () => window.clearTimeout(cleanupTimer);
  }, [athletes]);

  // Inactivity Check, update stats every 15 minutes if no activity
  useEffect(() => {
    let lastActivityTime = Date.now();

    const resetTimer = () => {
      lastActivityTime = Date.now();
    };

    const checkInactivity = () => {
      if (isRegistrationFlowActive()) {
        return;
      }
      if (Date.now() - lastActivityTime > 15 * 60 * 1000) { // 15 minutes
        fetchInitialData();
        lastActivityTime = Date.now(); // Reset the timer after fetching data
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }

      // Foreground returns are handled by the dedicated 2-minute board refresh logic.
      // Do not let the inactivity timer trigger an extra fetch for time spent in the background.
      if (getBoardBackgroundedAt()) {
        lastActivityTime = Date.now();
        return;
      }

      checkInactivity();
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keypress", resetTimer);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(checkInactivity, 60 * 1000); // Check every minute

    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keypress", resetTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  // Near-real-time sync: poll a tiny feed version token and only refetch athletes when it changes.
  // Avoids full-page reloads and avoids redundant full athlete fetches.
  useEffect(() => {
    if (!sport || !stat || loading) return;
    if (stat === TEAMS_STAT_KEY) return;
    if (isTopPlaysSport(sport)) return;

    let stopped = false;

    const shouldCheck = () => {
      // Only check while the current board is actually visible.
      if (document.hidden) return false;
      if (isRegistrationFlowActive()) return false;
      return true;
    };

    const refreshIfChanged = async ({ force = false } = {}) => {
      if (stopped) return;
      if (feedCheckInFlightRef.current) return;
      if (!force && !shouldCheck()) return;

      const refreshSport = normalizeSportKey(sport);
      const refreshSelectionKey = [
        refreshSport,
        normalizeProjectedStatKey(stat),
        String(selectedGameId || ""),
      ].join("|");
      const startedWithEmptyBoard =
        !Array.isArray(athletesRef.current) || athletesRef.current.length === 0;
      const observedRequestToken = boardRequestTokenRef.current;
      const refreshIsCurrent = () => isBoardRefreshCurrent({
        stopped,
        observedRequestToken,
        currentRequestToken: boardRequestTokenRef.current,
        requestedSport: refreshSport,
        activeSport: activeSportRef.current,
      });

      feedCheckInFlightRef.current = true;
      if (startedWithEmptyBoard) {
        setEmptyBoardRefreshKey(refreshSelectionKey);
      }
      try {
        let meta = await FetchFeedVersion(sport, getShrkValue());
        if (!refreshIsCurrent() || !meta || !meta.version) return;

        const feedWasStale = feedMetaIsStale(meta);
        if (feedWasStale) {
          const refreshResult = await maybeRefreshFeedIfStale(sport, { force, meta });
          if (!refreshIsCurrent()) return;
          if (refreshResult?.updated) {
            const refreshedMeta = await FetchFeedVersion(sport, getShrkValue());
            if (!refreshIsCurrent()) return;
            if (refreshedMeta?.version) {
              meta = refreshedMeta;
            }
          }
        }

        const prev = feedVersionRef.current[refreshSport];
        const changed = prev && prev.version && prev.version !== meta.version;
        feedVersionRef.current[refreshSport] = meta;

        const isPopularStat = stat === POPULAR_STAT_KEY;
        const boardIsEmpty = !Array.isArray(athletesRef.current) || athletesRef.current.length === 0;
        const boardHasLiveProps = Array.isArray(athletesRef.current)
          && athletesRef.current.some((athlete) => Boolean(athlete?.is_live));
        const shouldVerifyInitialBoard =
          !boardIsEmpty && verifiedBoardVersionRef.current[refreshSport] !== meta.version;
        const shouldRefetchEmptyPopular = !changed && isPopularStat && boardIsEmpty;
        const shouldRefetchStaleLive = feedWasStale && boardHasLiveProps;

        if (changed || shouldVerifyInitialBoard || shouldRefetchEmptyPopular || shouldRefetchStaleLive) {
          if (!refreshIsCurrent()) {
            return;
          }
          if (athleteRequestLanesRef.current.hasForeground()) {
            // Do not mark the displayed board as verified just because another
            // athlete request was still finishing. Retry shortly and keep the
            // verification requirement until a current-source fetch succeeds.
            window.setTimeout(() => {
              if (!stopped) {
                refreshIfChanged({ force: true });
              }
            }, 300);
            return;
          }

          // A feed-version change means any in-memory or persisted board rows are
          // obsolete. Bypass them so a live stat count can swap on the card as soon
          // as the lightweight poll detects the new source file.
          if (changed || shouldVerifyInitialBoard || shouldRefetchStaleLive) {
            clearBoardAthleteCache(refreshSport);
          }

          // Event-wide All is assembled from real stat markets; it
          // has no projected_popular endpoint. Refreshing that synthetic stat
          // used to invalidate the valid event request and strand the skeleton.
          // Keep the same event-wide resolver for both the click and version
          // watcher paths, and never replace rendered rows with an empty result.
          const requestToken = startBoardRequest();
          const isEventWideRefresh = Boolean(selectedGameId && isPopularStat);
          const bypassCache = changed || shouldVerifyInitialBoard || shouldRefetchStaleLive;
          let athleteResult = isEventWideRefresh
            ? await fetchBoardEventStats(
                sport,
                selectedGameId,
                getEventRequestStats(selectedGame, stats),
                requestToken,
                {
                  cache: !bypassCache,
                  targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                  perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                  returnOnFirstResult: true,
                }
              )
            : await fetchBoardAthletes(
                sport,
                stat,
                selectedGameId || false,
                requestToken,
                { cache: !bypassCache }
              );
          if (
            isEventWideRefresh &&
            !athleteResult.aborted &&
            refreshIsCurrent() &&
            athleteResult.list.length === 0
          ) {
            athleteResult = await fetchBoardEventStats(
              sport,
              selectedGameId,
              getEventRequestStats(selectedGame, stats),
              requestToken,
              {
                cache: false,
                targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                returnOnFirstResult: true,
              }
            );
          }
          if (
            athleteResult.aborted ||
            stopped ||
            !isBoardRequestCurrent(requestToken) ||
            normalizeSportKey(sport) !== activeSportRef.current
          ) {
            return;
          }
          if (!athleteResult.failed && Array.isArray(athleteResult.list)) {
            verifiedBoardVersionRef.current[refreshSport] = meta.version;
            if (athleteResult.list.length > 0) {
              setAthletes((currentAthletes) => (
                isEventWideRefresh
                  ? appendAthleteList(currentAthletes, athleteResult.list)
                  : mergeAthleteList(currentAthletes, athleteResult.list)
              ));
            } else if (isEventWideRefresh) {
              eventUnavailableHandlerRef.current?.();
            }
          }
        }
      } catch (e) {
        console.error('Error during feed version refresh:', e);
      } finally {
        feedCheckInFlightRef.current = false;
        if (startedWithEmptyBoard) {
          setEmptyBoardRefreshKey((currentKey) => (
            currentKey === refreshSelectionKey ? "" : currentKey
          ));
        }
      }
    };

    // Prime the version immediately for the selected sport
    refreshIfChanged();

    // Poll every minute: metadata only, refetch full athletes only on change.
    const interval = setInterval(refreshIfChanged, 60 * 1000);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [sport, stat, stats, loading, maybeRefreshFeedIfStale, selectedGame, selectedGameId]);

  useEffect(() => {
    if (stat === TEAMS_STAT_KEY) {
      return undefined;
    }

    const expireStartedBoardCards = () => {
      const now = Date.now();
      pruneBoardAthleteCache(now);
      if (selectedGameId) {
        // Only a full event-market scan may close event mode. A provider row
        // can briefly lose its live flag during refresh; pruning it here used
        // to blank the board and shift every remaining card.
        return;
      }
      setAthletes((currentAthletes) => {
        const freshAthletes = filterFreshCachedBoardRows(currentAthletes, now);
        return freshAthletes.length === currentAthletes.length ? currentAthletes : freshAthletes;
      });
    };

    expireStartedBoardCards();
    const interval = window.setInterval(expireStartedBoardCards, 15 * 1000);
    return () => window.clearInterval(interval);
  }, [sport, stat, selectedGameId]);

  const syncTawkWidgetOffset = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const isMobileViewport =
      typeof window.matchMedia === "function" && window.matchMedia(MOBILE_LINEUP_SHEET_QUERY).matches;

    if (!isMobileViewport) {
      document.body.classList.remove(TAWK_CHAT_OPEN_BODY_CLASS);
      getTawkOffsetTargets().forEach(clearTawkTargetStyles);
      return;
    }

    const isChatOpen = window.wwIsTawkChatOpen === true;
    document.body.classList.toggle(TAWK_CHAT_OPEN_BODY_CLASS, isChatOpen);
    const stickyMenuHeight = isMobileViewport
      ? getVisibleElementHeight(document.querySelector(".sticky-menu"))
      : 0;
    const stickyFooterHeight = isMobileViewport
      ? getVisibleElementHeight(document.querySelector(".sticky-footer"))
      : 0;
    const bottomOffset = isChatOpen && isMobileViewport
      ? stickyMenuHeight + stickyFooterHeight + MOBILE_STICKY_CHAT_GAP_PX
      : 0;
    const frameHeight = getTawkConstrainedFrameHeight(bottomOffset);

    getTawkOffsetTargets().forEach((target) => {
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const nextZIndex = isChatOpen ? TAWK_WINDOW_Z_INDEX : TAWK_LAUNCHER_Z_INDEX;
      const shouldConstrainFrame = bottomOffset > 0 && shouldConstrainTawkFrame(target);

      if (
        target.style.getPropertyValue("z-index") !== String(nextZIndex) ||
        target.style.getPropertyPriority("z-index") !== "important"
      ) {
        target.style.setProperty("z-index", String(nextZIndex), "important");
      }

      if (bottomOffset > 0) {
        const nextBottom = `${bottomOffset}px`;

        if (
          target.style.getPropertyValue("bottom") !== nextBottom ||
          target.style.getPropertyPriority("bottom") !== "important"
        ) {
          target.style.setProperty("bottom", nextBottom, "important");
        }

        if (shouldConstrainFrame) {
          if (
            target.style.getPropertyValue("top") !== "auto" ||
            target.style.getPropertyPriority("top") !== "important"
          ) {
            target.style.setProperty("top", "auto", "important");
          }

          if (
            target.style.getPropertyValue("height") !== frameHeight ||
            target.style.getPropertyPriority("height") !== "important"
          ) {
            target.style.setProperty("height", frameHeight, "important");
          }
        } else {
          target.style.removeProperty("top");
          target.style.removeProperty("height");
        }

        if (
          (shouldConstrainFrame || target.tagName !== "IFRAME") &&
          (
            target.style.getPropertyValue("max-height") !== frameHeight ||
            target.style.getPropertyPriority("max-height") !== "important"
          )
        ) {
          target.style.setProperty("max-height", frameHeight, "important");
        }

        return;
      }

      clearTawkLayoutStyles(target);
    });
  }, []);

  const setTawkChatOpen = useCallback((isOpen) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const nextIsOpen = Boolean(isOpen);

    window.wwIsTawkChatOpen = nextIsOpen;
    document.body.classList.toggle(TAWK_CHAT_OPEN_BODY_CLASS, nextIsOpen);
    syncTawkWidgetOffset();
    window.requestAnimationFrame(syncTawkWidgetOffset);
    window.setTimeout(syncTawkWidgetOffset, 180);
    window.setTimeout(syncTawkWidgetOffset, 500);

    if (nextIsOpen) {
      const openAttemptToken = Date.now();
      window.wwTawkOpenAttemptToken = openAttemptToken;

      window.setTimeout(() => {
        if (
          window.wwIsTawkChatOpen === true &&
          window.wwTawkOpenAttemptToken === openAttemptToken &&
          getTawkOffsetTargets().length === 0
        ) {
          window.wwIsTawkChatOpen = false;
          document.body.classList.remove(TAWK_CHAT_OPEN_BODY_CLASS);
          syncTawkWidgetOffset();
        }
      }, TAWK_OPEN_TARGET_TIMEOUT_MS);
    }
  }, [syncTawkWidgetOffset]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    window.wwSyncTawkWidgetOffset = syncTawkWidgetOffset;

    let frameId = 0;
    const mediaQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia(MOBILE_LINEUP_SHEET_QUERY)
        : null;
    const scheduleSync = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        syncTawkWidgetOffset();
      });
    };
    const mutationObserver =
      typeof MutationObserver === "function"
        ? new MutationObserver(scheduleSync)
        : null;

    if (mutationObserver) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }

    window.addEventListener("resize", scheduleSync);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", scheduleSync);
      window.visualViewport.addEventListener("scroll", scheduleSync);
    }

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", scheduleSync);
      } else {
        mediaQuery.addListener(scheduleSync);
      }
    }

    scheduleSync();

    return () => {
      delete window.wwSyncTawkWidgetOffset;

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      mutationObserver?.disconnect();
      window.removeEventListener("resize", scheduleSync);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", scheduleSync);
        window.visualViewport.removeEventListener("scroll", scheduleSync);
      }

      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === "function") {
          mediaQuery.removeEventListener("change", scheduleSync);
        } else {
          mediaQuery.removeListener(scheduleSync);
        }
      }

      getTawkOffsetTargets().forEach((target) => {
        clearTawkTargetStyles(target);
      });
    };
  }, [syncTawkWidgetOffset]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const tawkApi = window.Tawk_API = window.Tawk_API || {};
    const previousOnLoad = tawkApi.onLoad;
    const previousOnChatMaximized = tawkApi.onChatMaximized;
    const previousOnChatMinimized = tawkApi.onChatMinimized;
    const previousOnChatHidden = tawkApi.onChatHidden;

    const handleOnLoad = function(...args) {
      if (typeof previousOnLoad === "function") {
        previousOnLoad.apply(this, args);
      }

      if (typeof tawkApi.isChatMaximized === "function") {
        setTawkChatOpen(tawkApi.isChatMaximized());
        return;
      }

      syncTawkWidgetOffset();
    };
    const handleOnChatMaximized = function(...args) {
      if (typeof previousOnChatMaximized === "function") {
        previousOnChatMaximized.apply(this, args);
      }

      setTawkChatOpen(true);
    };
    const handleOnChatMinimized = function(...args) {
      if (typeof previousOnChatMinimized === "function") {
        previousOnChatMinimized.apply(this, args);
      }

      setTawkChatOpen(false);
    };
    const handleOnChatHidden = function(...args) {
      if (typeof previousOnChatHidden === "function") {
        previousOnChatHidden.apply(this, args);
      }

      setTawkChatOpen(false);
    };

    tawkApi.onLoad = handleOnLoad;
    tawkApi.onChatMaximized = handleOnChatMaximized;
    tawkApi.onChatMinimized = handleOnChatMinimized;
    tawkApi.onChatHidden = handleOnChatHidden;

    return () => {
      if (tawkApi.onLoad === handleOnLoad) {
        tawkApi.onLoad = previousOnLoad;
      }
      if (tawkApi.onChatMaximized === handleOnChatMaximized) {
        tawkApi.onChatMaximized = previousOnChatMaximized;
      }
      if (tawkApi.onChatMinimized === handleOnChatMinimized) {
        tawkApi.onChatMinimized = previousOnChatMinimized;
      }
      if (tawkApi.onChatHidden === handleOnChatHidden) {
        tawkApi.onChatHidden = previousOnChatHidden;
      }
    };
  }, [setTawkChatOpen, syncTawkWidgetOffset]);

  const handleChatClick = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }

    if (typeof window === "undefined") {
      return;
    }

    if (typeof window.wwOpenTawkChat === "function") {
      setTawkChatOpen(true);
      window.wwOpenTawkChat();
    } else if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
      setTawkChatOpen(true);
      window.Tawk_API.maximize();
    } else {
      return;
    }

    setTawkChatOpen(true);
  }, [setTawkChatOpen]);

  const getAthletes = async (
    sport,
    stat,
    attemptedStats = [],
    attemptedSports = [],
    retryAfterCacheClear = false,
    autoSwitchToastShown = false,
    statsObj = null,
    options = {}
  ) => {
    try {
      const requestToken = options.requestToken ?? startBoardRequest();
      const requestOptions = { ...options, requestToken };
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }

      const allowAutoSwitch = options.allowAutoSwitch !== false;
      const preserveAvailableStatsOnEmpty = options.preserveAvailableStatsOnEmpty === true;
      const initialFetchLimit = getInitialAthleteLimit(stat, options);
      const activeGameFilter =
        options.gameId !== undefined
          ? options.gameId
          : (selectedGameId || false);
      const athleteResult = await fetchBoardAthletes(
        sport,
        stat,
        activeGameFilter,
        requestToken,
        initialFetchLimit > 0 ? { limit: initialFetchLimit } : {}
      );
      if (athleteResult.aborted) {
        return;
      }
      let response = athleteResult.list;
      let stats = statsObj || verifiedStats[sport] || {};
			if (athleteResult.failed) {
				if (!isBoardRequestCurrent(requestToken)) {
					return;
				}
				setLoading(false);
				setSport(sport);
				setStats(stats);
				setStat(stat);
				setAthletesLoadFailed(true);
				setAthletes([]);
				setAthletesLoading(false);
				showBoardRefreshWarning();
				return;
			}
			setAthletesLoadFailed(false);
      dismissBoardRefreshWarning();
      const newAttemptedStats = [...attemptedStats, stat];
      const newAttemptedSports = attemptedSports.includes(sport) ? attemptedSports : [...attemptedSports, sport];

      if (isTopPlaysSport(sport) && (response === undefined || response.length === 0)) {
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }
        const recovered = await recoverEmptyTopPlays({
          requestToken,
          availableSports: options.availableSports || sports,
          statsMapBySport: options.statsMapBySport || verifiedStats,
          selectedStat: stat,
          allowFallback: allowAutoSwitch,
        });

        if (!recovered) {
          setLoading(false);
          setSport(sport);
          setStats(stats);
          setStat(stat);
          setAthletes([]);
          setAthletesLoading(false);
        }
        return;
      }

      if ((response === undefined || response.length === 0) && Object.keys(stats).length > 0) {
        // If we haven't cleared cache yet and this isn't already a retry after cache clear, try clearing cache
        if (
          stat !== POPULAR_STAT_KEY &&
          !preserveAvailableStatsOnEmpty &&
          !clearedSportsRef.current.has(sport) &&
          !retryAfterCacheClear
        ) {
          const cacheCleared = await ClearSportCache(sport);

          if (!isBoardRequestCurrent(requestToken)) {
            return;
          }

          if (cacheCleared) {
            cacheClearedRef.current = true;
            clearedSportsRef.current.add(sport);
            setCacheCleared(true);
            clearStatsCache();
            clearBoardAthleteCache();

            const refreshed = await refreshStatsForSport(sport, stat, { requestToken, force: true });
            if (!isBoardRequestCurrent(requestToken)) {
              return;
            }

            const retrySport = refreshed?.sport || sport;
            const retryStats = refreshed?.stats || verifiedStats[retrySport] || {};
            const retryStat =
              refreshed?.stat ||
              (Object.prototype.hasOwnProperty.call(retryStats, stat) ? stat : (Object.keys(retryStats)[0] || stat));

            // Retry the same sport/stat after clearing cache
            return getAthletes(
              retrySport,
              retryStat,
              attemptedStats,
              attemptedSports,
              true,
              autoSwitchToastShown,
              retryStats,
              requestOptions
            );
          }
        }

        if (!allowAutoSwitch) {
          if (!isBoardRequestCurrent(requestToken)) {
            return;
          }
          setLoading(false);
          setSport(sport);
          setStats(stats);
          setStat(stat);
          setAthletes([]);
          setAthletesLoading(false);
          return;
        }

        let availableSports = sports;
        let statsMapBySport = verifiedStats;
        let nextStats = stats;

        if (
          stat !== POPULAR_STAT_KEY &&
          !preserveAvailableStatsOnEmpty &&
          !shouldSkipRemainingStatsForSport(newAttemptedStats, nextStats)
        ) {
          // Ensure server-side cache/stat update runs: clear sport cache (once per session) then refresh stats
          try {
            if (!clearedSportsRef.current.has(sport)) {
              const cacheClearedNow = await ClearSportCache(sport);
              if (cacheClearedNow) {
                cacheClearedRef.current = true;
                clearedSportsRef.current.add(sport);
                setCacheCleared(true);
                clearStatsCache();
                clearBoardAthleteCache();
              }
            }

            const updatedVerifiedStats = await FetchStats({ force: true });
            if (updatedVerifiedStats) {
              if (!isBoardRequestCurrent(requestToken)) {
                return;
              }
              availableSports = Object.keys(updatedVerifiedStats);
              statsMapBySport = updatedVerifiedStats;
              setVerifiedStats(updatedVerifiedStats);
              setSports(availableSports);
              nextStats = updatedVerifiedStats[sport] || {};
              stats = nextStats;
              setStats(nextStats);
            }
          } catch (e) {
            console.error('Error clearing cache or refreshing stats:', e);
          }
        }

        const limitReached = shouldSkipRemainingStatsForSport(newAttemptedStats, nextStats);
        const nextStat = limitReached ? "" : getNextUnattemptedStat(nextStats, stat, newAttemptedStats);

        if (nextStat) {
          // Try next stat in same sport
          setStats(nextStats);
          setStat(nextStat);
          if (!autoSwitchToastShown && stat !== POPULAR_STAT_KEY) {
            toastr["info"](`No athletes found. Trying ${nextStats[nextStat]}...`, "Auto-switching");
          }
          // Request athletes for next stat
          return getAthletes(sport, nextStat, newAttemptedStats, newAttemptedSports, false, true, nextStats, requestOptions);
        }

        // All stats exhausted in current sport, or the auto-switch failure cap was reached.
        const nextSportSelection = getNextSportSelection(availableSports, newAttemptedSports, statsMapBySport);
        if (nextSportSelection) {
          if (preserveAvailableStatsOnEmpty) {
            setVerifiedStats(statsMapBySport);
            setSports(availableSports);
            setSport(nextSportSelection.sport);
            setStats(nextSportSelection.stats);
            setStat(nextSportSelection.stat);
          } else {
            pruneSportFromAvailableStats(
              statsMapBySport,
              availableSports,
              sport,
              nextSportSelection.sport,
              nextSportSelection.stat
            );
          }
          if (!autoSwitchToastShown && stat !== POPULAR_STAT_KEY) {
            const skipReason = limitReached
              ? `${newAttemptedStats.length} empty stats in ${sport}. Switching to ${nextSportSelection.sport}...`
              : `No athletes in ${sport}. Switching to ${nextSportSelection.sport}...`;
            toastr["info"](skipReason, "Auto-switching");
          }
          return getAthletes(
            nextSportSelection.sport,
            nextSportSelection.stat,
            [],
            newAttemptedSports,
            false,
            true,
            nextSportSelection.stats,
            requestOptions
          );
        }

        // No more sports/stats to try
        if (preserveAvailableStatsOnEmpty) {
          setVerifiedStats(statsMapBySport);
          setSports(availableSports);
          setSport(sport);
          setStats(stats);
          setStat(stat);
        } else {
          pruneSportFromAvailableStats(statsMapBySport, availableSports, sport);
        }
        toastr["warning"]("No athletes currently available. Try again later.", "No Athletes Found");
        setLoading(false);
        setAthletes([]);
        setAthletesLoading(false);
      } else if (response && response.length > 0) {
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }
        setLoading(false);
        setAthletes(response);
        setSport(sport);
        setStats(stats);
        setStat(stat);
        setAthletesLoading(false);
        if (shouldHydrateBoardAthleteResult(athleteResult, initialFetchLimit)) {
          scheduleBoardAthleteHydration(sport, stat, requestToken);
        }
      } else {
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }
        setLoading(false);
        setSport(sport);
        setStats(stats);
        setStat(stat);
        setAthletes([]);
        setAthletesLoading(false);
      }
    } catch (error) {
      console.error("Error in getAthletes:", error);
      setLoading(false);
      setAthletes([]);
      setAthletesLoading(false);
    }
  };

  const refreshCurrentBoard = useCallback(async (options = {}) => {
    if (isRegistrationFlowActive()) {
      return;
    }

    const preferredSport = sport || sports[0] || "";
    const currentStats = preferredSport ? (verifiedStats[preferredSport] || stats || {}) : {};
    const isEventWideStatView = Boolean(selectedGameId) && (!stat || stat === POPULAR_STAT_KEY);
    const preferredStat = stat || Object.keys(currentStats)[0] || "";

    if (!preferredSport) {
      setAthletesLoading(true);
      await fetchInitialData();
      return;
    }

    try {
      if (!options.skipFeedRefresh) {
        await maybeRefreshFeedIfStale(preferredSport, { force: true });
      }
      const requestToken = startBoardRequest();
      const refreshed = await FetchBoardBootstrap({
        sport: preferredSport,
        stat: preferredStat,
      });
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }
      syncActivePromoSummaryFromBootstrap(refreshed);
      let nextAvailableStats = refreshed?.availableStats || {};
      let nextSports = Array.isArray(refreshed?.sports) ? refreshed.sports : Object.keys(nextAvailableStats);
      let nextSport = refreshed?.sport || preferredSport;
      let nextStats = refreshed?.stats || nextAvailableStats[nextSport] || {};
      let nextStat =
        refreshed?.stat ||
        (Object.prototype.hasOwnProperty.call(nextStats, preferredStat)
          ? preferredStat
          : (Object.keys(nextStats)[0] || preferredStat));
      const activeGameFilter =
        nextSport === preferredSport ? (selectedGameId || false) : false;
      const nextAthletes = activeGameFilter
        ? []
        : Array.isArray(refreshed?.athletes)
        ? filterAthleteList(refreshed.athletes)
        : [];

      const hasResolvedSelection =
        Boolean(nextSport && nextStat) && Object.keys(nextStats).length > 0;
      if (!hasResolvedSelection) {
        const recoveredSelection = await recoverBoardSelection(preferredSport, preferredStat, requestToken);
        if (!isBoardRequestCurrent(requestToken)) {
          return;
        }

        if (!recoveredSelection?.sport || !recoveredSelection?.stat) {
          setAthletesLoading(false);
          return;
        }

        nextAvailableStats = recoveredSelection.verifiedStats || {};
        nextSports = recoveredSelection.sports || Object.keys(nextAvailableStats);
        nextSport = recoveredSelection.sport;
        nextStats = recoveredSelection.stats || nextAvailableStats[nextSport] || {};
        nextStat = recoveredSelection.stat;
      }

      if (nextAthletes.length === 0) {
        if (Object.keys(nextAvailableStats).length > 0) {
          setVerifiedStats(nextAvailableStats);
          setSports(nextSports);
        }

        if (activeGameFilter && isEventWideStatView) {
          const refreshedEventStats = selectedGame?._topPlaysLeague
            ? nextStats
            : getEventRequestStats(selectedGame, nextStats);
          setSport(nextSport);
          setStats(nextStats);
          setStat(POPULAR_STAT_KEY);
          setAthletesLoading(true);

          let athleteResult = await fetchBoardEventStats(
            nextSport,
            activeGameFilter,
            refreshedEventStats,
            requestToken,
            {
              targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
              perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
              maxStats: 1,
              returnOnFirstResult: true,
            }
          );
          if (
            !athleteResult.aborted &&
            isBoardRequestCurrent(requestToken) &&
            athleteResult.list.length === 0
          ) {
            athleteResult = await fetchBoardEventStats(
              nextSport,
              activeGameFilter,
              refreshedEventStats,
              requestToken,
              {
                cache: false,
                targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                returnOnFirstResult: true,
              }
            );
          }
          if (athleteResult.aborted || !isBoardRequestCurrent(requestToken)) {
            return;
          }

          setAthletesLoading(false);
          if (athleteResult.list.length > 0) {
            setAthletes((currentAthletes) => appendAthleteList(currentAthletes, athleteResult.list));
            scheduleBoardEventStatsHydration(nextSport, activeGameFilter, refreshedEventStats, requestToken);
          } else if (!athleteResult.failed) {
            eventUnavailableHandlerRef.current?.();
          } else {
            showBoardRefreshWarning();
          }
          return;
        }

        return getAthletes(
          nextSport,
          nextStat,
          [],
          [],
          clearedSportsRef.current.has(nextSport),
          false,
          nextStats,
          {
            requestToken,
            visibleLimit: false,
            gameId: activeGameFilter,
            availableSports: nextSports,
            statsMapBySport: nextAvailableStats,
          }
        );
      }

      setVerifiedStats(nextAvailableStats);
      setSports(nextSports);
      setSport(nextSport);
      setStats(nextStats);
      setStat(nextStat);
      setAthletes((currentAthletes) => mergeAthleteList(currentAthletes, nextAthletes));
      setAthletesLoading(false);
    } catch (error) {
      console.error("Error refreshing current board:", error);
      setAthletesLoading(false);
    }
  }, [sport, sports, stat, stats, verifiedStats, fetchInitialData, maybeRefreshFeedIfStale, selectedGame, selectedGameId, syncActivePromoSummaryFromBootstrap]);

  useEffect(() => {
    let disposed = false;

    const schedulePendingResumeRefresh = () => {
      if (
        disposed ||
        document.hidden ||
        resumeRefreshRetryTimerRef.current !== null
      ) {
        return;
      }

      resumeRefreshRetryTimerRef.current = window.setTimeout(() => {
        resumeRefreshRetryTimerRef.current = null;
        runResumeRefresh();
      }, BOARD_RESUME_REFRESH_RETRY_MS);
    };

    const runResumeRefresh = () => {
      if (disposed || document.hidden || !resumeRefreshPendingRef.current) {
        return;
      }

      if (shouldDeferBoardResumeRefresh({
        loading,
        initialLoadInFlight: initialBoardLoadInFlightRef.current,
        bootstrapPending: window.__WW_BOARD_BOOTSTRAP_PENDING === true,
        refreshInFlight: resumeRefreshInFlightRef.current,
      })) {
        schedulePendingResumeRefresh();
        return;
      }

      resumeRefreshPendingRef.current = false;
      resumeRefreshInFlightRef.current = true;
      Promise.resolve(refreshCurrentBoard()).finally(() => {
        resumeRefreshInFlightRef.current = false;
        if (resumeRefreshPendingRef.current) {
          schedulePendingResumeRefresh();
        }
      });
    };

    const requestResumeRefresh = () => {
      resumeRefreshPendingRef.current = true;
      runResumeRefresh();
    };

    const shouldForceResumeRefresh = () => {
      const isPopularStat = stat === POPULAR_STAT_KEY;
      const boardIsEmpty = !Array.isArray(athletesRef.current) || athletesRef.current.length === 0;
      return isPopularStat && boardIsEmpty;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        markBoardBackgrounded();
        return;
      }

      if (resumeRefreshPendingRef.current) {
        runResumeRefresh();
        return;
      }

      if (shouldForceResumeRefresh()) {
        requestResumeRefresh();
        return;
      }

      if (!consumeBoardReturnRefreshSignal()) {
        return;
      }

      requestResumeRefresh();
    };

    const handleBoardResumeRefresh = () => {
      // Median keeps the board WebView alive between app sessions. Always
      // reconcile the server-provided league list on a native resume so a
      // removed league cannot remain mounted from an older web deployment.
      consumeBoardReturnRefreshSignal();
      requestResumeRefresh();
    };

    const handleBootstrapSettled = () => {
      if (resumeRefreshPendingRef.current) {
        runResumeRefresh();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(BOARD_RESUME_REFRESH_EVENT, handleBoardResumeRefresh);
    window.addEventListener(BOARD_BOOTSTRAP_SETTLED_EVENT, handleBootstrapSettled);
    if (resumeRefreshPendingRef.current && !document.hidden) {
      runResumeRefresh();
    }

    return () => {
      disposed = true;
      if (resumeRefreshRetryTimerRef.current !== null) {
        window.clearTimeout(resumeRefreshRetryTimerRef.current);
        resumeRefreshRetryTimerRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(BOARD_RESUME_REFRESH_EVENT, handleBoardResumeRefresh);
      window.removeEventListener(BOARD_BOOTSTRAP_SETTLED_EVENT, handleBootstrapSettled);
    };
  }, [loading, refreshCurrentBoard, stat]);

  const captureEventModeSnapshot = () => {
    if (eventModeSnapshotRef.current) {
      return;
    }

    eventModeSnapshotRef.current = {
      athletes,
      athletesLoading,
      searchMode,
      searchQuery,
      sport,
      stat,
      stats,
      teamsHubOpen,
      scrollY: window.scrollY,
    };
  };

  const restoreBoardFromEventMode = () => {
    const snapshot = eventModeSnapshotRef.current;
    if (!snapshot) {
      return;
    }

    startBoardRequest();
    eventModeSnapshotRef.current = null;
    setSelectedGameId("");
    setSelectedGame(null);
    setSport(snapshot.sport);
    setStat(snapshot.stat);
    setStats(snapshot.stats);
    setAthletes(snapshot.athletes);
    setAthletesLoading(snapshot.athletesLoading);
    setSearchMode(snapshot.searchMode);
    setSearchQuery(snapshot.searchQuery);
    setTeamsHubOpen(snapshot.teamsHubOpen);
    setBoardLoadingMoreAthletes(false);

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: snapshot.scrollY, behavior: "auto" });
    });
  };

  const rejectUnavailableEvent = (game, targetLeague) => {
    const key = getEventAvailabilityKey(game, targetLeague);
    const now = Date.now();
    setRejectedEvents((current) => ({
      ...Object.fromEntries(Object.entries(current).filter(([, expires]) => expires > now)),
      [key]: now + 60 * 1000,
    }));
    clearCachedGamesForSport(targetLeague);
    setTopPlaysLiveEventCards((currentCards) => currentCards.filter(
      (card) => getEventAvailabilityKey(card, targetLeague) !== key
    ));
  };

  const exitUnavailableEvent = (gameIdOverride = "") => {
    const closedGameId = String(gameIdOverride || selectedGameId || "");
    if (!closedGameId || !eventModeSnapshotRef.current) {
      return;
    }

    rejectUnavailableEvent({ ...selectedGame, id: closedGameId }, sport);
    restoreBoardFromEventMode();
    toastr["warning"](
      "That live event no longer has selectable offers. Your line-up was kept and the previous board is back.",
      "Event closed"
    );
    void reportBoardIssue("active-event-closed", {
      sport,
      stat,
      gameId: closedGameId,
      selectedCount: selectedRef.current.length,
    });
  };
  eventUnavailableHandlerRef.current = exitUnavailableEvent;

  // Validate the destination before changing any visible board state. Both
  // hero and rail clicks use this path; an empty/error response keeps shopping
  // context and picks intact rather than flashing an empty event board.
  const openEventBoard = async (game, targetLeague) => {
    if (!game?.id || !targetLeague || isTopPlaysSport(targetLeague)) return;
    const eventKey = getEventAvailabilityKey(game, targetLeague);
    if (pendingEventRef.current === eventKey) return;
    if (Number(rejectedEvents[eventKey]) > Date.now()) return;

    const requestToken = startBoardRequest();
    pendingEventRef.current = eventKey;
    setPendingEventKey(eventKey);
    const nextStats = verifiedStats[targetLeague] || (targetLeague === sport ? stats : null)
      || game.available_stats || {};
    const currentEventStats = getEventRequestStats(game, nextStats);

    try {
      let eventResult = await fetchBoardEventStats(
        targetLeague,
        game.id,
        currentEventStats,
        requestToken,
        {
          targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
          perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
          cache: false,
          maxStats: 1,
          returnOnFirstResult: true,
        }
      );

      // One closed market does not mean the whole event closed. Confirm every
      // advertised market, returning immediately when any selection survives.
      if (
        !eventResult.aborted &&
        !eventResult.failed &&
        isBoardRequestCurrent(requestToken) &&
        eventResult.list.length === 0
      ) {
        eventResult = await fetchBoardEventStats(
          targetLeague,
          game.id,
          currentEventStats,
          requestToken,
          {
            targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
            perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
            cache: false,
            returnOnFirstResult: true,
          }
        );
      }

      if (eventResult.aborted || !isBoardRequestCurrent(requestToken)) {
        return;
      }
      if (eventResult.list.length === 0) {
        if (eventResult.failed) {
          toastr["warning"]("We couldn't check that event. Please try again.", "Connection issue");
        } else {
          rejectUnavailableEvent(game, targetLeague);
          toastr["info"]("That event has no selectable offers right now.", "Event unavailable");
        }
        void reportBoardIssue(eventResult.failed ? "event-check-failed" : "event-check-empty", {
          sport: targetLeague, gameId: game.id, selectedCount: selectedRef.current.length,
        });
        return;
      }

      captureEventModeSnapshot();
      setSport(targetLeague);
      setStats(nextStats);
      setSelectedGameId(String(game.id));
      setSelectedGame(game);
      setStat(POPULAR_STAT_KEY);
      setSearchResetSignal((currentSignal) => currentSignal + 1);
      setSearchMode(false);
      setSearchQuery("");
      setTeamsHubOpen(false);
      if (stat === TEAMS_STAT_KEY) cancelTeamLineupValidation();
      setAthletes(eventResult.list);
      setAthletesLoading(false);
      scheduleBoardEventStatsHydration(targetLeague, game.id, currentEventStats, requestToken);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      });
    } catch (error) {
      if (!isBoardRequestCurrent(requestToken)) {
        return;
      }

      console.error("Error checking event athletes:", error);
      toastr["warning"]("We couldn't check that event. Please try again.", "Connection issue");
    } finally {
      if (isBoardRequestCurrent(requestToken)) {
        pendingEventRef.current = "";
        setPendingEventKey("");
      }
    }
  };

  const onGameSelect = (game) => openEventBoard(game, sport);
  const onTopPlaysEventSelect = (game) => openEventBoard(game, String(game?._topPlaysLeague || "").toLowerCase());

  const boardDataSource = createBoardDataSource({
    fetchStatsSelection: FetchStatsSelection,
    fetchAthletes: fetchBoardAthletes,
    getRecentVerifiedAthletes: (selectedSport, selectedStat, limit, onStatus) =>
      recentVerifiedBoardCacheRef.current.get(getRecentVerifiedBoardKey(selectedSport, selectedStat, false, limit), onStatus),
    isRequestCurrent: isBoardRequestCurrent,
    normalizeSport: normalizeSportKey,
    getInitialAthleteLimit,
    getVerifiedStats: () => verifiedStats,
    getCurrentSports: () => sports,
    maxStatProbes: MAX_AUTO_SWITCH_EMPTY_STATS_PER_SPORT,
    excludedStats: [TEAMS_STAT_KEY],
  });

  const commitBoardSnapshot = (snapshot) => {
    setVerifiedStats(snapshot.availableStats);
    setSports(snapshot.sports);
    setSelectedGameId(snapshot.selectedGameId);
    setSelectedGame(snapshot.selectedGame);
    setSport(snapshot.sport);
    setStats(snapshot.stats);
    setStat(snapshot.stat);
    setAthletes(snapshot.athletes);
    setAthletesLoadFailed(false);
    setAthletesLoading(false);
    setSportStatsLoading(false);
  };

  // Explicit league/stat navigation is transactional. BoardDataSource verifies
  // a destination and returns one immutable snapshot; React commits that board
  // snapshot together while lineup state remains outside the transaction.
  const loadAtomicBoardSelection = async (
    requestedSport,
    preferredStat = "",
    knownStats = null,
    requestToken = null,
    reuseRecent = false
  ) => {
    const currentRequestToken = requestToken ?? startBoardRequest();
    const normalizedRequestedSport = normalizeSportKey(requestedSport);
    if (!normalizedRequestedSport || !isBoardRequestCurrent(currentRequestToken)) {
      return false;
    }

    beginAtomicBoardTransition(currentRequestToken, normalizedRequestedSport, preferredStat);
    dismissBoardRefreshWarning();
    let transitionOutcome = "error";
    let transitionSnapshot = null;
    let transitionError = "failed";
    let transitionDiagnostics = {};

    try {
      const result = await boardDataSource.resolveSelection({
        requestToken: currentRequestToken,
        requestedSport: normalizedRequestedSport,
        preferredStat,
        knownStats,
        reuseRecent,
      });
      transitionDiagnostics = result.diagnostics || {};

      if (result.outcome === "aborted") {
        transitionError = "aborted";
        return false;
      }
      if (result.outcome !== "ready") {
        transitionError = result.outcome;
        if (result.outcome === "failed") {
          if (result.error) {
            throw result.error;
          }
          throw new Error("The destination could not be refreshed.");
        }

        toastr["info"](
          `${String(result.targetSport || normalizedRequestedSport).toUpperCase()} has no selectable offers right now. Your current board was kept.`,
          "No Selections Available"
        );
        return false;
      }

      if (!isBoardRequestCurrent(currentRequestToken)) {
        return false;
      }

      transitionOutcome = "ready";
      transitionSnapshot = result.snapshot;
      transitionError = "";
      const committedSelectionKey = [
        normalizeSportKey(result.snapshot.sport),
        normalizeProjectedStatKey(result.snapshot.stat),
        "",
      ].join("|");
      boardSelectionPerformanceKeyRef.current = committedSelectionKey;
      boardSelectionPerformanceTrackerRef.current?.update({
        athletesCount: result.snapshot.athletes.length,
        sport: result.snapshot.sport,
        stat: result.snapshot.stat,
        reason: "atomic-selection-resolved",
        stageTimings: {
          selectionMetadataMs: transitionDiagnostics.metadataMs,
          selectionAthletesMs: transitionDiagnostics.athleteMs,
          selectionResolveMs: transitionDiagnostics.totalMs,
          selectionCacheStatus: transitionDiagnostics.cacheStatus,
          selectionCacheHit: transitionDiagnostics.verifiedCacheHit ? 1 : 0,
        },
      });
      commitBoardSnapshot(result.snapshot);

      if (result.committedStat !== result.targetStat && result.targetStat) {
        toastr["info"](
          "That market closed, so we moved you to the next available stat.",
          "Market updated"
        );
      }
      if (result.targetSport !== normalizedRequestedSport) {
        toastr["info"](
          `${normalizedRequestedSport.toUpperCase()} is no longer available. Showing ${result.targetSport.toUpperCase()}.`,
          "League updated"
        );
      }

      const committedLimit = getInitialAthleteLimit(result.committedStat);
      if (shouldHydrateBoardAthleteResult(result.athleteResult, committedLimit)) {
        scheduleBoardAthleteHydration(result.targetSport, result.committedStat, currentRequestToken);
      }
      return true;
    } catch (error) {
      if (!isBoardRequestCurrent(currentRequestToken)) {
        return false;
      }
      console.error("Error checking board destination:", error);
      toastr["warning"](
        "We couldn't verify that board. Please try again. Your current board and line-up were kept.",
        "Connection issue"
      );
      return false;
    } finally {
      if (isBoardRequestCurrent(currentRequestToken)) {
        if (transitionOutcome !== "ready") {
          const performanceOutcome = transitionError === "empty"
            ? "empty"
            : (transitionError === "aborted" ? "abandoned" : "error");
          finishBoardSelectionPerformance(performanceOutcome, {
            athletesCount: 0,
            sport: normalizedRequestedSport,
            stat: preferredStat,
            reason: `atomic-selection-${transitionError || "failed"}`,
            stageTimings: {
              selectionMetadataMs: transitionDiagnostics.metadataMs,
              selectionAthletesMs: transitionDiagnostics.athleteMs,
              selectionResolveMs: transitionDiagnostics.totalMs,
            },
          });
        }
        finishAtomicBoardTransition(
          currentRequestToken,
          transitionOutcome,
          transitionSnapshot,
          transitionError
        );
        setSportStatsLoading(false);
      }
    }
  };

  // Helper to handle stat change and fallback to next stat if no athletes found
  const handleStatChange = async (
    selectedSport,
    statValue,
    statsObj = null,
    attemptedStats = [],
    retryAfterCacheClear = false,
    autoSwitchToastShown = false,
    requestToken = null,
    preserveSelectedSport = false
  ) => {
    const currentRequestToken = requestToken ?? startBoardRequest();
    if (!isBoardRequestCurrent(currentRequestToken)) {
      return;
    }

    let stats = statsObj || verifiedStats[selectedSport] || {};
    const initialFetchLimit = getInitialAthleteLimit(statValue);
    const activeGameFilter = selectedSport === sport ? (selectedGameId || false) : false;
    const cachedAthleteResult = getCachedBoardAthleteResult(
      selectedSport,
      statValue,
      activeGameFilter,
      initialFetchLimit
    );
    const displayedSelectionKey = [
      normalizeSportKey(selectedSport),
      normalizeProjectedStatKey(statValue),
      String(activeGameFilter || ""),
    ].join("|");
    const displayedAthletes = filterAthletesForRequestedStat(
      filterAthletesForRequestedSport(
        filterFreshCachedBoardRows(
          displayedAthletesBySelectionRef.current.get(displayedSelectionKey) || []
        ),
        selectedSport,
        TOP_PLAYS_SPORT
      ),
      statValue,
      POPULAR_STAT_KEY
    );
    const optimisticPreview = getOptimisticAthletePreview(selectedSport, statValue, stats);
    const hasCompleteOptimisticPreview = hasCompleteAthleteSelectionPreview(
      optimisticPreview,
      initialFetchLimit
    );

    setStat(statValue);
    setStats(stats);
		setAthletesLoadFailed(false);

    // League buttons optimistically clear the previous card set. Apply a valid
    // cache hit in the same event turn so React never commits the transient
    // empty/skeleton frame that made cached league switches flash.
    if (cachedAthleteResult && cachedAthleteResult.list.length > 0) {
      setAthletes(cachedAthleteResult.list);
      setAthletesLoading(false);
      if (shouldHydrateBoardAthleteResult(cachedAthleteResult, initialFetchLimit)) {
        scheduleBoardAthleteHydration(selectedSport, statValue, currentRequestToken);
      }
      return;
    }

    // A cache version can roll while another league is selected. Keep the
    // last successfully rendered rows visible during that background refresh
    // instead of replacing them with a one-frame skeleton.
    if (displayedAthletes.length > 0) {
      setAthletes(displayedAthletes);
      setAthletesLoading(false);
    } else if (hasCompleteOptimisticPreview) {
      // The preview comes from this viewer's already-filtered board response.
      // Only paint a full initial batch. A sparse preview (often one alternate
      // market from the previous board) creates a misleading one-card flash
      // before the authoritative response replaces it with different athletes.
      // Keep accepted preview rows non-interactive until that response arrives.
      setAthletes(optimisticPreview);
      setAthletesLoading(true);
    } else {
      setAthletesLoading(true);
    }

    // Track which stats we've already attempted
    const newAttemptedStats = [...attemptedStats, statValue];

    try {
      const athleteResult = await fetchBoardAthletes(
        selectedSport,
        statValue,
        activeGameFilter,
        currentRequestToken,
        {
          limit: initialFetchLimit,
          ...(retryAfterCacheClear ? { cache: false } : {}),
        }
      );
      if (athleteResult.aborted) {
        return;
      }
      let response = athleteResult.list;

			if (athleteResult.failed) {
				if (!isBoardRequestCurrent(currentRequestToken)) {
					return;
				}
				const hasUsableFailureFallback = displayedAthletes.length > 0 || hasCompleteOptimisticPreview;
				setSport(selectedSport);
				setStats(stats);
				setStat(statValue);
				setAthletesLoadFailed(!hasUsableFailureFallback);
				if (displayedAthletes.length > 0) {
					setAthletes(displayedAthletes);
				} else if (hasCompleteOptimisticPreview) {
					setAthletes(optimisticPreview);
				} else {
					setAthletes([]);
				}
				setAthletesLoading(false);
				if (hasUsableFailureFallback) {
					dismissBoardRefreshWarning();
				} else {
					showBoardRefreshWarning();
				}
				return;
			}

			setAthletesLoadFailed(false);
			dismissBoardRefreshWarning();

      if (isTopPlaysSport(selectedSport) && (response === undefined || response.length === 0)) {
        if (!isBoardRequestCurrent(currentRequestToken)) {
          return;
        }
        const recovered = await recoverEmptyTopPlays({
          requestToken: currentRequestToken,
          selectedStat: statValue,
          allowFallback: true,
        });

        if (!recovered) {
          setAthletes([]);
          setAthletesLoading(false);
        }
        return;
      }

      if ((response === undefined || response.length === 0) && Object.keys(stats).length > 0) {

        // Reconcile an authoritative empty response against fresh metadata.
        // Metadata can briefly trail the athlete source (or outlive a recovered
        // source-cache generation), so probe every remaining market in the
        // selected league before removing that league for this board session.
        // This keeps explicit navigation in-app and prevents an empty card wall
        // or a native WebView refresh loop.
        if (preserveSelectedSport) {
          try {
            const refreshedStats = await FetchStats({ force: true });
            if (!isBoardRequestCurrent(currentRequestToken)) {
              return;
            }

            if (refreshedStats) {
              const refreshedSports = Object.keys(refreshedStats);
              const refreshedSportStats = refreshedStats[selectedSport] || {};
              setVerifiedStats(refreshedStats);
              setSports(refreshedSports);

              if (!Object.prototype.hasOwnProperty.call(refreshedStats, selectedSport)) {
                const selectedSportIndex = sports.indexOf(selectedSport);
                const orderedFallbackSports = selectedSportIndex >= 0
                  ? [
                      ...sports.slice(selectedSportIndex + 1),
                      ...sports.slice(0, selectedSportIndex),
                    ].filter((sportKey) => (
                      sportKey !== selectedSport &&
                      refreshedStats[sportKey] &&
                      leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
                    ))
                  : refreshedSports.filter((sportKey) => (
                      sportKey !== selectedSport &&
                      leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
                    ));
                const nextSportSelection = getNextSportSelection(
                  orderedFallbackSports,
                  [],
                  refreshedStats
                );

                if (nextSportSelection) {
                  setSport(nextSportSelection.sport);
                  setStats(nextSportSelection.stats);
                  setStat(nextSportSelection.stat);
                  return handleStatChange(
                    nextSportSelection.sport,
                    nextSportSelection.stat,
                    nextSportSelection.stats,
                    [],
                    false,
                    true,
                    currentRequestToken,
                    true
                  );
                }
              } else {
                // Stay in the chosen league while any untried advertised market
                // remains. This covers both a withdrawn stat and stale metadata
                // that still advertises the just-confirmed empty stat.
                const selectableRefreshedStats = Object.keys(refreshedSportStats)
                  .filter((statKey) => statKey !== TEAMS_STAT_KEY);
                const emptyStatProbeLimitReached = shouldSkipRemainingStatsForSport(
                  newAttemptedStats,
                  refreshedSportStats
                );
                const replacementStat = emptyStatProbeLimitReached
                  ? ""
                  : getNextUnattemptedStat(
                      refreshedSportStats,
                      statValue,
                      newAttemptedStats
                    );

                if (replacementStat) {
                  setSport(selectedSport);
                  setStats(refreshedSportStats);
                  setStat(replacementStat);
                  return handleStatChange(
                    selectedSport,
                    replacementStat,
                    refreshedSportStats,
                    newAttemptedStats,
                    false,
                    true,
                    currentRequestToken,
                    true
                  );
                }

                // Move to the adjacent available pill after the bounded probe.
                // Quarantine the league only when every advertised market was
                // actually exhausted; a large league that merely reached the
                // three-request safety cap remains selectable.
                const allAdvertisedStatsExhausted = selectableRefreshedStats.length > 0 &&
                  selectableRefreshedStats.every((statKey) => newAttemptedStats.includes(statKey));
                const selectedSportIndex = sports.indexOf(selectedSport);
                const nextVerifiedStats = { ...refreshedStats };
                if (allAdvertisedStatsExhausted) {
                  delete nextVerifiedStats[selectedSport];
                }
                const nextSports = Object.keys(nextVerifiedStats);
                const orderedFallbackSports = selectedSportIndex >= 0
                  ? [
                      ...sports.slice(selectedSportIndex + 1),
                      ...sports.slice(0, selectedSportIndex),
                    ].filter((sportKey) => (
                      sportKey !== selectedSport &&
                      nextVerifiedStats[sportKey] &&
                      leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
                    ))
                  : nextSports.filter((sportKey) => (
                      sportKey !== selectedSport &&
                      leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
                    ));
                const nextSportSelection = getNextSportSelection(
                  orderedFallbackSports,
                  [],
                  nextVerifiedStats
                );

                if (nextSportSelection) {
                  setVerifiedStats(nextVerifiedStats);
                  setSports(nextSports);
                  setSport(nextSportSelection.sport);
                  setStats(nextSportSelection.stats);
                  setStat(nextSportSelection.stat);
                  return handleStatChange(
                    nextSportSelection.sport,
                    nextSportSelection.stat,
                    nextSportSelection.stats,
                    [],
                    false,
                    true,
                    currentRequestToken,
                    true
                  );
                }
              }
            }
          } catch (error) {
            console.error("Error reconciling an empty selected league or stat:", error);
          }

          setSport(selectedSport);
          setStats(stats);
          setStat(statValue);
          setAthletes([]);
          setAthletesLoading(false);
          return;
        }

        // If we haven't cleared cache yet and this isn't already a retry after cache clear, try clearing cache
        if (
          statValue !== POPULAR_STAT_KEY &&
          !clearedSportsRef.current.has(selectedSport) &&
          !retryAfterCacheClear
        ) {
          const cacheCleared = await ClearSportCache(selectedSport);

          if (!isBoardRequestCurrent(currentRequestToken)) {
            return;
          }

          if (cacheCleared) {
            cacheClearedRef.current = true;
            clearedSportsRef.current.add(selectedSport);
            setCacheCleared(true);
            clearStatsCache();
            clearBoardAthleteCache();

            const refreshed = await refreshStatsForSport(selectedSport, statValue, {
              requestToken: currentRequestToken,
              force: true,
            });
            if (!isBoardRequestCurrent(currentRequestToken)) {
              return;
            }
            const retrySport = refreshed?.sport || selectedSport;
            const retryStats = refreshed?.stats || verifiedStats[retrySport] || {};
            const retryStat =
              refreshed?.stat ||
              (Object.prototype.hasOwnProperty.call(retryStats, statValue)
                ? statValue
                : (Object.keys(retryStats)[0] || statValue));

            // Retry the same sport/stat after clearing cache
            return handleStatChange(retrySport, retryStat, retryStats, attemptedStats, true, autoSwitchToastShown, currentRequestToken, preserveSelectedSport);
          }
        }

        let availableSports = sports;
        let statsMapBySport = verifiedStats;
        let nextStats = stats;

        if (statValue !== POPULAR_STAT_KEY && !shouldSkipRemainingStatsForSport(newAttemptedStats, nextStats)) {
          // Ensure server-side cache/stat update runs: clear sport cache (once per session) then refresh stats
          try {
            if (!clearedSportsRef.current.has(selectedSport)) {
              const cacheClearedNow = await ClearSportCache(selectedSport);
              if (cacheClearedNow) {
                cacheClearedRef.current = true;
                clearedSportsRef.current.add(selectedSport);
                setCacheCleared(true);
                clearStatsCache();
                clearBoardAthleteCache();
              }
            }

            const updatedVerifiedStats = await FetchStats({ force: true });
            if (updatedVerifiedStats) {
              if (!isBoardRequestCurrent(currentRequestToken)) {
                return;
              }
              availableSports = Object.keys(updatedVerifiedStats);
              statsMapBySport = updatedVerifiedStats;
              setVerifiedStats(updatedVerifiedStats);
              setSports(availableSports);
              nextStats = updatedVerifiedStats[selectedSport] || {};
              stats = nextStats;
              setStats(nextStats);
            }
          } catch (e) {
            console.error('Error clearing cache or refreshing stats:', e);
          }
        }

        const limitReached = shouldSkipRemainingStatsForSport(newAttemptedStats, nextStats);
        const nextStat = limitReached ? "" : getNextUnattemptedStat(nextStats, statValue, newAttemptedStats);

        if (nextStat) {
          // Popular cache recovery is routine and should remain silent.
          if (!autoSwitchToastShown && statValue !== POPULAR_STAT_KEY) {
            toastr["info"](`No athletes found for current stat. Trying ${nextStats[nextStat]}...`, "Auto-switching");
          }
          return handleStatChange(
            selectedSport,
            nextStat,
            nextStats,
            newAttemptedStats,
            false,
            true,
            currentRequestToken,
            preserveSelectedSport
          );
        }

        // All stats in this sport exhausted, or the auto-switch failure cap was reached.
        const currentSportIndex = availableSports.indexOf(selectedSport);
        const nextSportSelection = getNextSportSelection(
          currentSportIndex >= 0 ? availableSports.slice(currentSportIndex + 1) : availableSports,
          [],
          statsMapBySport
        );
        if (nextSportSelection) {
          if (!autoSwitchToastShown && statValue !== POPULAR_STAT_KEY) {
            const skipReason = limitReached
              ? `${newAttemptedStats.length} empty stats in ${selectedSport}. Switching to ${nextSportSelection.sport}...`
              : `No athletes found in ${selectedSport}. Switching to ${nextSportSelection.sport}...`;
            toastr["info"](skipReason, "Auto-switching");
          }
          pruneSportFromAvailableStats(
            statsMapBySport,
            availableSports,
            selectedSport,
            nextSportSelection.sport,
            nextSportSelection.stat
          );
          return handleStatChange(
            nextSportSelection.sport,
            nextSportSelection.stat,
            nextSportSelection.stats,
            [],
            false,
            true,
            currentRequestToken,
            preserveSelectedSport
          );
        }

        // Truly no athletes found anywhere
        pruneSportFromAvailableStats(statsMapBySport, availableSports, selectedSport);
        toastr["warning"]("No athletes currently available. Try again later or check back soon.", "No Athletes Found");
        setAthletes([]);
        setAthletesLoading(false);
      } else if (response && response.length > 0) {
        if (!isBoardRequestCurrent(currentRequestToken)) {
          return;
        }
        setAthletes(response);
        setAthletesLoading(false);
        if (shouldHydrateBoardAthleteResult(athleteResult, initialFetchLimit)) {
          scheduleBoardAthleteHydration(selectedSport, statValue, currentRequestToken);
        }
      } else {
        if (!isBoardRequestCurrent(currentRequestToken)) {
          return;
        }
        setAthletes([]);
        setAthletesLoading(false);
      }
    } catch (error) {
      console.error("Error fetching athletes:", error);
      toastr["error"]("Error loading athletes. Please try again.", "Error");
      setAthletes([]);
      setAthletesLoading(false);
    }
  };

  const handleEventStatChange = async (selectedSport, statValue, statsObj = null) => {
    const activeGameFilter = selectedSport === sport ? (selectedGameId || false) : false;
    if (!activeGameFilter) {
      return handleStatChange(selectedSport, statValue, statsObj, [], false, false, null, true);
    }

    const currentStats = statsObj || verifiedStats[selectedSport] || stats || {};
    const requestToken = startBoardRequest();
    beginAtomicBoardTransition(requestToken, selectedSport, statValue, activeGameFilter);

    try {
      if (statValue === POPULAR_STAT_KEY) {
        const eventStats = selectedGame?._topPlaysLeague
          ? currentStats
          : getEventRequestStats(selectedGame, currentStats);
        let eventResult = await fetchBoardEventStats(
          selectedSport,
          activeGameFilter,
          eventStats,
          requestToken,
          {
            targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
            perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
            maxStats: 1,
            returnOnFirstResult: true,
          }
        );

        if (
          !eventResult.aborted &&
          isBoardRequestCurrent(requestToken) &&
          eventResult.list.length === 0
        ) {
          eventResult = await fetchBoardEventStats(
            selectedSport,
            activeGameFilter,
            eventStats,
            requestToken,
            {
              cache: false,
              targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
              perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
              returnOnFirstResult: true,
            }
          );
        }

        if (eventResult.aborted || !isBoardRequestCurrent(requestToken)) {
          return;
        }

        if (eventResult.list.length > 0) {
          setSport(selectedSport);
          setStats(currentStats);
          setStat(POPULAR_STAT_KEY);
          setAthletes(eventResult.list);
          setAthletesLoading(false);
          finishAtomicBoardTransition(requestToken);
          scheduleBoardEventStatsHydration(selectedSport, activeGameFilter, eventStats, requestToken);
        } else if (!eventResult.failed) {
          exitUnavailableEvent();
        } else {
          showBoardRefreshWarning();
        }
        return;
      }

      const initialFetchLimit = getInitialAthleteLimit(statValue);
      const athleteResult = await fetchBoardAthletes(
        selectedSport,
        statValue,
        activeGameFilter,
        requestToken,
        initialFetchLimit > 0 ? { limit: initialFetchLimit } : {}
      );

      if (athleteResult.aborted || !isBoardRequestCurrent(requestToken)) {
        return;
      }

      if (athleteResult.failed && athleteResult.list.length === 0) {
        showBoardRefreshWarning();
        return;
      }

      if (athleteResult.list.length > 0) {
        setSport(selectedSport);
        setStats(currentStats);
        setStat(statValue);
        setAthletes(athleteResult.list);
        setAthletesLoading(false);
        finishAtomicBoardTransition(requestToken);
        if (shouldHydrateBoardAthleteResult(athleteResult, initialFetchLimit)) {
          scheduleBoardAthleteHydration(selectedSport, statValue, requestToken, activeGameFilter);
        }
        return;
      }

      const fallbackEventStats = getEventRequestStats(selectedGame, currentStats);
      const fallbackResult = await fetchBoardEventStats(
        selectedSport,
        activeGameFilter,
        fallbackEventStats,
        requestToken,
        {
          cache: false,
          targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
          perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
          returnOnFirstResult: true,
        }
      );
      if (fallbackResult.aborted || !isBoardRequestCurrent(requestToken)) {
        return;
      }
      if (fallbackResult.list.length > 0) {
        setSport(selectedSport);
        setStats(currentStats);
        setStat(POPULAR_STAT_KEY);
        setAthletes(fallbackResult.list);
        setAthletesLoading(false);
        finishAtomicBoardTransition(requestToken);
        toastr["info"](
          "That stat closed, so we moved you to the event's available picks.",
          "Market updated"
        );
        scheduleBoardEventStatsHydration(
          selectedSport,
          activeGameFilter,
          fallbackEventStats,
          requestToken
        );
      } else if (!fallbackResult.failed) {
        exitUnavailableEvent();
      } else {
        showBoardRefreshWarning();
      }
    } catch (error) {
      if (!isBoardRequestCurrent(requestToken)) return;
      console.error("Error loading event stat athletes:", error);
      showBoardRefreshWarning();
    } finally {
      if (isBoardRequestCurrent(requestToken)) {
        finishAtomicBoardTransition(requestToken);
      }
    }
  };

  useEffect(() => {
    const selectedSport = normalizeSportKey(sport);
    const inventoryIsEmpty = leagueInventoryBySport[selectedSport] === false;
    const athleteResultIsEmpty = Array.isArray(athletes) && athletes.length === 0;
    if (
      !selectedSport ||
      !inventoryIsEmpty ||
      !athleteResultIsEmpty ||
      loading ||
      athletesLoading ||
      athletesLoadFailed ||
      searchMode ||
      selectedGameId ||
      isTopPlaysSport(selectedSport) ||
      isTeamsViewSelected
    ) {
      return;
    }

    const nextVerifiedStats = { ...(verifiedStats || {}) };
    delete nextVerifiedStats[selectedSport];
    const remainingSports = sports.filter(
      (sportKey) => (
        sportKey !== selectedSport &&
        nextVerifiedStats[sportKey] &&
        leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
      )
    );
    const selectedIndex = sports.indexOf(selectedSport);
    const orderedRemainingSports = selectedIndex >= 0
      ? [
          ...sports.slice(selectedIndex + 1),
          ...sports.slice(0, selectedIndex),
        ].filter((sportKey) => (
          sportKey !== selectedSport &&
          nextVerifiedStats[sportKey] &&
          leagueInventoryBySport[normalizeSportKey(sportKey)] !== false
        ))
      : remainingSports;
    const nextSelection = getNextSportSelection(
      orderedRemainingSports,
      [],
      nextVerifiedStats
    );

    setVerifiedStats(nextVerifiedStats);
    setSports(remainingSports);
    if (!nextSelection) {
      setSport("");
      setStats({});
      setStat("");
      return;
    }

    const requestToken = startBoardRequest();
    setSport(nextSelection.sport);
    setStats(nextSelection.stats);
    setStat(nextSelection.stat);
    setAthletesLoading(true);
    toastr["info"](
      `${selectedSport.toUpperCase()} no longer has selections. Showing ${nextSelection.sport.toUpperCase()}.`,
      "League Updated"
    );
    void handleStatChange(
      nextSelection.sport,
      nextSelection.stat,
      nextSelection.stats,
      [],
      false,
      true,
      requestToken,
      true
    );
  }, [
    athletes,
    athletesLoadFailed,
    athletesLoading,
    isTeamsViewSelected,
    leagueInventoryBySport,
    loading,
    searchMode,
    selectedGameId,
    sport,
    sports,
    verifiedStats,
  ]);

  useEffect(() => {
    if (
      loading ||
      sportStatsLoading ||
      !athletesLoading ||
      !sport ||
      searchMode ||
      isTeamsViewSelected ||
      isTopPlaysSport(sport)
    ) {
      return undefined;
    }

    let cancelled = false;
    let observedRequestToken = boardRequestTokenRef.current;
    let timeoutId = null;
    const observedSport = sport;
    const observedStats = stats && typeof stats === "object" ? stats : {};
    const recoveryStat = stat || Object.keys(observedStats)[0] || "";
    const observedGameId = selectedGameId || false;

    const scheduleRecovery = () => {
      timeoutId = window.setTimeout(async () => {
        if (cancelled || normalizeSportKey(observedSport) !== activeSportRef.current) {
          return;
        }

        // Another current-board request took ownership without changing the
        // visible selection. Give that request its own full recovery window.
        if (!isBoardRequestCurrent(observedRequestToken)) {
          observedRequestToken = boardRequestTokenRef.current;
          scheduleRecovery();
          return;
        }

        void reportBoardIssue("athletes-loading-timeout", {
          sport: observedSport,
          stat: recoveryStat,
          gameId: observedGameId,
          requestToken: observedRequestToken,
        });

        const recoveryToken = startBoardRequest();
        try {
          const isEventWideRecovery = Boolean(observedGameId && recoveryStat === POPULAR_STAT_KEY);
          const athleteResult = isEventWideRecovery
            ? await fetchBoardEventStats(
                observedSport,
                observedGameId,
                getEventRequestStats(selectedGame, observedStats),
                recoveryToken,
                {
                  cache: false,
                  targetCount: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                  perStatLimit: EVENT_BOARD_INITIAL_ATHLETE_LIMIT,
                  returnOnFirstResult: true,
                }
              )
            : recoveryStat
            ? await fetchBoardAthletes(
                observedSport,
                recoveryStat,
                observedGameId,
                recoveryToken,
                { cache: false, limit: INITIAL_BOARD_ATHLETE_LIMIT }
              )
            : { aborted: false, failed: true, list: [] };

          if (
            cancelled ||
            athleteResult.aborted ||
            !isBoardRequestCurrent(recoveryToken) ||
            normalizeSportKey(observedSport) !== activeSportRef.current
          ) {
            return;
          }

          setStat(recoveryStat);
          if (Array.isArray(athleteResult.list) && athleteResult.list.length > 0) {
            setAthletes((currentAthletes) => (
              isEventWideRecovery
                ? appendAthleteList(currentAthletes, athleteResult.list)
                : athleteResult.list
            ));
          }
          setAthletesLoading(false);

          if (athleteResult.failed || !Array.isArray(athleteResult.list) || athleteResult.list.length === 0) {
            if (isEventWideRecovery && !athleteResult.failed && Array.isArray(athleteResult.list)) {
              eventUnavailableHandlerRef.current?.();
              return;
            }
            toastr["warning"](
              `${observedSport.toUpperCase()} took too long to load. Please tap the league to try again.`,
              "Board Refresh"
            );
          }
        } catch (error) {
          if (cancelled || !isBoardRequestCurrent(recoveryToken)) {
            return;
          }

          console.error("Error recovering stalled athlete board:", error);
          setAthletesLoading(false);
        }
      }, BOARD_VISIBLE_LOADING_RECOVERY_MS);
    };

    scheduleRecovery();
    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [athletesLoading, loading, sportStatsLoading, sport, stat, stats, selectedGame, selectedGameId, searchMode, isTeamsViewSelected]);

  const selectProp = async (e) => {
    let prop = e.currentTarget.dataset.prop;
    let value = e.currentTarget.value;

    // if ( !loggedIn && (prop === "more" || prop === "less") ) {
    //   markRegistrationFlowStarted();
    //   document.getElementById("register-link").click();
    //   return;
    // }

    if (prop.includes("more") || prop.includes("less") || prop === "delete") {

      let selectedAthleteID = value;

      // Count of selected athletes
      let selectedCount = selected.length;
      selectedCount = prop === "delete" ? selectedCount - 1 : selectedCount + 1;
      // setPayoutMultiplier(selectedCount);

      let selectedAthleteStat = normalizeProjectedStatKey(e.currentTarget.dataset.stat);
      let statValue = e.currentTarget.dataset.value;
      let promo = e.currentTarget.dataset.promo;
      const selectionState = getSelectionStateFromToken(promo);
      const matchesCurrentVariant = (entry) =>
        matchesSelectionVariant(
          entry,
          selectedAthleteID,
          selectedAthleteStat,
          promo,
          selectionState.easy,
          selectionState.hard
        );
      const matchesCurrentFamily = (entry) =>
        matchesSelectionFamily(entry, selectedAthleteID, selectedAthleteStat);

      if (prop === "delete") {
        setSelected((prevSelected) =>
          prevSelected.filter((entry) => !matchesCurrentVariant(entry))
        );
        return;
      }

      // Lineup More/Less edits must remain available while quoting or checking
      // lines. Do not route them through add-pick eligibility or mutate the
      // selected object in place. Sidebar invalidates consent on the new side.
      if (e.currentTarget.dataset.lineupDirectionEdit === 'true') {
        setSelected((prevSelected) => editLineupDirection(prevSelected, matchesCurrentVariant, prop));
        return;
      }

      let athleteIndex = athletes.findIndex(
        (x) => x.athlete_id == selectedAthleteID && x.stat_key == selectedAthleteStat
      );
      let athlete = athleteIndex > -1
        ? athletes[athleteIndex]
        : selected.find(matchesCurrentVariant) || selected.find(matchesCurrentFamily);

      if (!athlete && e.currentTarget.dataset.athlete) {
        try {
          const parsedAthlete = JSON.parse(e.currentTarget.dataset.athlete);
          if (parsedAthlete && typeof parsedAthlete === "object") {
            athlete = parsedAthlete;
          }
        } catch (error) {
          athlete = null;
        }
      }

      if (!athlete) {
        return;
      }

      // Wake the selected athlete's actual league. This matters on TOP PLAYS,
      // where the visible board sport is synthetic and cannot identify the
      // provider component that checkout will need.
      const demandLeague = String(athlete.league || sport || "").trim().toLowerCase();
      if (demandLeague && demandLeague !== "topplays") {
        fetch(`/wp-json/wagedwar/v1/shadow-demand/${encodeURIComponent(demandLeague)}`, {
          method: "POST",
          credentials: "same-origin",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shrk: String(getShrkValue() ?? "0"),
            reason: "add_selection",
          }),
        }).catch(() => {});
      }

      if (statValue) {
        athlete.stat = statValue;
      }

      athlete.stat_key = selectedAthleteStat;
      athlete.easy = selectionState.easy;
      athlete.hard = selectionState.hard;
      athlete.promo = selectionState.promo;

      let selectedAthlete = selected.find(matchesCurrentFamily);

      if (selected.length >= maxAthletes && !selectedAthlete) {
        toastr["warning"](`You have reached the max ${maxAthletes} Athlete Line-up.`, "Notice");
        return;
      }

      let currentVariantSelection = selected.find(matchesCurrentVariant);
      const selectionTap = {
        athleteId: selectedAthleteID,
        statKey: selectedAthleteStat,
        promo,
        prop,
      };
      if (currentVariantSelection?.selected == prop) {
        const selectedSyncLineMoved =
          currentVariantSelection?._selectionSyncStatus === "changed" &&
          !lineValuesMatch(currentVariantSelection?.stat, currentVariantSelection?._selectionCurrentStat);
        const selectedLineMoved =
          selectedSyncLineMoved ||
          !lineValuesMatch(currentVariantSelection?.stat, statValue ?? athlete?.stat);

        if (selectedLineMoved) {
          athlete.selected = prop;
          const refreshedAthlete = normalizeSelectedAthlete(athlete);
          setSelected((prevSelected) =>
            prevSelected.map((entry) =>
              matchesCurrentVariant(entry) ? refreshedAthlete : entry
            )
          );
          return;
        }

        if (shouldIgnoreRapidSelectionToggle(lastSelectionTapRef.current, selectionTap)) {
          return;
        }

        lastSelectionTapRef.current = null;
        setSelected((prevSelected) =>
          prevSelected.filter((entry) => !matchesCurrentVariant(entry))
        );
        return;
      }

      // if (prop !== "delete") {
      //   sendAthleteSelectionToAPI(athlete);
      // }

      athlete.selected = prop;
      const normalizedAthlete = normalizeSelectedAthlete(athlete);
      const currentSelectionIndex = selected.findIndex((entry) => matchesCurrentVariant(entry));
      const fallbackSelectionIndex = currentSelectionIndex > -1
        ? currentSelectionIndex
        : selected.findIndex((entry) => matchesCurrentFamily(entry));
      const nextSelected = fallbackSelectionIndex === -1
        ? [...selected, normalizedAthlete]
        : selected.reduce((nextLineup, entry, index) => {
          if (index === fallbackSelectionIndex) {
            nextLineup.push(normalizedAthlete);
            return nextLineup;
          }

          if (matchesCurrentFamily(entry)) {
            return nextLineup;
          }

          nextLineup.push(entry);
          return nextLineup;
        }, []);
      const win100Violation = win100ModeActive
        ? getWin100LineupViolation(nextSelected)
        : null;

      if (win100Violation) {
        toastr["warning"](win100Violation.message, win100Violation.title);
        return;
      }
      const lowLiquidityViolation = getLowLiquiditySelectionChangeViolation(selected, nextSelected, null, loggedIn);

      if (lowLiquidityViolation) {
        toastr["warning"](lowLiquidityViolation.message, "Notice");
        return;
      }

      const isShrk = Number(document.getElementById("shrk")?.value) > 0;
      const propCombinabilityValidation = validatePropCombinability(
        nextSelected,
        isShrk ? "sharp" : "recreational"
      );
      if (!propCombinabilityValidation.valid) {
        toastr["warning"](propCombinabilityValidation.errors[0].message, "Notice");
        return;
      }

      const currentLiveSameEventPolicy = getLiveSameEventLineupPolicy(
        selected,
        currentUserTier,
        0
      );
      const nextLiveSameEventPolicy = getLiveSameEventLineupPolicy(
        nextSelected,
        currentUserTier,
        0
      );
      if (
        nextLiveSameEventPolicy &&
        (
          !currentLiveSameEventPolicy ||
          currentLiveSameEventPolicy.code !== nextLiveSameEventPolicy.code ||
          currentLiveSameEventPolicy.eventKey !== nextLiveSameEventPolicy.eventKey ||
          currentLiveSameEventPolicy.legCount !== nextLiveSameEventPolicy.legCount
        )
      ) {
        toastr["warning"](nextLiveSameEventPolicy.message, nextLiveSameEventPolicy.title);
      }

      lastSelectionTapRef.current = {
        ...selectionTap,
        timestamp: Date.now(),
      };
      setSelected((prevSelected) => {
        const currentIndex = prevSelected.findIndex((entry) => matchesCurrentVariant(entry));
        const fallbackIndex = currentIndex > -1
          ? currentIndex
          : prevSelected.findIndex((entry) => matchesCurrentFamily(entry));

        if (fallbackIndex === -1) {
          return [...prevSelected, normalizedAthlete];
        }

        return prevSelected.reduce((nextSelected, entry, index) => {
          if (index === fallbackIndex) {
            nextSelected.push(normalizedAthlete);
            return nextSelected;
          }

          if (matchesCurrentFamily(entry)) {
            return nextSelected;
          }

          nextSelected.push(entry);
          return nextSelected;
        }, []);
      });
      trackAthleteSelected(normalizedAthlete, {
        lineupSize: nextSelected.length,
        selectionAction: fallbackSelectionIndex === -1 ? "added" : "replaced",
        selectionSource: isTopPlaysSport(sport) ? "top_plays" : "board",
      });
      triggerSelectionHaptic();
      openMobileLineupReviewAtPickCount(selected.length, nextSelected.length);
      return;
    }

    if (prop === "sport" || prop === "stat") {
      let selectedSport = prop === "sport" ? value : sport;
      const selectionChanged = prop === "sport" ? selectedSport !== sport : value !== stat;
      if (selectionChanged) {
        setSearchResetSignal((currentSignal) => currentSignal + 1);
        setSearchMode(false);
        setSearchQuery("");
      }

      if (prop === "stat" && isTopPlaysSport(selectedSport) && value === TEAMS_STAT_KEY) {
        setStat(TEAMS_STAT_KEY);
        handleTeamsHubClick(e);
        return;
      }

      if (prop === "stat" && isTopPlaysSport(selectedSport)) {
        setTeamsHubOpen(false);
        cancelTeamsHubRequest();
        setStat(value || POPULAR_STAT_KEY);
        setAthletesLoading(false);
        setSearchMode(false);
        return;
      }

      setTeamsHubOpen(false);
      cancelTeamsHubRequest();

      if (prop === "sport") {
        cancelTeamLineupValidation();
        sportValidationTokenRef.current += 1;
        // Invalidate late bootstrap/background work. Pending intent selects the
        // tapped league and renders card skeletons while verified rows remain available for rollback.
        const requestToken = startBoardRequest();
        const optimisticStats = verifiedStats[selectedSport] || {};
        const optimisticFirstStat = Object.keys(optimisticStats)[0] || "";
        void loadAtomicBoardSelection(
          selectedSport,
          optimisticFirstStat,
          optimisticStats,
          requestToken,
          true
        );
      } else {
        cancelTeamLineupValidation();
        sportValidationTokenRef.current += 1;
        setSportStatsLoading(false);
        setSport(selectedSport);
        if (value === TEAMS_STAT_KEY) {
          setStat(TEAMS_STAT_KEY);
          setAthletes([]);
          setAthletesLoading(false);
          setSearchMode(false);
        } else if (selectedGameId) {
          handleEventStatChange(selectedSport, value);
        } else {
          void loadAtomicBoardSelection(
            selectedSport,
            value,
            verifiedStats[selectedSport] || stats || {},
            null,
            true
          );
        }
      }
    }
  };

  const handleFindReplacement = (athlete) => {
    if (!athlete) {
      return;
    }

    const replacementSport = normalizeTapInLeague(athlete.league || sport);
    const replacementStat = normalizeProjectedStatKey(athlete.stat_key || stat);
    const promoToken = athlete.hard ? "hard" : (athlete.easy ? "easy" : (athlete.promo ? "promo" : ""));

    void selectProp({
      currentTarget: {
        value: athlete.athlete_id,
        dataset: {
          prop: "delete",
          stat: replacementStat,
          promo: promoToken,
        },
      },
    });

    setShowLineupReviewSheet(false);
    setSearchResetSignal((currentSignal) => currentSignal + 1);
    setSearchMode(false);
    setSearchQuery("");
    setSelectedGameId("");

    if (replacementSport && replacementStat) {
      void handleStatChange(
        replacementSport,
        replacementStat,
        verifiedStats[replacementSport] || null,
        [],
        false,
        false,
        null,
        true
      );
      window.setTimeout(() => {
        document.querySelector(".athlete-card-grid")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  useEffect(() => {
    const handleTapInPick = async (event) => {
      const detail = event?.detail || {};
      const targetName = String(detail.name || "").trim();
      const sportCandidates = getTapInSportCandidates(detail.league, verifiedStats);
      const targetSport = sportCandidates[0] || normalizeTapInLeague(detail.league);
      const targetStat = normalizeTapInStatKey(detail.statKey || detail.statName);
      const loggedIn = document.body.classList.contains("logged-in");
      const tapInEligibility = document.getElementById("tap-in-promo-eligible")?.value;

      if (loggedIn && (tapInEligibility !== "1" || hasRecentDiscountedStatPromoUse())) {
        window.dispatchEvent(new CustomEvent(TAP_IN_PROMO_UNAVAILABLE_EVENT, { detail }));
        toastr["warning"](
          "This discount is no longer available for your account. Refresh the board to see current offers.",
          "Promo Unavailable"
        );
        return;
      }

      if (!targetName || !targetSport || !targetStat) {
        toastr["warning"]("We could not add that promo pick automatically. Please pick it from the board.", "Promo Pick");
        return;
      }

      const requestToken = startBoardRequest();
      sportValidationTokenRef.current += 1;
      setLoading(false);
      setAthletesLoading(true);
      setSelectedGameId("");

      let tapInAthlete = null;
      let tapInAthleteList = [];
      let resolvedSport = targetSport;
      let resolvedStats = {
        ...(verifiedStats?.[targetSport] || {}),
      };

      for (const sportCandidate of sportCandidates) {
        const candidateStats = {
          ...(verifiedStats?.[sportCandidate] || {}),
        };

        if (!candidateStats[targetStat]) {
          candidateStats[targetStat] = getTapInStatLabel(detail, targetStat);
        }

        setSport(sportCandidate);
        setStats(candidateStats);
        setStat(targetStat);

        const athleteResult = await fetchBoardAthletes(
          sportCandidate,
          targetStat,
          false,
          requestToken,
          { limit: 0 }
        );

        if (athleteResult.aborted || !isBoardRequestCurrent(requestToken)) {
          return;
        }

        setAthletes(athleteResult.list);
        setAthletesLoading(false);

        tapInAthlete = findTapInAthlete(athleteResult.list, detail, targetStat);
        tapInAthleteList = athleteResult.list;
        resolvedSport = sportCandidate;
        resolvedStats = candidateStats;

        if (!tapInAthlete) {
          const fallbackResult = await findTapInAthleteAcrossStats(
            fetchBoardAthletes,
            sportCandidate,
            targetStat,
            detail,
            requestToken,
            candidateStats
          );

          if (fallbackResult.aborted || !isBoardRequestCurrent(requestToken)) {
            return;
          }

          if (fallbackResult.athlete) {
            tapInAthlete = fallbackResult.athlete;
            tapInAthleteList = fallbackResult.list;
            setAthletes(tapInAthleteList);
            setStat(normalizeProjectedStatKey(tapInAthlete.stat_key));
            break;
          }

          const searchResponse = await SearchAthletes(sportCandidate, targetName, { limit: 50 });
          if (!isBoardRequestCurrent(requestToken)) {
            return;
          }

          if (Array.isArray(searchResponse) && searchResponse.length > 0) {
            const searchedAthletes = filterAthleteList(searchResponse);
            const searchedTapInAthlete = findTapInAthlete(searchedAthletes, detail, targetStat);

            if (searchedTapInAthlete) {
              const searchedStat = normalizeProjectedStatKey(searchedTapInAthlete.stat_key || targetStat);
              tapInAthlete = searchedTapInAthlete;
              tapInAthleteList = searchedAthletes;
              resolvedSport = sportCandidate;
              resolvedStats = {
                ...candidateStats,
                [searchedStat]: candidateStats[searchedStat] || getTapInStatLabel(detail, searchedStat),
              };
              setSearchMode(true);
              setAthletes(tapInAthleteList);
              setStat(searchedStat);
              break;
            }
          }
        } else {
          break;
        }
      }

      setSport(resolvedSport);
      setStats(resolvedStats);

      if (!tapInAthlete) {
        window.dispatchEvent(new CustomEvent(TAP_IN_PROMO_UNAVAILABLE_EVENT, { detail }));
        toastr["warning"](
          `${targetName}'s discounted line is no longer on the current board. Please choose another pick.`,
          "Promo Updated"
        );
        return;
      }

      const selectionState = getCardSelectionState(
        tapInAthlete.stat_key,
        tapInAthlete.promo,
        tapInAthlete.easy,
        tapInAthlete.hard
      );
      const alreadySelected = selectedRef.current.find((entry) =>
        entry?.selected === "more" &&
        matchesSelectionVariant(
          entry,
          tapInAthlete.athlete_id,
          selectionState.statKey,
          selectionState.promoToken,
          selectionState.easy,
          selectionState.hard
        )
      );

      if (alreadySelected) {
        toastr["info"](`${tapInAthlete.name} is already in your lineup.`, "Promo Pick");
        setShowLineupReviewSheet(false);
        return;
      }

      selectProp({
        currentTarget: {
          value: tapInAthlete.athlete_id,
          dataset: {
            prop: "more",
            stat: selectionState.statKey,
            value: tapInAthlete.stat,
            promo: selectionState.promoToken,
            athlete: JSON.stringify(tapInAthlete),
          },
        },
      });

      setShowLineupReviewSheet(false);
      window.setTimeout(() => {
        const addedSelection = selectedRef.current.find((entry) =>
          entry?.selected === "more" &&
          matchesSelectionVariant(
            entry,
            tapInAthlete.athlete_id,
            selectionState.statKey,
            selectionState.promoToken,
            selectionState.easy,
            selectionState.hard
          )
        );

        if (addedSelection) {
          toastr["success"](`${tapInAthlete.name} added to your lineup.`, "Promo Pick Added");
        }
      }, 150);
    };

    window.addEventListener("ww:tap-in-pick", handleTapInPick);

    return () => {
      window.removeEventListener("ww:tap-in-pick", handleTapInPick);
    };
  }, [verifiedStats, selectProp]);

  useEffect(() => {
    if (selected.length === 0) {
      setShowLineupReviewSheet(false);
      setPendingTeamReplaceKey("");
    }
  }, [selected.length]);

  useEffect(() => {
    cancelTeamLineupValidation();
    setPendingTeamReplaceKey("");
    setPendingTeamApplyKey("");
  }, [sport, stat, selectedGameId]);

  useEffect(() => {
    if (!showLineupReviewSheet || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_LINEUP_SHEET_QUERY);
    const handleViewportChange = (event) => {
      if (!event.matches) {
        setShowLineupReviewSheet(false);
      }
    };

    if (!mediaQuery.matches) {
      setShowLineupReviewSheet(false);
      return undefined;
    }

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, [showLineupReviewSheet]);

  const handleScroll = () => {
    if (isMobileLineupViewport()) {
      setShowLineupReviewSheet(true);
      return;
    }

    const selectedContainer = document.getElementById("scroll-anchor");

    if (selectedContainer) {
      selectedContainer.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleModal = (type) => {
    if ($(".modal-dialog").is(":visible")) {
      return;
    }

    const modalId = `${type}-modal`;
    if (activeInfoModal === type && $(`#${modalId}`).length) {
      $(`#${modalId}`).modal("show");
      return;
    }

    setActiveInfoModal(type);
  };

  const handleDarkToggle = () => {
    if (darkMode) {
      document.body.classList.remove("dark-mode");
      setDarkMode(false);
    } else {
      document.body.classList.add("dark-mode");
      setDarkMode(true);
    }
  };

  const mobileMultiplier = () => {
    return getMobileStickyPayoutMultiplier(selected.length);
  };

  const mobileStickySubcopy =
    selected.length < 2
      ? "Add 1 more pick to enter"
      : isH2H
        ? "⚔️ Head-to-Head Payout"
        : `Up to ${mobileMultiplier()}X Payout`;
  const mobileStickyCtaLabel = selected.length < 2 ? "Add Pick" : "Complete Entry";
  const mobileStickyCtaMultiplier =
    selected.length < 2
      ? "Add 1 more"
      : isH2H
        ? "H2H payout"
        : `Up to ${mobileMultiplier()}x Payout`;
  const mobilePickCountLabel = `${selected.length} Pick${selected.length === 1 ? "" : "s"}`;
  const stickyFooterAvatarLimit = 5;
  const rawStickyOverflow = Math.max(selected.length - stickyFooterAvatarLimit, 0);
  const stickyFooterAthletes = rawStickyOverflow === 1 ? selected.slice(0, stickyFooterAvatarLimit + 1) : selected.slice(0, stickyFooterAvatarLimit);
  const stickyFooterOverflowCount = rawStickyOverflow === 1 ? 0 : rawStickyOverflow;
  const stickyFooterVisibleAvatarCount = stickyFooterAthletes.length + (stickyFooterOverflowCount > 0 ? 1 : 0);
  const stickyFooterPotentialPayouts = isH2H
    ? []
    : getStickyFooterPotentialPayouts(selected.length, stickyFooterVisibleAvatarCount);
  const stickyFooterPotentialBackground = darkMode
    ? STICKY_FOOTER_DARK_BACKGROUND
    : STICKY_FOOTER_LIGHT_BACKGROUND;

  const handleTeamLineupSelect = async (option, { confirmed = false } = {}) => {
    cancelTeamLineupValidation();

    if (!option?.available || !Array.isArray(option.candidateLineups) || option.candidateLineups.length === 0) {
      teamLineupNoticeRef.current = toastr["warning"](
        option?.unavailableReason || "This team lineup is currently unavailable.",
        "Team Lineup"
      );
      return;
    }

    const currentSelectedCount = selectedRef.current.length;
    if (currentSelectedCount > 0 && !confirmed && pendingTeamReplaceKey !== option.key) {
      setPendingTeamReplaceKey(option.key);
      return;
    }

    setPendingTeamApplyKey(option.key);
    setPendingTeamReplaceKey("");

    const validationToken = teamLineupValidationTokenRef.current;
    const validationController = new AbortController();
    teamLineupValidationControllerRef.current = validationController;

    const validationFailures = [];
    option.debug.validationFailures = validationFailures;
    const { validateTeamLineupForAdd } = await loadTeamLineupModule();

    for (const lineup of option.candidateLineups) {
      const validation = await validateTeamLineupForAdd(lineup, {
        signal: validationController.signal,
      });
      if (
        validationController.signal.aborted ||
        validationToken !== teamLineupValidationTokenRef.current
      ) {
        return;
      }
      if (!validation.valid) {
        validationFailures.push({
          reason: validation.reason,
          message: validation.message,
          lineup: lineup.map((athlete) => ({
            name: athlete?.name,
            team: athlete?.team_short || athlete?.team,
            stat_key: athlete?.stat_key,
            stat: athlete?.stat,
            selected: athlete?.selected,
          })),
          validation: validation.validation || null,
        });
        continue;
      }

      const teamLineupMeta = {
        key: option.key,
        eventKey: option.eventKey || option.debug?.eventKey || "",
        sport: String(option.sport || "").toLowerCase(),
        teamKey: option.team?.key || "",
        teamName: option.team?.name || "",
        opponentKey: option.opponent?.key || "",
        opponentName: option.opponent?.name || "",
        startTime: option.startTime || "",
      };
      const nextLineup = lineup.map((athlete) => ({
        ...normalizeSelectedAthlete(athlete),
        teamLineup: teamLineupMeta,
      }));
      option.debug.selected = nextLineup.map((athlete) => ({
        name: athlete?.name,
        team: athlete?.team_short || athlete?.team,
        stat_key: athlete?.stat_key,
        stat: athlete?.stat,
        selected: athlete?.selected,
      }));
      option.debug.quote = validation.quote || null;

      setSelected(nextLineup);
      setPendingTeamApplyKey("");
      setShowLineupReviewSheet(isMobileLineupViewport());
      if (teamLineupValidationControllerRef.current === validationController) {
        teamLineupValidationControllerRef.current = null;
      }
      toastr["success"]("Team lineup added.", "Lineup Ready");
      return;
    }

    if (
      validationController.signal.aborted ||
      validationToken !== teamLineupValidationTokenRef.current
    ) {
      return;
    }

    setPendingTeamApplyKey("");
    if (teamLineupValidationControllerRef.current === validationController) {
      teamLineupValidationControllerRef.current = null;
    }
    teamLineupNoticeRef.current = toastr["warning"](
      validationFailures[0]?.message || "This team lineup could not be verified.",
      "Team Lineup"
    );
  };

  const handleTeamsHubClick = (event) => {
    event?.preventDefault();
    if (!teamsHubEnabled) {
      return;
    }

    if (isTopPlaysSelected) {
      setStat(TEAMS_STAT_KEY);
    }

    setTeamsHubOpen(true);
    setSearchMode(false);
    setLoading(false);
    setAthletesLoading(false);
    setAthletes([]);
    void fetchTeamsHubLineups();
  };

  const handleFlushTeamsCache = (event) => {
    event?.preventDefault();
    cancelTeamLineupRequest();
    cancelTeamsHubRequest();

    try {
      window.localStorage?.removeItem(TEAM_LINEUP_POOL_CACHE_KEY);
      window.localStorage?.removeItem(TEAM_LINEUP_QUOTE_CACHE_KEY);
    } catch (error) {
      // Storage may be unavailable in private mode; state resets still force live reloads.
    }

    setTeamLineupAthletes([]);
    setTeamLineupQuotedGroups([]);
    setTeamLineupQuotesReady(false);
    setTeamLineupLoading(false);
    setTeamsHubGroups([]);
    setTeamsHubQuotedGroups([]);
    setTeamsHubQuotesReady(false);
    setTeamsHubLoading(false);

    if (teamsHubOpen) {
      void fetchTeamsHubLineups();
    } else if (isTeamsSelected) {
      void fetchTeamLineupAthletePool(sport, stats, selectedGameId || false);
    }

    fetch("/wp-json/wagedwar/v1/team-lineups-cache/flush", restFetchOptions({
      method: "POST",
      cache: "no-store",
    })).catch(() => {});

    toastr["success"]("Teams cache flushed.", "Admin Tools");
  };

  const renderTopPlaysRows = () => {
    const leagueRows = getTopPlaysLeagueRows(topPlaysVisibleAthletes);
    let cardIndex = 0;

    const setTopPlaysScrollerRef = (league, node) => {
      if (node) {
        topPlaysScrollerRefs.current.set(league, node);
        return;
      }

      topPlaysScrollerRefs.current.delete(league);
    };

    const scrollTopPlaysRow = (league, direction) => {
      const scroller = topPlaysScrollerRefs.current.get(league);
      if (!scroller) {
        return;
      }

      const firstCard = scroller.querySelector(".athlete-card");
      const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 185;
      const style = window.getComputedStyle(scroller);
      const gap = Number.parseFloat(style.columnGap || style.gap || "12") || 12;
      const scrollAmount = Math.max(cardWidth + gap, scroller.clientWidth * 0.82);

      scroller.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
      });
    };

    const isTopPlaysInteractiveTarget = (target) => (
      target instanceof Element &&
      Boolean(target.closest("button, a, input, textarea, select"))
    );

    const cancelTopPlaysMomentum = () => {
      const dragState = topPlaysDragRef.current;
      if (dragState.animationFrame) {
        window.cancelAnimationFrame(dragState.animationFrame);
      }
      if (dragState.inertiaFrame) {
        window.cancelAnimationFrame(dragState.inertiaFrame);
        dragState.settlingScroller?.classList.remove("top-plays-row__scroller--settling");
      }

      topPlaysDragRef.current = {
        ...dragState,
        animationFrame: null,
        inertiaFrame: null,
        settlingScroller: null,
      };
    };

    const scheduleTopPlaysScroll = (scroller, targetScrollLeft) => {
      topPlaysDragRef.current.targetScrollLeft = targetScrollLeft;

      if (topPlaysDragRef.current.animationFrame) {
        return;
      }

      topPlaysDragRef.current.animationFrame = window.requestAnimationFrame(() => {
        const currentDragState = topPlaysDragRef.current;
        currentDragState.animationFrame = null;
        if (currentDragState.scroller === scroller) {
          scroller.scrollLeft = currentDragState.targetScrollLeft;
        }
      });
    };

    const startTopPlaysMomentum = (scroller, velocityX) => {
      const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      let momentum = -velocityX * 16;

      if (Math.abs(velocityX) < TOP_PLAYS_DRAG_MIN_VELOCITY || maxScrollLeft <= 0) {
        return;
      }

      scroller.classList.add("top-plays-row__scroller--settling");
      topPlaysDragRef.current.settlingScroller = scroller;

      const step = () => {
        const previousScrollLeft = scroller.scrollLeft;
        const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, previousScrollLeft + momentum));
        scroller.scrollLeft = nextScrollLeft;
        momentum *= TOP_PLAYS_DRAG_MOMENTUM_FRICTION;

        if (
          Math.abs(momentum) < 0.35 ||
          nextScrollLeft === previousScrollLeft ||
          nextScrollLeft <= 0 ||
          nextScrollLeft >= maxScrollLeft
        ) {
          scroller.classList.remove("top-plays-row__scroller--settling");
          topPlaysDragRef.current.inertiaFrame = null;
          topPlaysDragRef.current.settlingScroller = null;
          return;
        }

        topPlaysDragRef.current.inertiaFrame = window.requestAnimationFrame(step);
      };

      topPlaysDragRef.current.inertiaFrame = window.requestAnimationFrame(step);
    };

    const handleTopPlaysPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      if (isTopPlaysInteractiveTarget(event.target)) {
        return;
      }

      const scroller = event.currentTarget;
      cancelTopPlaysMomentum();
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      topPlaysDragRef.current = {
        animationFrame: null,
        inertiaFrame: null,
        isDown: true,
        lastClientX: event.clientX,
        lastMoveAt: now,
        moved: false,
        pointerId: event.pointerId,
        scroller,
        scrollLeft: scroller.scrollLeft,
        startX: event.clientX,
        suppressClick: false,
        targetScrollLeft: scroller.scrollLeft,
        velocityX: 0,
      };
      scroller.classList.add("top-plays-row__scroller--dragging");
      scroller.setPointerCapture?.(event.pointerId);
    };

    const stopTopPlaysDrag = () => {
      const dragState = topPlaysDragRef.current;
      if (!dragState.isDown) {
        return;
      }

      const scroller = dragState.scroller;
      if (scroller) {
        if (dragState.animationFrame) {
          window.cancelAnimationFrame(dragState.animationFrame);
          scroller.scrollLeft = dragState.targetScrollLeft;
        }
        scroller.classList.remove("top-plays-row__scroller--dragging");
        if (dragState.pointerId !== null) {
          scroller.releasePointerCapture?.(dragState.pointerId);
        }
      }

      topPlaysDragRef.current = {
        ...dragState,
        animationFrame: null,
        isDown: false,
        pointerId: null,
        scroller: null,
        suppressClick: dragState.moved,
      };

      if (scroller && dragState.moved) {
        startTopPlaysMomentum(scroller, dragState.velocityX);
      }

      if (dragState.moved) {
        window.setTimeout(() => {
          topPlaysDragRef.current.suppressClick = false;
          topPlaysDragRef.current.moved = false;
        }, 0);
      }
    };

    const handleTopPlaysPointerMove = (event) => {
      const dragState = topPlaysDragRef.current;
      if (!dragState.isDown || dragState.scroller !== event.currentTarget) {
        return;
      }

      const walk = event.clientX - dragState.startX;
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const deltaTime = Math.max(now - dragState.lastMoveAt, 16);
      const velocityX = (event.clientX - dragState.lastClientX) / deltaTime;
      const targetScrollLeft = dragState.scrollLeft - walk;
      if (Math.abs(walk) > 4) {
        topPlaysDragRef.current.moved = true;
        event.preventDefault();
      }

      topPlaysDragRef.current.lastClientX = event.clientX;
      topPlaysDragRef.current.lastMoveAt = now;
      topPlaysDragRef.current.velocityX = velocityX;
      scheduleTopPlaysScroll(event.currentTarget, targetScrollLeft);
    };

    const handleTopPlaysClickCapture = (event) => {
      if (!topPlaysDragRef.current.suppressClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    const handleSuperPackApply = async (packLegs, packTitle) => {
      const now = Date.now();
      const incomingLegs = (Array.isArray(packLegs) ? packLegs : [])
        .filter((athlete) => {
          const start = parseBoardStartDate(
            athlete?.start_time_iso || athlete?.start_time_obj || athlete?.start_time || ""
          );
          return start && start.getTime() > now;
        })
        .map((athlete) => normalizeSelectedAthlete({
          ...athlete,
          selected: "more",
          selection_origin: "super_pack",
        }))
        .filter(Boolean);
      if (incomingLegs.length < 3) {
        toastr["warning"]("This pack no longer has enough available events.", "Super Packs");
        return;
      }

      const incomingKeys = new Set(
        incomingLegs.map((athlete) => `${athlete.athlete_id}|${normalizeProjectedStatKey(athlete.stat_key)}`)
      );
      const retainedSelections = selected.filter((athlete) => (
        !incomingKeys.has(`${athlete.athlete_id}|${normalizeProjectedStatKey(athlete.stat_key)}`)
      ));
      const nextSelected = [...retainedSelections, ...incomingLegs];

      if (nextSelected.length > maxAthletes) {
        const openSlots = Math.max(maxAthletes - retainedSelections.length, 0);
        toastr["warning"](
          `This pack needs ${incomingLegs.length} open spots. Your lineup currently has ${openSlots}.`,
          "Super Packs"
        );
        return;
      }

      const win100Violation = win100ModeActive
        ? getWin100LineupViolation(nextSelected)
        : null;
      if (win100Violation) {
        toastr["warning"](win100Violation.message, win100Violation.title);
        return;
      }

      const lowLiquidityViolation = getLowLiquidityLineupLimitViolation(nextSelected, null, loggedIn);
      if (lowLiquidityViolation) {
        toastr["warning"](lowLiquidityViolation.message, "Super Packs");
        return;
      }

      const isShrk = Number(document.getElementById("shrk")?.value) > 0;
      const combinability = validatePropCombinability(
        nextSelected,
        isShrk ? "sharp" : "recreational"
      );
      if (!combinability.valid) {
        toastr["warning"](combinability.errors[0]?.message || "This pack cannot be added.", "Super Packs");
        return;
      }

      setSelected(nextSelected);
      setShowLineupReviewSheet(isMobileLineupViewport());
      toastr["success"](`${incomingLegs.length} picks added.`, packTitle);
    };

    const handleCommunityPick = (athlete) => {
      if (!athlete) return;
      const selection = athlete.selected === "less" ? "less" : "more";
      void selectProp({
        currentTarget: {
          dataset: {
            athlete: JSON.stringify(athlete),
            promo: athlete.hard ? "hard" : (athlete.easy ? "easy" : ""),
            prop: selection,
            stat: athlete.stat_key,
            value: athlete.stat,
          },
          value: athlete.athlete_id,
        },
      });
    };

    return (
      <div className="top-plays-board">
        {leagueRows.map((row) => {
          const rowCards = row.athletes.map((athleteData) => {
            const index = cardIndex;
            cardIndex += 1;

            return (
              <AthleteCard
                key={`${getTopPlaysLeagueKey(athleteData)}-${getAthleteVariantKey(athleteData)}`}
                index={index}
                athlete={athleteData}
                athletes={row.athletes}
                loadHeadshotEagerly={index < initialHeadshotEagerCount}
                showHeadshotPlaceholderWhileLoading
                onClick={selectProp}
                onSwapStat={handleSwappedSelection}
                onOpenInfoModal={handleModal}
                selected={selected}
              />
            );
          });

          return (
            <section className="top-plays-row" key={row.league} aria-label={`${row.label} top plays`}>
              <div className="top-plays-row__header">
                <h2 className="top-plays-row__title">Popular</h2>
                <div className="top-plays-row__controls">
                  <button
                    aria-label={`Scroll ${row.label} top plays left`}
                    className="top-plays-row__control top-plays-row__control--prev"
                    onClick={() => scrollTopPlaysRow(row.league, -1)}
                    type="button"
                  >
                    <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
                  </button>
                  <button
                    aria-label={`Scroll ${row.label} top plays right`}
                    className="top-plays-row__control top-plays-row__control--next"
                    onClick={() => scrollTopPlaysRow(row.league, 1)}
                    type="button"
                  >
                    <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
                  </button>
                </div>
              </div>
              <div
                className="top-plays-row__scroller"
                onClickCapture={handleTopPlaysClickCapture}
                onDragStart={(event) => event.preventDefault()}
                onPointerCancel={stopTopPlaysDrag}
                onPointerDown={handleTopPlaysPointerDown}
                onPointerLeave={stopTopPlaysDrag}
                onPointerMove={handleTopPlaysPointerMove}
                onPointerUp={stopTopPlaysDrag}
                ref={(node) => setTopPlaysScrollerRef(row.league, node)}
              >
                {rowCards}
              </div>
            </section>
          );
        })}
        <DeferredSuperPacks
          allowLoad={popularPrewarmComplete}
          communityAthletes={leagueRows.flatMap((row) => row.athletes)}
          onApply={handleSuperPackApply}
          onCommunityPick={handleCommunityPick}
          onDemandLoad={prioritizeSuperPacks}
        />
      </div>
    );
  };

  const skeletonStickyMenu = stickyPortalTarget
    ? createPortal(<MobileStickyMenuShell loggedIn={loggedIn} />, stickyPortalTarget)
    : null;
  const initialSkeletonIsTopPlays = shouldShowTopPlaysInitialSkeleton();
  const skeletonSports = displayedSports.length > 0 ? displayedSports : recentBoardLeagues;
  const skeletonSelectedSport = sport
    || (initialSkeletonIsTopPlays ? TOP_PLAYS_SPORT : normalizeSportKey(urlParams.get("sport")))
    || recentBoardLeagues[0]
    || "";

  if (loading) {
    return (
      <AthleteBoardSkeleton
        variant={initialSkeletonIsTopPlays ? "topplays" : "app"}
        statLabel={formatSkeletonStatLabel(stats, stat)}
        stickyMenu={skeletonStickyMenu}
        sports={skeletonSports}
        selectedSport={skeletonSelectedSport}
      />
    );
  }

  if (!athletes && !athletesLoading) {
    const tryNextOption = () => {
      const statKeys = Object.keys(stats);
      const currentIndex = statKeys.indexOf(stat);

      // Try next stat in current sport
      if (currentIndex !== -1 && currentIndex < statKeys.length - 1) {
        const nextStat = statKeys[currentIndex + 1];
        setStat(nextStat);
        setAthletesLoading(true);
        handleStatChange(sport, nextStat, stats, []);
      } else {
        // Try next sport
        const currentSportIndex = displayedSports.indexOf(sport);
        if (currentSportIndex !== -1 && currentSportIndex < displayedSports.length - 1) {
          const nextSport = displayedSports[currentSportIndex + 1];
          const nextSportStats = verifiedStats[nextSport];
          const firstNextStat = Object.keys(nextSportStats)[0];
          setSport(nextSport);
          setStat(firstNextStat);
          setStats(nextSportStats);
          setAthletesLoading(true);
          handleStatChange(nextSport, firstNextStat, nextSportStats, []);
        } else {
          toastr["info"]("Already at the last option. Try changing filters manually.", "Notice");
        }
      }
    };

    return (
      <div className="container text-center">
        {displayedSports.length > 1 && (
          <div className="mb-3">
            <SportSelection sports={displayedSports} sport={sport} liveSports={liveSports} onClick={selectProp} onPrefetch={prefetchBoardPopularSport} />
          </div>
        )}

        {!isTopPlaysSelected && Object.keys(activeStatOptions).length > 0 && (
          <StatSelection
            stats={activeStatOptions}
            stat={visibleSelectedStat}
            sport={sport}
            onClick={selectProp}
            popularLabel={isEventMode ? "All" : undefined}
          />
        )}
        <div className="w-100 mt-5">
          <div className="alert alert-warning">
            <ExclamationCircleIcon aria-hidden="true" className="ww-inline-icon mr-2" /> No Athletes Found
          </div>
          <div className="mb-3">
            {isTopPlaysSelected
              ? "TOP PLAYS is currently unavailable. Choose another league above."
              : "Try changing the stat or league above, or click below to automatically try the next option."}
          </div>
          {!isTopPlaysSelected && (
            <button onClick={tryNextOption} className="btn btn-primary">
              <ArrowRightIcon aria-hidden="true" className="ww-inline-icon mr-2" /> Try Next Option
            </button>
          )}
        </div>
      </div>
    );
  }

  const eventAwayColor = selectedGame?.away_color || selectedGame?.away_color_two || "";
  const eventHomeColor = selectedGame?.home_color || selectedGame?.home_color_two || "";
  const eventHeaderStyle = isEventMode ? {
    background: `linear-gradient(110deg, ${withEventColorAlpha(eventAwayColor, darkMode ? 0.24 : 0.13)} 0%, ${darkMode ? "#0d1117" : "#fff"} 43%, ${darkMode ? "#0d1117" : "#fff"} 57%, ${withEventColorAlpha(eventHomeColor, darkMode ? 0.24 : 0.13)} 100%)`,
    borderLeftColor: eventAwayColor || undefined,
    borderRightColor: eventHomeColor || undefined,
  } : undefined;
  const eventStartTime = isEventMode
    ? formatBoardStartTime(selectedGame.start_time_iso || selectedGame.start_time, {
        fallback: selectedGame.start_time || "Time TBD",
        todayLabel: "Today",
        weekdayPeriod: true,
      })
    : "";

  return (
    <div className="text-center mt-1 ww-board-view">
      {isEventMode && (
        <div className="ww-event-mode-header" style={eventHeaderStyle}>
          <button
            type="button"
            className="ww-event-mode-header__back"
            onClick={restoreBoardFromEventMode}
            aria-label="Back to board filters"
          >
            <EventModeBackIcon />
          </button>
          <div className="ww-event-mode-header__matchup">
            <div className="ww-event-mode-header__teams">
              <div className="ww-event-mode-header__team">
                {selectedGame.away_logo && (
                  <img src={selectedGame.away_logo} alt="" aria-hidden="true" decoding="async" />
                )}
                <span className="ww-event-mode-header__team-name">{selectedGame.away || "TBD"}</span>
              </div>
              <span className="ww-event-mode-header__time">{eventStartTime}</span>
              <div className="ww-event-mode-header__team ww-event-mode-header__team--home">
                <span className="ww-event-mode-header__team-name">{selectedGame.home || "TBD"}</span>
                {selectedGame.home_logo && (
                  <img src={selectedGame.home_logo} alt="" aria-hidden="true" decoding="async" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isEventMode && (
        <div className="ww-board-section ww-board-section--events ww-board-section--event-alternatives">
          <GamesSlider
            key={`event-mode-events-${sport}`}
            excludeGameId={selectedGameId}
            rejectedEvents={rejectedEvents}
            pendingEventKey={pendingEventKey}
            label="Other events"
            onSelectGame={onGameSelect}
            selectedGameId={selectedGameId}
            showTbd={false}
            shrk={getShrkValue()}
            sport={sport}
          />
        </div>
      )}

      {!isEventMode && displayedSports.length > 0 && (
        <div className="ww-board-section ww-board-section--sports mb-3">
          <SportSelection sports={displayedSports} sport={navigationSelectedSport} liveSports={liveSports} onClick={selectProp} onPrefetch={prefetchBoardPopularSport} />
        </div>
      )}

      {isEventMode && (
        <div className="ww-board-section ww-board-section--stats ww-board-section--event-stats">
          <StatSelection
            stats={eventStatOptions}
            stat={navigationSelectedStat}
            sport={sport}
            onClick={selectProp}
            popularLabel="All"
          />
        </div>
      )}

      {!isEventMode && (!isTopPlaysSelected || transitioningFromTopPlaysToLeague) && !teamsHubOpen && (
        <>
          {sportStatsLoading || transitioningFromTopPlaysToLeague ? (
            <AthleteBoardSkeleton variant="stats" />
          ) : (
            <div className="ww-board-section ww-board-section--stats">
              <StatSelection stats={visibleStatOptions} stat={navigationSelectedStat} sport={sport} onClick={selectProp} />
            </div>
          )}
          <div className="ww-board-section ww-board-section--events">
            <div className="ww-board-section-label">Events</div>
            {transitioningFromTopPlaysToLeague ? (
              <GamesSlider
                key={`transition-events-${pendingBoardSport}`}
                games={[]}
                pending
                sport={pendingBoardSport}
                selectedGameId=""
              />
            ) : (
              <GamesSlider
                key={`league-events-${sport}`}
                rejectedEvents={rejectedEvents}
                pendingEventKey={pendingEventKey}
                sport={sport}
                shrk={getShrkValue()}
                selectedGameId={selectedGameId}
                onSelectGame={onGameSelect}
                onInventoryChange={handleLeagueInventoryChange}
                onLiveAvailabilityChange={handleLiveAvailabilityChange}
              />
            )}
          </div>
        </>
      )}

      {!isEventMode && isTopPlaysSelected && !transitioningFromTopPlaysToLeague && !teamsHubOpen && (
        <div className="ww-board-section ww-board-section--events">
          <div className="ww-board-section-label">Events</div>
          <GamesSlider
            key={`top-plays-events-${sport}`}
            games={topPlaysEventCards}
            pendingEventKey={pendingEventKey}
            pending={athletesLoading || shouldDeferEmptyAthleteState}
            sport={sport}
            selectedGameId={sport === TOP_PLAYS_SPORT ? "" : selectedGameId}
            onSelectGame={onTopPlaysEventSelect}
          />
        </div>
      )}

      {!win100ModeActive && !isEventMode && (isTopPlaysSelected || topPlaysHeroExiting) && !teamsHubOpen && (
        <div className="ww-board-section ww-board-section--promos">
          <Suspense fallback={<PromoOffersFallback />}>
            <PromoOffers
              activePromoCount={activePromoSummary.count}
              activePromoText={activePromoSummary.text}
              activePromoAthlete={activePromoAthlete}
              liveEvents={topPlaysLiveHeroEvents}
              pendingEventKey={pendingEventKey}
              previewEvents={topPlaysEventCards}
              exiting={topPlaysHeroExiting}
              entering={enteredTopPlaysFromLeague}
              onSelectLiveEvent={onTopPlaysEventSelect}
              onOpenInfoModal={handleModal}
            />
          </Suspense>
        </div>
      )}

      <Suspense fallback={null}>
        {loggedIn && <GeoLocate />}
        {activeInfoModal && <BoardInfoModalLauncher type={activeInfoModal} />}
        {h2hParam && <H2HMatchModal />}
      </Suspense>

      <div className="ww-board-section ww-board-section--utility d-flex flex-wrap mt-2 mt-md-4">
        <div className="col-md-7 col-sm-12 p-0 d-flex align-items-center">
          <div className="how-to-play">
            <span
              aria-controls="board-search-shell"
              aria-expanded={searchOpen}
              className="mr-2"
              onClick={() => setSearchOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSearchOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <MagnifyingGlassIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Search
            </span>{" "}
            <span onClick={() => handleModal("how-to-play")} className="ml-2 mr-2">
              <PlayCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Rules
            </span>{" "}
            <span onClick={() => handleModal("icons-explained")} className="ml-2 mr-2">
              <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Icons
            </span>{" "}
            <span className="ml-2 mr-2 d-none d-md-inline-block">
              <a href="#" onClick={handleChatClick}>
                <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Support
              </a>
            </span>{" "}
            <span onClick={() => handleDarkToggle()} className="ml-2">
              <ThemeModeIcon aria-hidden="true" className="ww-inline-icon mr-1" darkMode={darkMode} />
              {darkMode ? "Light Mode" : "Dark Mode"}
            </span>
          </div>
        </div>

        <SearchInput
          sport={sport}
          sports={displayedSports}
          stat={stat}
          setLoading={setLoading}
          setAthletes={setAthletes}
          setAthletesLoading={setAthletesLoading}
          getAthletes={getAthletes}
          cancelBoardRequest={invalidateBoardRequest}
          filterAthleteList={filterAthleteList}
          isOpen={searchOpen}
          resetSignal={searchResetSignal}
          onSearchModeChange={setSearchMode}
          onSearchQueryChange={setSearchQuery}
        />
      </div>

      <div
        aria-busy={atomicBoardTransition ? "true" : "false"}
        className="ww-board-section ww-board-section--cards d-flex flex-wrap flex-md-nowrap"
      >
        {atomicBoardTransition && (
          <div
            className="ww-board-transition-overlay"
            role="status"
            aria-live="polite"
            aria-label="Loading selected board"
          >
            <span className="ww-board-transition-spinner" aria-hidden="true" />
            <span className="sr-only">Loading selected board</span>
          </div>
        )}
        {!atomicBoardTransition && !isTeamsViewSelected && !athletesLoading && (!emptyAthleteStatePending || searchMode) && (emptyAthleteStateReady || searchMode) && (!visibleAthletes || visibleAthletes.length === 0) && (
          <div className="w-100 mt-5 text-center">
            <div className="alert alert-warning d-inline-block">
              <ExclamationCircleIcon aria-hidden="true" className="ww-inline-icon mr-2" />
							{athletesLoadFailed
								? "Unable to Load Selections"
								: Number(getCurrentUserTierValue()) > 1 ? "No Selections Available" : "No Athletes Found"}
            </div>
            <div className="mt-2">
						{athletesLoadFailed
							? "This stat could not be refreshed. Tap it above to try again."
							: isTopPlaysSelected
                ? "TOP PLAYS is currently unavailable."
                : Number(getCurrentUserTierValue()) > 1
                  ? "There are no selections available for this stat right now."
                  : "The automatic stat switching may have already tried other options."}
            </div>
            <div className="mb-3">
						{athletesLoadFailed
							? "The board did not treat this temporary error as an empty market."
							: isTopPlaysSelected
                ? "Try manually changing the league above."
                : Number(getCurrentUserTierValue()) > 1
                  ? "Try another stat or league, or check back shortly."
                  : "Try manually changing the stat or league above."}
            </div>
          </div>
        )}

        <div
          className={isTopPlaysSelected && !transitioningFromTopPlaysToLeague && !searchMode && !isTeamsViewSelected
            ? "top-plays-board-shell"
            : `d-flex flex-wrap justify-content-md-start align-self-baseline h-100 athlete-card-grid pr-3 ${
                searchMode ? "athlete-card-grid--search-results" : ""
              } ${
                isTeamsViewSelected ? "athlete-card-grid--search-results athlete-card-grid--team-lineups" : ""
              } ${
                !athletesLoading && cardAthletes.length === 1 ? "athlete-card-grid--single" : "athlete-card-grid--multiple"
              } ${
                !athletesLoading && cardAthletes.some((athlete) => isFallbackHeadshotImage(athlete?.image))
                  ? "athlete-card-grid--fallback-headshots"
                  : ""
              }`}
        >
          {atomicBoardTransition && !hasCachedBoardPreview && !isTeamsViewSelected ? (
            <AthleteBoardSkeleton
              variant={isTopPlaysSport(pendingBoardSport) && !searchMode ? "topplays-cards" : "cards"}
              previewRows={atomicBoardTransition.previewRows || []}
              statLabel={formatSkeletonStatLabel(
                verifiedStats[pendingBoardSport] || (pendingBoardSport === normalizeSportKey(sport) ? stats : {}),
                pendingBoardStat
              )}
            />
          ) : searchMode && athletesLoading && searchQuery && !isTeamsViewSelected ? (
            <div className="board-search-loading" role="status" aria-live="polite" aria-label={`Searching for ${searchQuery}`}>
              <span className="board-search-loading__spinner" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="board-search-loading__text">Searching for <strong>{searchQuery}</strong>...</span>
            </div>
          ) : !hasCachedBoardPreview && ((athletesLoading && !hasOptimisticAthletePreview) || shouldDeferEmptyAthleteState) && !isTeamsViewSelected ? (
            <AthleteBoardSkeleton
              variant={isTopPlaysSelected && !searchMode ? "topplays-cards" : "cards"}
              statLabel={formatSkeletonStatLabel(stats, stat)}
            />
          ) : isTeamsViewSelected ? (
            <Suspense fallback={<AthleteBoardSkeleton variant="cards" />}>
              <TeamLineupBoard
                groups={teamsHubOpen
                  ? getVisibleTeamLineupGroups(teamsHubGroups, teamsHubQuotedGroups, teamsHubQuotesReady)
                  : getVisibleTeamLineupGroups(teamLineupGroups, teamLineupQuotedGroups, teamLineupQuotesReady)}
                sport={sport}
                loading={teamsHubOpen ? teamsHubLoading : teamLineupLoading}
                emptyMessage={teamsHubOpen ? "No Team events available." : undefined}
                pendingReplaceKey={pendingTeamReplaceKey}
                pendingApplyKey={pendingTeamApplyKey}
                onSelectTeamLineup={handleTeamLineupSelect}
                showDebug={teamLineupDebugEnabled}
              />
            </Suspense>
          ) : (
            cardAthletes.length > 0 &&
            (searchMode ? (
              <Suspense fallback={null}>
                <SearchResultsList
                  athletes={visibleAthletes}
                  onClick={selectProp}
                  selected={selected}
                />
              </Suspense>
            ) : isTopPlaysSelected && !hasCachedBoardPreview ? (
              renderTopPlaysRows()
            ) : (
              cardAthletes.map((athleteData, index) => {
                return (
                  <AthleteCard
                    key={`${normalizeSportKey(cardSport)}|${normalizeProjectedStatKey(cardStat)}|${getAthleteVariantKey(athleteData)}`}
                    index={index}
                    athlete={athleteData}
                    athletes={cardAthletes}
                    previewOnly={hasCachedBoardPreview || Boolean(athleteData?._boardOptimisticPreview)}
                    loadHeadshotEagerly={index < initialHeadshotEagerCount}
                    hideEventContext={cardEventMode}
                    onClick={selectProp}
                    onOpenInfoModal={handleModal}
                    onSwapStat={handleSwappedSelection}
                    selected={selected}
                  />
                );
              })
            ))
          )}
          {hasOptimisticAthletePreview && !isTeamsViewSelected && !searchMode && (
            <div className="athlete-card-grid__loading-more" role="status" aria-live="polite">
              <span className="athlete-card-grid__loading-more-spinner" aria-hidden="true" />
              <span>Updating athletes.</span>
            </div>
          )}
          {boardLoadingMoreAthletes && !athletesLoading && !isTeamsViewSelected && !searchMode && (
            <div className="athlete-card-grid__loading-more" role="status" aria-live="polite">
              <span className="athlete-card-grid__loading-more-spinner" aria-hidden="true" />
              <span>Loading more athletes.</span>
            </div>
          )}
          {boardHydrationHasMore && !isTeamsViewSelected && !isTopPlaysSelected && !searchMode && (
            <div
              aria-hidden="true"
              className="athlete-card-grid__hydration-sentinel"
              ref={boardHydrationSentinelRef}
            />
          )}
        </div>
        {selected.length > 0 || sidebarLoadRequested ? (
          <Suspense fallback={<CollapsedSidebarFallback />}>
            <Sidebar
              selected={selected}
              onClick={selectProp}
              balance={balance}
              initiallyExpanded={sidebarLoadRequested}
              mobileSheetOpen={showLineupReviewSheet}
              onCloseMobileSheet={() => setShowLineupReviewSheet(false)}
              onRefreshBoard={refreshCurrentBoard}
			  onLineValidationIssues={applyLineValidationIssues}
			  onAcceptLineChange={acceptLineChange}
			  onFindReplacement={handleFindReplacement}
            />
          </Suspense>
        ) : isDesktopViewport ? (
          <>
            <label htmlFor="toggle-sidebar-button" className="sr-only">Expand Sidebar</label>
            <div
              id="toggle-sidebar-button"
              className="align-top toggle-sidebar"
              onClick={() => setSidebarLoadRequested(true)}
            >
              <span>Expand</span>
              <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
            </div>
          </>
        ) : null}
      </div>

      {stickyPortalTarget && createPortal(
        <Fragment key={stickyChromeMountKey}>
          {selected.length > 0 && (
            <div className="sticky-footer d-md-none" data-ww-sticky-chrome="footer">
              <button onClick={handleScroll} className="btn btn-primary" type="button">
                <div className="sticky-footer__meta" aria-label={`${mobilePickCountLabel}. ${mobileStickySubcopy}`}>
                  <div className="sticky-footer__avatar-stack" aria-hidden="true">
                    {stickyFooterAthletes.map((athlete, index) => (
                      <StickyFooterAvatar
                        athlete={athlete}
                        index={index}
                        key={`${athlete.athlete_id}-${athlete.stat_key}-${athlete.selected}-${index}`}
                      />
                    ))}
                    {stickyFooterOverflowCount > 0 ? (
                      <StickyFooterAvatar
                        index={stickyFooterAthletes.length}
                        overflow={stickyFooterOverflowCount}
                      />
                    ) : null}
                    {stickyFooterPotentialPayouts.map((payoutPotential, index) => (
                      <StickyFooterAvatar
                        index={stickyFooterVisibleAvatarCount + index}
                        key={`potential-${payoutPotential.pickCount}`}
                        payoutPotential={payoutPotential}
                        payoutPotentialBackground={stickyFooterPotentialBackground}
                      />
                    ))}
                  </div>
                </div>
                <div className="sticky-footer__cta">
                  <span className="sticky-footer__cta-copy">
                    <span className="sticky-footer__cta-label">{mobileStickyCtaLabel}</span>
                    <span className="sticky-footer__cta-multiplier">{mobileStickyCtaMultiplier}</span>
                  </span>
                  <span className="sticky-footer__cta-icon">
                    <StickyArrowIcon />
                  </span>
                </div>
              </button>
            </div>
          )}

          <div className="sticky-menu d-md-none" data-ww-sticky-chrome="menu">
            {loggedIn ? (
              <MobileStickyItem active={currentPath === "/my-account"} href="/my-account/" label="Account">
                <StickyAccountIcon />
              </MobileStickyItem>
            ) : (
              <MobileStickyItem label="Account" loginRequired renderAs="div">
                <StickyAccountIcon />
              </MobileStickyItem>
            )}

            {loggedIn ? (
              <MobileStickyItem active={currentPath === "/my-lineup"} href="/my-lineup/" label="Lineups">
                <StickyLineupsIcon />
              </MobileStickyItem>
            ) : (
              <MobileStickyItem label="Lineups" loginRequired renderAs="div">
                <StickyLineupsIcon />
              </MobileStickyItem>
            )}

            <MobileStickyItem active={currentPath === "/promos"} href="/promos/" label="Promos">
              <StickyPromoIcon />
            </MobileStickyItem>

            <MobileStickyItem href="#" id="chat-btn" label="Support" onClick={handleChatClick}>
              <StickySupportIcon />
            </MobileStickyItem>
          </div>
        </Fragment>,
        stickyPortalTarget
      )}


    </div>
  );
};

const getSkeletonBoardReviewVariant = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "";
  }

  const params = new URLSearchParams(window.location.search);
  const reviewFlag = String(params.get("ww_review_skeleton_board") || "").trim().toLowerCase();
  const topPlaysValues = ["1", "true", "yes", "on", "topplays", "top-picks", "top_picks"];
  const appValues = ["app", "sport", "standard"];

  if (!document.getElementById("wpadminbar")) {
    return "";
  }

  if (appValues.includes(reviewFlag)) {
    return "app";
  }

  return topPlaysValues.includes(reviewFlag) ? "topplays" : "";
};

const skeletonBoardReviewVariant = getSkeletonBoardReviewVariant();
const skeletonBoardReviewMode = Boolean(skeletonBoardReviewVariant);
const container = document.getElementById("react-load");
if (container) {
  const root = createRoot(container);
  if (skeletonBoardReviewMode) {
    document.body.classList.add("ww-review-skeleton-board");
    root.render(
      <AthleteBoardSkeleton
        variant={skeletonBoardReviewVariant}
        statLabel="Popular"
        stickyMenu={createPortal(<MobileStickyMenuShell loggedIn={loggedIn} />, document.body)}
      />
    );
  } else {
    root.render(
      <GlobalProvider>
        <App />
      </GlobalProvider>
    );
  }
}

// Reconcile the live board after the site-wide Median bridge repairs the retained WebView.
window.addEventListener(MEDIAN_APP_RESUMED_EVENT, function() {
  if (skeletonBoardReviewMode) {
    return;
  }

  if (isRegistrationFlowActive()) {
    return;
  }
  window.dispatchEvent(new Event(BOARD_RESUME_REFRESH_EVENT));
});
